import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";

// Translation resources
const resources = {
  en: {
    translation: {
      // Locale
      locale: "en-US",
      // Billing
      billing: {
        title: "Billing",
        sidebarDescription: "Invoices, payment & subscription",
        description: "Manage your invoices, payment method and subscription securely via the Stripe customer portal.",
        featureInvoices: "View & download invoices",
        featurePayment: "Update payment method",
        featureCancel: "Manage subscription",
        manageButton: "Manage billing & payment",
        openError: "Could not open the customer portal. Please try again.",
        noAccountTitle: "No billing account yet",
        noAccountBody: "No payment account is linked for this studio yet. Contact us if you have billing questions.",
      },
      // Referral program
      referral: {
        nav: "Refer & earn",
        title: "Refer Sylva",
        subtitle: "Recommend Sylva to another studio — you both get a free month.",
        howItWorksTitle: "How it works",
        step1: "Share your personal link or code with another studio.",
        step2: "They sign up through your link.",
        step3: "Once they become a paying customer, you both get 1 free month.",
        yourLinkTitle: "Your referral link",
        yourCodeLabel: "Your code",
        copyLink: "Copy link",
        copyCode: "Copy code",
        shareWhatsApp: "Share via WhatsApp",
        shareEmail: "Share via email",
        linkCopied: "Link copied",
        codeCopied: "Code copied",
        shareMessage: "Hey! I use Sylva as the AI assistant for my studio and I love it. If you sign up through my link, we both get a free month: {{link}}",
        emailSubject: "A recommendation for your studio: Sylva",
        emailCopied: "Email text copied — paste it into your email.",
        rewardsTitle: "Your referrals",
        statInvited: "Invited",
        statActive: "Active",
        statMonthsEarned: "Months earned",
        emptyState: "No referrals yet. Share your link to get started!",
        rewardNote: "The free month is credited as soon as the referred studio becomes a paying customer.",
        loadError: "Could not load referral data.",
        ahaTitle: "Strong week! 🎉",
        ahaBody: "You {{reason}} this week — congrats! Know another studio that wants to grow too? Refer each other and you'll both get a free month.",
        ahaReasonCustomers_one: "won a new customer",
        ahaReasonCustomers_other: "won {{count}} new customers",
        ahaReasonReviews_one: "received a new review",
        ahaReasonReviews_other: "received {{count}} new reviews",
        ahaAnd: "and",
        ahaCta: "Refer a studio",
        ahaDismiss: "Hide",
        signupStudioName: "Studio name",
        signupStudioNamePlaceholder: "e.g. Hair & Style Berlin",
        signupReferralCode: "Referral code (optional)",
        signupReferralPlaceholder: "e.g. ABC1234",
        signupReferralApplied: "Referral code applied — you'll both get a free month.",
        signupAcceptTerms: "I accept the terms and conditions.",
        signupStudioRequired: "Please enter your studio name.",
        signupTermsRequired: "Please accept the terms to continue.",
        signupError: "Signup failed. Please try again.",
        signupEmailExists: "An account with this email already exists. Please log in instead, or use a different email.",
        signupSignInHint: "Your account was created — please sign in.",
      },
      // PWA
      pwa: {
        newContentAvailable: "A new version is available. Click reload to update.",
        reload: "Reload",
        installTitle: "Install Sylva App",
        installDescription: "Install this app on your device for a better experience and offline access.",
        installCta: "Install App"
      },

      // Header
      selectBusiness: "Select Business",
      signOut: "Sign Out",
      // App start-up loading states (Layout). NOTE: translations live here,
      // not in src/locales/ -- that directory is not wired into i18next.
      reloadPage: "Reload",
      stillLoadingTitle: "This is taking longer than usual",
      stillLoadingBody: "The connection to the server is slow right now. We keep trying — you can also reload the page.",
      connectionProblemTitle: "No connection to the server",
      connectionProblemBody: "Your data could not be loaded. This is a service problem, not an issue with your account. Please try again.",
      aiAssistant: "AI Assistant",
      createNewBusiness: "Create New Business",
      businessName: "Business Name",
      urlSlug: "URL Slug",
      cancel: "Cancel",
      create: "Create",
      copy: "Copy",
      copied: "Copied",

      // Navigation
      dashboard: "Dashboard",
      statsOverview: "Overview",
      sidebarControl: "Sidebar",
      sidebarExpanded: "Expanded",
      sidebarCollapsed: "Collapsed",
      sidebarExpandOnHover: "Expand on hover",
      interestedSince: "Interested since",
      interest: "Interest",
      call: "Call",
      message: "Message",
      leadStatusHint: "Lead — hasn't booked an appointment yet. Becomes a customer automatically once they book.",
      customerStatusHint: "Customer — has booked at least one appointment.",
      importedStatusHint: "Imported — added from WhatsApp or an import, no appointment yet.",
      appointments: "Appointments",
      analytics: "Analytics",
      keywords: "Keywords",
      keyword: "Keyword",
      addKeyword: "Add Keyword",
      editKeyword: "Edit Keyword",
      deleteKeyword: "Delete Keyword",
      phrase: "Phrase",
      searchType: "Search Type",
      localFinder: "Local Finder",
      organic: "Organic",
      keywordDeleted: "Keyword deleted successfully",
      keywordCreated: "Keyword created successfully",
      keywordUpdated: "Keyword updated successfully",
      deleteKeywordConfirm: "Are you sure you want to delete this keyword?",
      enterKeywordPhrase: "Enter keyword phrase",
      noKeywordsYet: "No keywords yet",
      manageRankingKeywords: "Manage ranking keywords for your business",
      analyticsDescription: "Track your business performance and customer acquisition",
      acquisition: "Acquisition",
      acquisitionDescription: "How many leads and customers you gained per day or week",
      keywordsOverviewDescription: "Visual overview of your keyword rankings over time",
      serankingDescription: "Keyword data from SEranking analytics",
      ahrefsDescription: "Keyword data from Ahrefs analytics",
      serviceAreasDescription: "Geographic areas for local SEO targeting",
      addFirstKeyword: "Add your first ranking keyword to get started",
      editKeywordDetails: "Edit the keyword details below.",
      addNewKeyword: "Add a new keyword to track rankings.",
      keywordsOverview: "Overview",
      organicPositions: "Organic Positions",
      organicPositionsDescription: "Number of organic keywords per position over time",
      localFinderPositions: "Local Finder Positions",
      localFinderPositionsDescription: "Number of local finder keywords per position over time",
      noRankingDataYet: "No data collected yet",
      noRankingDataDescription: "Ranking data will appear here once your keywords are tracked",
      currentRanking: "Current Ranking",
      monthChange: "30d Change",
      searchVolume: "Search Volume",
      competition: "Competition",
      noChange: "No change",
      noKeywordsFound: "No keywords found",
      collapse: "Collapse",
      expand: "Expand",

      // SEranking Keywords
      serankingAnalytics: "SEranking Analytics",
      volume: "Volume",
      position: "Position",
      positionChange: "Change",
      traffic: "Traffic",
      trafficPercent: "Traffic %",
      cpc: "CPC",
      block: "Block",
      additionalInfo: "Info",
      trafficTooltip: "Estimated traffic from this keyword",
      trafficPercentTooltip: "Percentage of total traffic from this keyword",
      cpcTooltip: "Estimated cost-per-click for paid advertising",
      blockTooltip: "SERP feature type (organic, featured snippet, etc.)",

      // Ahrefs Keywords
      ahrefsAnalytics: "Ahrefs Analytics",
      keywordDifficulty: "Difficulty",
      paidTraffic: "Paid Traffic",
      serpFeatures: "SERP Features",
      organicTrafficTooltip: "Estimated monthly organic traffic from this keyword",
      paidTrafficTooltip: "Estimated monthly paid traffic from this keyword",
      serpFeaturesTooltip: "Number of SERP features present for this keyword",
      difficultyLowHigh: "Difficulty: Low to High",
      difficultyHighLow: "Difficulty: High to Low",

      // Sorting
      phraseAZ: "Phrase A-Z",
      phraseZA: "Phrase Z-A",
      searchTypeAsc: "Search Type (Asc)",
      searchTypeDesc: "Search Type (Desc)",
      rankingBestFirst: "Best Ranking First",
      rankingWorstFirst: "Worst Ranking First",
      improvementAsc: "Least Improved First",
      improvementDesc: "Most Improved First",
      volumeLowHigh: "Volume: Low to High",
      volumeHighLow: "Volume: High to Low",
      competitionLowHigh: "Competition: Low to High",
      competitionHighLow: "Competition: High to Low",

      // Pagination
      showingResults: "Showing {{from}}-{{to}} of {{total}} results",

      // Service Areas
      serviceAreas: "Service Areas",
      addServiceArea: "Add Service Area",
      editServiceArea: "Edit Service Area",
      deleteServiceArea: "Delete Service Area",
      geoTargetCode: "Geo Target Code",
      searchCity: "Search city...",
      searchCityDescription: "Search for a city to get its geo target code from Google Ads",
      serviceAreaCreated: "Service area created successfully",
      serviceAreaUpdated: "Service area updated successfully",
      serviceAreaDeleted: "Service area deleted successfully",
      noServiceAreasYet: "No service areas yet",
      addFirstServiceArea: "Add your first service area to get started",
      orEnterManually: "Or enter manually",
      cityName: "City name",
      manualCityDescription: "Enter a city name without a geo target code",
      noResults: "No results found",
      notSet: "Not set",

      availability: "Availability",
      availabilityManagement: "Availability Management",
      roomsResources: "Rooms & Resources",
      globalConstraints: "Global Constraints",
      organizationWideRules: "Organization-wide booking rules and constraints",
      calendarProfiles: "Calendar Profiles",
      minAdvanceBooking: "Minimum advance booking",
      maxAdvanceBooking: "Maximum advance booking",
      startTimeIntervals: "Start time intervals",
      customerPermissions: "Customer Permissions",
      customerCanCancel: "Customers can cancel appointments",
      customerCanReschedule: "Customers can reschedule appointments",
      customerCanEditForms: "Customers can edit forms",
      timezoneSetting: "Timezone Setting",
      businessHours: "Opening Hours",
      generalInformation: "General",
      pricingAndDuration: "Pricing & Duration",
      appearance: "Appearance",
      bookingRules: "Booking Rules",
      bookingPortalSettings: "Booking Portal",
      bookingNoticeEnabled: "Show notice on booking page",
      bookingNoticeEnabledHelp: "When enabled, a notice is displayed on the first step of the booking page.",
      bookingNoticeText: "Notice text",
      bookingNoticeTextHelp: "Enter important booking information for your customers here. This notice appears on the first page of the booking portal.",
      bookingNoticeTextPlaceholder: "Enter the notice text that should be displayed to your customers...",
      cancellationPolicyTitle: "Cancellation policy",
      cancellationPolicyEnabled: "Show fee notice on short-notice cancellations",
      cancellationPolicyEnabledHelp: "When a customer cancels online shortly before the appointment, the booking portal warns them that a cancellation fee may apply. Informational only — Sylva does not charge the fee; you collect it yourself.",
      cancellationCutoffHours: "Short-notice threshold",
      cancellationCutoffHoursHelp: "Cancellations less than this many hours before the appointment count as short-notice.",
      cancellationFeePercent: "Cancellation fee",
      cancellationFeePercentHelp: "Percentage of the appointment price shown as the possible fee.",
      cancellationPolicyText: "Policy text (optional)",
      cancellationPolicyTextPlaceholder: "e.g. For cancellations less than 24 hours in advance we charge 50% of the service price.",
      cancellationPolicyTextHelp: "Shown to customers when cancelling and during booking. Leave empty to use a standard text.",
      hours: "hours",
      days: "days",
      minutes: "minutes",
      every30Minutes: "Every 30 minutes",
      autoBasedOnDuration: "Automatic based on service duration",
      customInterval: "Custom interval",

      addTimeRange: "Add Time Range",

      editDeadline: "Edit deadline",
      customers: "Customers",
      resources: "Resources",
      calendar: "Calendar",
      conversations: "Conversations",

      // Email Template Types
      confirmation: "Confirmation",
      reminder: "Reminder",
      reschedule: "Reschedule",
      cancellation: "Cancellation",
      followup: "Follow-up",

      // Notification Types
      customerAppointmentConfirmed: "Customer Appointment Confirmed",
      customerAppointmentReminder: "Customer Appointment Reminder",
      customerAppointmentRescheduled: "Customer Appointment Rescheduled",
      customerAppointmentCancelled: "Customer Appointment Cancelled",
      adminNewAppointment: "Admin New Appointment",
      adminAppointmentRescheduled: "Admin Appointment Rescheduled",
      adminAppointmentCancelled: "Admin Appointment Cancelled",

      // Email Preview
      preview: "Preview",
      htmlCode: "HTML Code",
      desktop: "Desktop",
      mobile: "Mobile",
      emailSubject: "Email Subject",
      emailBody: "Email Body",
      textVersion: "Text Version",
      textVersionDescription:
        "Plain text version for email clients that don't support HTML",
      htmlSource: "HTML Source",
      sampleDataUsed: "Sample Data Used",
      sampleDataUsedDescription:
        "The following placeholder values were used in this preview",
      sendTest: "Send Test",
      installDefaultTemplates: "Install Default Templates",
      getStartedWithTemplates:
        "Get started with professional email templates or create your own custom templates.",
      createCustomTemplate: "Create Custom Template",
      backToTemplates: "Back to Templates",
      templatePreview: "Template Preview",
      today: "Today",
      inDays: "in {{days}} days",
      month: "Month",
      week: "Week",
      twoDays: "2 days",
      dayShort: "D",
      weekShort: "W",
      sun: "Sun",
      mon: "Mon",
      tue: "Tue",
      wed: "Wed",
      thu: "Thu",
      fri: "Fri",
      sat: "Sat",

      scheduleAndManageAppointments: "Schedule and manage appointments",
      newAppointment: "New Appointment",
      bookAppointment: "Book Appointment",
      legend: "Legend",
      templates: "Templates",
      notifications: "Notifications",
      settings: "Settings",
      manageTenantSettings: "Manage your business settings",

      studioSettings: "Studio Settings",
      studioSettingsDescription: "Configure your studio name, branding, and contact information",
      "whatsappSettings.title": "WhatsApp",
      "whatsappSettings.description": "Configure WhatsApp messaging and welcome messages",
      "phoneSettings.title": "Phone",
      "phoneSettings.description": "Configure voice agent and call forwarding",
      studioInformation: "Studio Information",
      studioLogo: "Studio Logo",
      uploadLogo: "Upload Logo",
      logoUploadSuccess: "Logo uploaded successfully",
      logoUploadError: "Failed to upload logo",
      removeLogo: "Remove Logo",
      supportedFormats: "Supported formats: JPG, PNG (max 2MB)",
      dragDropLogoOrClick: "Drag and drop an image here, or click to select",
      localization: "Localization",
      localizationDescription: "Set your language, currency, and timezone preferences",
      localizationPreferences: "Localization Preferences",
      language: "Language",
      selectLanguage: "Select Language",
      selectCurrency: "Select Currency",
      selectTimezone: "Select Timezone",
      timezone: "Timezone",
      saveSettings: "Save Settings",
      saving: "Saving...",
      settingsUpdatedSuccessfully: "Settings updated successfully",
      settingsUpdateFailed: "Failed to update settings",

      // Availability Sidebar
      selectAvailabilityCategory: "Select an availability category",
      manageRoomsAndResources: "Manage rooms and resources for bookings",
      manageBusinessHours: "Set your opening hours for each day",
      manageBookingLinks: "Share links for online bookings",

      // Settings Sidebar
      selectSettingCategory: "Select a setting category",
      selectAnalyticsCategory: "Select an analytics category",

      // Auswertungen module (tabs)
      auswComingSoon: "Coming soon",
      auswOverview: "Overview",
      auswOverviewDescription: "How your salon is doing right now — from visibility to revenue.",
      auswRevenue: "Revenue",
      auswRevenueDescription: "Revenue over time, by service and by staff member.",
      auswCustomers: "Customers",
      auswCustomersDescription: "New vs. returning, repeat rate and customers worth winning back.",
      auswUtilisation: "Utilisation",
      auswUtilisationDescription: "How well you're booked, weak times and no-show rate.",
      auswOutstandingFees: "Outstanding fees",
      auswOutstandingFeesDescription: "No-shows and short-notice cancellations you can still charge.",
      feeOpenTotal: "Open fees total",
      feeOpenCount: "Open cases",
      feeInfoOnly: "Informational — you collect the fee yourself",
      feeOnlyOpen: "Only open",
      feeNoneOpen: "No outstanding fees in this period 🎉",
      feeNoneAtAll: "No no-shows or short-notice cancellations in this period 🎉",
      feeTypeNoShow: "No-show",
      feeTypeShortNotice: "Short-notice cancellation",
      feeStatusOutstanding: "Open",
      feeStatusCollected: "Paid",
      feeStatusWaived: "Waived",
      feeMarkCollected: "Paid",
      feeMarkWaived: "Waive",
      feeReopen: "Reopen",
      auswVisibility: "Online visibility",
      auswVisibilityDescription: "Does your online presence pay off? Google, website and rankings.",
      // Funnel
      funnelTitle: "From visibility to revenue",
      funnelHint: "Total reach combines Google profile views and website visits (last 7 days); bookings and revenue reflect the selected period.",
      funnelVisits: "Website visits",
      funnelViews: "Google views",
      funnelReach: "Total reach",
      ovRevenue: "Revenue",
      ovRevenueTip: "Revenue from bookings in the selected period (booked service price, excl. cancellations).",
      ovBookingsTip: "Completed bookings in the selected period.",
      ovAvgTicketTip: "Average revenue per booking.",
      ovNewCustomers: "New customers",
      ovNewCustomersTip: "Customers with their first-ever appointment in the selected period.",
      ovVsPrev: "vs. previous period",
      ovPerBooking: "per booking",
      ovInPeriod: "in this period",
      funnelActions: "Calls & clicks",
      funnelBookings: "Bookings",
      funnelRevenue: "Revenue",
      // Top customers
      topCustomersTitle: "Top-revenue customers",
      topCustomersTeaser: "Who brings in the most — and who's about to lapse.",
      topCustomersNote: "Ranked list with revenue, visits, average ticket and a win-back hint.",
      viewInRevenueTab: "View in Revenue",
      // Revenue tab sections
      auswRevenueTrend: "Revenue trend",
      auswRevenueByService: "Revenue by service",
      auswRevenueByStaff: "Revenue by staff member",
      // Customers tab sections
      auswNewVsReturning: "New vs. returning customers",
      auswRepeatRate: "Repeat rate",
      auswWinbackList: "Win-back list",
      auswWinbackNote: "High-value customers who haven't been in for a while.",
      auswVisitFrequency: "Visit frequency",
      auswAcquisitionSource: "Acquisition source",
      kpiRepeatRate: "Repeat-customer rate",
      kpiAvgVisits: "Avg. visits per customer",
      kpiActiveCustomers: "Active customers",
      legendNew: "New",
      legendReturning: "Returning",
      sourceUnknown: "Unknown",
      sourceCustomers: "{{n}} customers",
      // Utilisation tab sections
      auswUtilisationRate: "Utilisation rate",
      auswUtilisationByStaffNote: "Booked vs. available hours — filterable by staff member.",
      auswWeakTimes: "Weak days & times",
      auswNoShowRate: "No-show & cancellation rate",
      kpiNoShowRate: "No-show rate",
      kpiCancelRate: "Cancellation rate",
      auswUtilisationByStaff: "Utilisation by staff member",
      heatmapNote: "Darker = more bookings.",
      heatmapBookings: "bookings",
      heatmapPeak: "busiest time",
      utilEstimate: "Estimate from the weekly availability schedule.",
      // Visibility sub-views
      visProfile: "Google profile",
      visHeadline: "Are you visible? What customers find you for.",
      visSearchTitle: "What customers search to find you",
      visSearchExplain: "The terms people used on Google last month to reach your profile.",
      visSeenTimes: "{{n}}× seen",
      visSearchMonth: "As of {{month}}",
      visSearchEmpty: "No search-term data from Google yet.",
      visMapsTitle: "Your Google Maps ranking",
      visMapsComing: "We're setting up ongoing tracking of where you rank on Google Maps for your main terms — soon you'll see your position for e.g. \"hairdresser [city]\" here.",
      visAdvanced: "Advanced: SEO details",
      // Revenue tab — stats, breakdown, top customers
      auswTotalRevenue: "Total revenue",
      auswAvgTicket: "Average ticket",
      auswNoData: "No data for this period yet.",
      auswBookingsCount: "{{n}} bookings",
      dimService: "By service",
      dimStaff: "By staff",
      tcCustomer: "Customer",
      tcRevenue: "Revenue",
      tcVisits: "Visits",
      tcAvgTicket: "Avg. ticket",
      tcLastVisit: "Last visit",
      tcFavService: "Favourite service",
      tcHint: "Note",
      tcUnnamed: "Unnamed",
      tcActions: "Actions",
      tcCall: "Call",
      tcOpenProfile: "Open profile",
      auswWinbackMonths: "⚠️ {{months}} mo. away",
      // Reviews tab
      auswReviews: "Reviews",
      auswReviewsDescription: "Filter, read and understand your Google reviews.",
      revNotConnected: "Connect your Google Business Profile to see reviews here.",
      revSortNewest: "Newest",
      revSortOldest: "Oldest",
      revAllStars: "All stars",
      revAll: "All",
      revAnswered: "Answered",
      revUnanswered: "Unanswered",
      revSearchPlaceholder: "Search reviews…",
      revSentimentTitle: "Sentiment analysis",
      revSentimentNote: "AI breakdown of positive / neutral / negative feedback — coming next.",
      revSentimentExplain: "We automatically detect whether each review sounds positive, neutral or negative — so you see your customers' mood at a glance.",
      revAllAnswers: "All statuses",
      revAllSentiment: "All sentiment",
      revNoReviews: "No reviews match these filters.",
      revReplyLabel: "Your reply",
      revNotAnswered: "Not answered yet",
      revSentimentPositive: "Positive",
      revSentimentNeutral: "Neutral",
      revSentimentNegative: "Negative",
      revSentimentEmpty: "No sentiment analyzed yet — run the analysis to see the breakdown.",
      revAnalyzing: "Analyzing…",
      revAnalyzePending: "Analyze {{n}}",
      revReply: "Reply",
      revReplyPlaceholder: "Write a reply…",
      revSend: "Post reply",
      revSending: "Posting…",
      revCancel: "Cancel",
      revReplyError: "Could not post the reply. Please try again.",
      revDraftHint: "AI suggestion prefilled — edit before posting.",
      revEdit: "Edit",
      revUpdate: "Update reply",
      revDelete: "Delete",
      revAutoAnswered: "Answered automatically",
      revHeldReason: "Waiting for you: {{reason}}",
      revHeldRisk: "sensitive wording in the review",
      revHeldFailed: "posting to Google failed",
      revHeldKillSwitch: "automation is paused",
      revHeldDisabled: "automation is switched off",
      revHeldStars: "too few stars, always manual",
      revHeldNegative: "the text reads negative",
      revHeldOld: "the review is older than 14 days",
      revHeldBefore: "from before automation was switched on",
      revHeldRateHour: "hourly limit reached",
      revHeldRateDay: "daily limit reached",
      revHeldDraft: "the draft did not pass the safety check",
      gbpAutoReplyTitle: "Answer reviews automatically",
      gbpAutoReplyDescription:
        "Positive reviews are answered on their own, within about 10 minutes. Critical reviews, and anything the system is unsure about, always wait for a person.",
      gbpAutoReplyMinStars: "Answer automatically from",
      gbpAutoReplyFromFour: "4 stars and up",
      gbpAutoReplyFromFive: "5 stars only",
      gbpAutoReplyBacklogNote:
        "Only reviews that arrive from now on are answered automatically. Older reviews are never touched.",
      gbpAutoReplyOn:
        "New positive reviews are now answered automatically within about 10 minutes.",
      gbpAutoReplyOff: "Reviews now wait for your approval again.",
      insTitle: "AI insights",
      insGenerate: "Generate insights",
      insRefresh: "Refresh",
      insGenerating: "Generating…",
      insEmpty: "No insights yet — generate a summary of all your reviews.",
      insStrengths: "What customers love",
      insImprovements: "Room to improve",
      insRecommendations: "Recommendations",
      insStaff: "Praised staff",
      insBasedOn: "Based on {{n}} reviews",

      googleBusinessProfile: "Google Business Profile",
      googleBusinessProfileDescription: "Connect and manage your Google Business Profile",
      gbpIntegration: "Google Business Profile",
      gbpIntegrationDescription: "Connect your Google Business Profile to manage reviews",
      googleSettings: "Google",
      googleSettingsDescription: "Review link, ranking & automatic review replies",
      googleReviewLinkSection: "Review link & ranking",
      googleReviewRepliesSection: "Answer reviews automatically",
      gbpProfileViews: "Google profile views",
      gbpAvgRating: "Avg. rating",
      gbpNewReviews: "New reviews",
      gbpAnswerRate: "Reply rate",
      gbpFromReviews: "from {{count}} reviews",
      gbpAnsweredOf: "{{answered}} of {{total}} answered",
      gbpProfileViewsTooltip: "How often your Google profile was shown this week on Google Search and Maps. Google's data lags about 2-3 days.",
      gbpAvgRatingTooltip: "The average of all your Google star ratings.",
      gbpNewReviewsTooltip: "New Google reviews in the last 7 days, compared with the week before.",
      gbpAnswerRateTooltip: "Share of your reviews that are already answered. Sylva replies to your reviews automatically.",
      gbpConnected: "Connected to Google Business Profile",
      gbpConnectionFailed: "Failed to connect Google Business Profile",
      gbpDisconnected: "Google Business Profile disconnected",
      connectGBP: "Connect Google Business Profile",
      gbpDescription: "Connect your Google Business Profile to manage reviews, respond to customers, and post updates directly from this dashboard.",
      connectedOn: "Connected on",
      availableFeatures: "Available features:",
      gbpFeatureReviews: "View and respond to reviews",
      gbpFeaturePosts: "Create posts and updates",
      gbpFeatureNotifications: "Get notified about new reviews",
      gbpChangeProfile: "Change profile",
      gbpLocationSelected: "Google Business Profile saved",
      gbpSelectLocationTitle: "Select the correct Google profile",
      gbpSelectLocationDescription: "This Google account manages several business profiles. Choose the one that belongs to this customer.",
      gbpSuggestedMatch: "Suggested",
      gbpNoProfilesFound: "No Google profiles found for this account.",
      disconnect: "Disconnect",
      gbpNotConnected: "Not connected",
      gbpConnectPrompt: "Connect your Google Business Profile to unlock review management and posting features.",
      gbpOAuthNote: "You will be redirected to Google to authorize access to your Business Profile.",
      disconnectGBPTitle: "Disconnect Google Business Profile?",
      disconnectGBPDescription: "This will remove the connection to your Google Business Profile. You will no longer be able to manage reviews or create posts until you reconnect.",
      notificationPreferencesDescription: "Configure how you receive notifications",
      manageTeamMembers: "Manage Team Members",

      // Notification Preferences
      notificationPreferences: {
        notificationPreferencesTitle: "Notification Preferences",
        configureNotifications: "Configure Notifications",
        myNotificationPreferences: "My Notification Preferences",
        emailAddresses: "Email Addresses",
        emailAddressRequired: "Email address is required",
        invalidEmailAddress: "Please enter a valid email address",
        addEmailError: "Failed to add email address",
        emailAdded: "Email address added successfully",
        deleteEmailError: "Failed to remove email address",
        emailDeleted: "Email address removed successfully",
        toggleEmailError: "Failed to update notification settings",
        noEmailsConfigured: "No email addresses configured yet",
        enterEmail: "Enter email address",
        addEmail: "Add Email",
        removeEmail: "Remove email",
        toggleNotifications: "Toggle notifications",
        createdBy: "Created by",
        emailDescription: "You will receive notifications about new appointments, cancellations, and rescheduling at these email addresses.",
        comingSoon: "Coming soon:",
        smsComingSoon: "SMS notifications",
        whatsappComingSoon: "WhatsApp notifications",
      },

      notificationHistory: {
        tenantNotifications: "All Notifications",
        historyDescription: "View all notifications sent across your business",
        noNotifications: "No notifications sent yet",
        recipient: "To",
        created: "Created",
        sent: "Sent",
        errorStatus: "Error",
        unknownRecipient: "Unknown recipient",
      },

      // Voice Agent Settings
      voiceAgent: {
        title: "Voice Agent",
        description: "Configure your automated voice assistant for handling incoming calls",
        enableAgent: "Enable Voice Agent",
        enableAgentDescription: "Allow the voice agent to answer and handle incoming calls automatically",
        phoneNumber: "Phone Number",
        phoneNumberDescription: "This is your assigned phone number. Forward your business calls to this number to enable the voice agent.",
        phoneNumberUpdated: "Phone number updated successfully",
        failedToUpdatePhoneNumber: "Failed to update phone number",
        copyPhoneNumber: "Copy",
        phoneNumberCopied: "Phone number copied to clipboard",
        agentAvailableHeading: "Your voice agent is available at this number",
        agentAvailableDescription: "Forward calls to this number to have the agent automatically answer and assist your customers.",
        proRequired: "Pro plan required",
        proRequiredDescription: "Upgrade to the Pro plan to get a dedicated phone number for your voice agent.",
        lastSync: "Last Synchronized",
        syncNow: "Sync Now",
        syncing: "Syncing...",
        syncNowDescription: "Manually trigger synchronization to update the agent with latest services and team members",
        syncSuccess: "Voice agent synchronized successfully",
        syncError: "Failed to synchronize voice agent",
        statusUpdated: "Voice agent status updated",
        failedToUpdateStatus: "Failed to update voice agent status",
        statusSynced: "Synced",
        statusPending: "Syncing...",
        statusError: "Sync Failed",
        statusNeverSynced: "Not Synced",
        assistantInfo: "Assistant Information",
        assistantId: "Assistant ID",
        voiceProvider: "Voice Provider",
        modelProvider: "AI Model",
        configurationUpdated: "Configuration updated successfully",
        failedToUpdateConfiguration: "Failed to update configuration",
      },

      callForwarding: {
        title: "Set Up Call Forwarding",
        description: "Forward your calls to Sylvia so you never miss important customer inquiries",
        setupGuide: "Set Up Call Forwarding",
        setupGuideDescription: "Step-by-step guide to set up call forwarding",
        dialCodeInstructions: "How to Set Up Call Forwarding",
        step1: "Pick up your phone",
        step2: "Dial one of the codes below",
        step3: "Press the green call button",
        forwardingOptions: "Call Forwarding Options",
        chooseOption: "Choose one of the following options:",
        sylviaPhoneNumber: "Sylvia's Phone Number",
        method1Title: "Method 1: Via Settings",
        method2Title: "Method 2: Via Dial Code",
        dialCodeDescription: "Dial one of the following codes on your phone:",

        // Android instructions
        android: {
          step1: "Open the Phone app",
          step2: "Tap the menu (three dots) and select 'Settings'",
          step3: "Select 'Calls' and then 'Call forwarding'",
          step4: "Choose the desired option (e.g., 'Always forward', 'When busy')",
          step5: "Enter Sylvia's phone number:",
        },

        // iPhone instructions
        iphone: {
          step1: "Open 'Settings'",
          step2: "Select 'Phone'",
          step3: "Tap 'Call Forwarding' and enable the switch",
          step4: "Enter Sylvia's phone number:",
        },

        // Landline instructions
        landlineInstructions: "Call Forwarding via Dial Code",
        landlineDescription2: "Pick up the receiver and dial one of the following codes:",

        // Forwarding types
        alwaysForward: "Forward All Calls",
        alwaysForwardDescription: "Immediately forwards all incoming calls to Sylvia",
        busyForward: "Forward When Busy",
        busyForwardDescription: "Forwards calls when your line is busy",
        noAnswerForward: "Forward When No Answer",
        noAnswerForwardDescription: "Forwards calls when you don't answer",
        unreachableForward: "Forward When Unreachable",
        unreachableForwardDescription: "Forwards calls when you are unreachable",

        // Deactivation
        deactivateTitle: "Deactivate Call Forwarding",
        deactivateDescription: "To turn off call forwarding:",
        deactivateAll: "Deactivate All Call Forwarding",
        checkStatus: "Check Current Status",

        // Important notes
        importantNotes: "Important Notes",
        note1: "Call forwarding is usually free, but may vary depending on your carrier and plan",
        note2: "After setup, call your own number to test if Sylvia answers",
      },

      retry: "Retry",
      viewSettings: "View Settings",

      // Studio/Tenant Setup
      welcomeToSylva: "Welcome to Sylva",
      accountBeingSetUp: "Your account is being set up.",
      workspaceAccessPending: "You'll receive access to your workspace once it's ready.",
      setupYourStudio: "Let's set up your studio to get started",
      studioName: "Studio Name",
      studioNamePlaceholder: "Enter your studio name",
      ownerEmail: "Owner Email",
      ownerEmailPlaceholder: "owner@example.com",
      ownerEmailHelp: "Email address for notifications",
      primaryColor: "Primary Color",
      primaryColorHelp: "This color will be used for buttons in customer-facing emails",
      businessAddress: "Business Address",
      businessAddressPlaceholder: "123 Main Street",
      businessAddressHelp: "This address will be shown in customer emails with a link to Google Maps",
      city: "City",
      cityPlaceholder: "Your City",
      studioSlug: "Studio URL",
      studioSlugPlaceholder: "my-studio",
      studioSlugHelp: "This will be used in your public booking URL",
      createStudio: "Create Studio",

      // Google Profile
      connectGoogleProfile: "Connect Google Profile",
      googleProfileConnected: "Google Profile Connected",
      searchGoogleBusiness: "Search for your business",
      search: "Search",
      searchBusinessPlaceholder: "Enter your business name and location",
      selectYourBusiness: "Select your business from results",
      changeGoogleProfile: "Change Profile",
      viewOnGoogleMaps: "View on Google Maps",
      googleProfileHelp: "Connect your Google Business Profile to track rankings and manage your online presence",
      searching: "Searching...",
      noResultsFound: "No businesses found. Try a different search term.",
      disconnectGoogleProfile: "Disconnect",
      googleProfileDescription: "Link your Google Business Profile to enable local SEO tracking and analytics",

      // Member Management
      teamManagement: "Team Management",
      teamManagementDescription: "Manage team members and invitations for your organization",
      inviteNewOwner: "Invite New Owner",
      inviteOwner: "Invite Owner",
      inviteOwnerDescription: "Invite another owner to help manage this organization",
      memberEmailAddress: "Email Address",
      enterEmailAddress: "Enter email address",
      sendInvitation: "Send Invitation",
      sendingInvitation: "Sending Invitation...",
      pendingInvitations: "Pending Invitations",
      currentMembers: "Current Members",
      invitedOn: "Invited on",
      joinedOn: "Joined on",
      by: "by",
      revokeInvitation: "Revoke Invitation",
      revokeInvitationConfirmation: "Are you sure you want to revoke the invitation for {{email}}? They will no longer be able to join your organization using this invitation.",
      removeMember: "Remove Member",
      removeMemberConfirmation: "Are you sure you want to remove {{name}} from your organization? They will lose access to all data and settings.",
      noMembersYet: "No team members yet",
      inviteFirstMember: "Invite your first team member to start collaborating",

      // Member Management Success/Error Messages
      invitationSentSuccessfully: "Invitation sent successfully",
      failedToSendInvitation: "Failed to send invitation",
      memberRemovedSuccessfully: "Member removed successfully",
      failedToRemoveMember: "Failed to remove member",
      invitationRevokedSuccessfully: "Invitation revoked successfully",
      failedToRevokeInvitation: "Failed to revoke invitation",
      failedToLoadMembers: "Failed to load team members",
      userAlreadyMember: "This user is already a member of your organization",
      userAlreadyInvited: "This user has already been invited",
      memberEmailRequired: "Email address is required",
      memberInvalidEmailFormat: "Please enter a valid email address",

      // Roles
      owner: "Owner",
      admin: "Admin",
      manager: "Manager",
      member: "Member",
      viewer: "Viewer",
      creatingStudio: "Creating studio...",
      studioCreatedSuccessfully: "Studio created successfully",
      studioCreationFailed: "Failed to create studio",
      studioNameRequired: "Studio name is required",
      studioSlugRequired: "Studio URL is required",

      // Dashboard
      overview: "Overview",

      totalCustomers: "Total Customers",
      upcomingAppointments: "Upcoming Appointments",
      pastAppointments: "Past Appointments",
      todaysAppointments: "Today's Appointments",
      noAppointmentsToday: "No appointments scheduled for today",
      unassigned: "Unassigned",

      // Appointments
      services: "Appointment Types",

      duration: "Duration",
      price: "Price",
      priceOnRequest: "By arrangement",
      description: "Description",
      color: "Color",

      manageAllAppointments: "Manage all your appointments",
      allAppointments: "All Appointments",
      noAppointments: "No appointments found",
      searchServices: "Search services...",

      // Team
      teamMembers: "Team Members",

      updateTeamMember: "Update Team Member",
      editTeamMemberDescription:
        "Update team member information and availability",
      teamMemberUpdatedSuccess: "Team member has been updated successfully",
      teamMemberAddedSuccess: "Team member has been added successfully",
      back: "Back",
      teamMemberDetails: "Team Member Details",
      email: "Email",
      role: "Role",
      openDirectSchedulingLink: "Open Direct Scheduling Link",
      teamMemberNotAvailable: "Team member is not available",
      noServicesAvailable: "No services available",
      teamMemberNoServices: "Team member has no services assigned",

      // Customers
      newCustomer: "New Customer",
      name: "Name",
      firstNameIsRequired: "First name is required",
      lastNameIsRequired: "Last name is required",

      phone: "Phone",
      birthdate: "Birthdate",
      gender: "Gender",
      male: "Male",
      female: "Female",
      diverse: "Diverse",
      genderOptional: "Gender (optional)",
      street: "Street",
      zipCode: "ZIP Code",
      emailOptional: "Email (optional)",
      phoneOptional: "Phone (optional)",
      phoneFormatExample: "Format: +49221123456 (E.164)",
      searchCustomers: "Search customers...",
      createNew: "Create new",
      createNewCustomer: "Create New Customer",
      createCustomer: "Create Customer",
      customerCreated: "Customer created successfully",
      noCustomersFound: "No customers found",

      // Conversations
      noConversations: "No conversations",
      searchConversations: "Search conversations...",
      callInProgress: "Call in progress",
      callInProgressDescription: "Real-time transcript will appear below",
      aiPausedTitle: "Sylvia is paused.",
      aiPausedMessage: "Sylvia will start answering automatically again {{time}}.",
      customerTyping: "Customer is typing...",
      agentTyping: "Agent is typing...",
      customersList: "Customers",
      noCustomersWithConversations: "No customers with conversations",
      selectCustomerToViewMessages: "Select a customer to view messages",
      conversationCount: "{{count}} conversation",
      conversationCount_plural: "{{count}} conversations",
      channelVoice: "Voice",
      clickToViewTranscript: "Click to view transcript",
      viewTranscript: "View",
      voiceCallTranscript: "Voice Call Transcript",
      noTranscriptAvailable: "No transcript available",
      fullTranscript: "Full Transcript",
      sendMessage: "Send message",
      typeMessage: "Type a message...",
      sendVia: "Send via",
      channelSms: "SMS",
      channelWhatsapp: "WhatsApp",
      channelWeb: "Web",
      channelAvailable: "Available",
      channelNotConfigured: "Not configured",
      suggestedReplies: "Suggested Replies",
      generatingSuggestions: "Generating suggestions...",
      showSuggestions: "Show suggestions",
      whatsappConversation: "WhatsApp",
      chatbotConversation: "Chatbot",
      smsConversation: "SMS",
      allConversations: "All conversations",
      unreadConversations: "Unread",
      readConversations: "Read",
      conversationWith: "Conversation with",
      lastMessage: "Last message",
      messageSent: "Sent",
      messageFailed: "Failed",
      messagePending: "Sending...",
      messageQueued: "Queued",
      messageSending: "Sending",
      messageDelivered: "Delivered",
      messageRead: "Read",
      messageUndeliverable: "Undeliverable",
      sentBySylvia: "Sent automatically by Sylvia",
      callSummary: "Call Summary",
      errorCode: "Error Code",
      loadingConversations: "Loading conversations...",
      yesterday: "Yesterday",
      selectConversation: "Select a conversation to view messages",
      chatbotReadOnly: "Chatbot conversations are read-only",
      all: "All",
      unread: "Unread",
      leads: "Leads",
      whatsappChats: "WhatsApp Chats",
      existingCustomer: "Existing Customer",
      errorLoadingChats: "Error loading chats",
      noChatsWithCustomers: "No chats with existing customers",
      chatsWillAppearWhenCustomersMessage: "Chats will appear here when existing customers send messages",
      you: "You",
      loading: "Loading...",
      whatsappChatSelected: "WhatsApp chat selected",
      implementChatInterface: "Chat interface coming soon - currently showing WAHA integration",
      conversationSettings: "Conversation Settings",
      "conversationSettings.overallSettings": "General Settings",
      "conversationSettings.overallSettingsDescription": "Configure how the agent communicates with your customers",
      "conversationSettings.whatsappSettingsDescription": "Configure WhatsApp messaging settings",
      "conversationSettings.phoneSettings": "Phone",
      "conversationSettings.phoneSettingsDescription": "Configure voice agent and phone number settings",
      welcomeMessage: "Welcome Message",
      enableWelcomeMessage: "Enable welcome message",
      welcomeMessageDescription: "Automatically send a welcome message to new leads via WhatsApp",
      selectTemplate: "Select template",
      agentMode: "Agent Mode",
      enableAgentMode: "Enable automatic replies",
      agentModeDescription: "When enabled, the agent will automatically respond to customer messages",
      agentModeDisabledDescription: "When disabled, the agent will only suggest replies for staff to review",
      templateNotFound: "Template not found",
      templateNotFoundDescription: "The selected WhatsApp template does not exist. Please select a different template or create it in your Meta Business account.",
      templatePendingDescription: "This template is pending approval by Meta. It cannot be used until it is approved.",
      noTemplatesDescription: "No WhatsApp templates are configured. Please create templates in your Meta Business account first.",
      customerFormality: "Customer Address",
      customerFormalityDescription: "Choose how the agent addresses your customers in German",
      formalAddress: "Formal (Sie)",
      informalAddress: "Informal (Du)",
      manageTemplatesInMeta: "Manage your WhatsApp message templates in Meta Business Manager",
      metaBillingRequired: "Meta Account Billing Required",
      metaBillingDescription: "To send welcome messages, you need to add a payment method to your Meta Business account.",
      openBillingHub: "Add payment method",
      detailedInstructions: "Detailed instructions",
      createWelcomeTemplate: "Create Welcome Template",
      createWelcomeTemplateModalTitle: "Create WhatsApp Welcome Template",
      createWelcomeTemplateModalDescription: "This will create a new welcome message template in your WhatsApp Business account. The template will be submitted for review by Meta.",
      templateLanguage: "Language",
      templateCategory: "Category",
      templateCategoryMarketing: "Marketing",
      websiteButton: "Website Button",
      websiteButtonUrl: "Website URL",
      createTemplateButton: "Create Template",
      templateCreatedSuccessTitle: "Template Created",
      templateCreatedSuccessDescription: "The welcome template has been created and submitted for Meta's review. It will be available once approved.",
      templateCreationFailedTitle: "Template Creation Failed",
      templateCreationFailedDescription: "Failed to create the welcome template. Please try again or contact support.",
      templateAlreadyExists: "A template with this name already exists",
      missingWebsiteUrl: "Please add a website URL to your business settings first",
      selectFormalityFirst: "Please select a customer formality level first",

      // WhatsApp Integration
      whatsapp: {
        preflightTitle: 'Before you start',
        preflightSubtitle: 'Please confirm each point to avoid common setup issues.',
        preflightItem1: 'My business phone number is NOT currently active in the WhatsApp or WhatsApp Business app on any phone.',
        preflightItem1Hint: 'If it is, the setup will fail. Delete the WhatsApp account on the phone first — see the reset video below.',
        preflightItem2: 'I have a Meta Business Manager / Business Portfolio (or am okay with Meta creating one during setup).',
        preflightItem2Link: 'Open Meta Business Suite',
        preflightItem3: 'I have my Facebook login credentials ready (personal account used to manage the business).',
        preflightAllRequired: 'Please confirm all items to continue.',
        connectionActive: 'Connected',
        connectionExpired: 'Connection expired — please reconnect',
        connectionError: 'Connection issue — please check',
        connectionUnknown: 'Connection status unknown',
        connectedNumber: 'Connected number ID',
        lastChecked: 'Last checked',
        recheckNow: 'Check now',
        rechecking: 'Checking...',
        conciergeTitle: 'Need help? Let us set it up for you',
        conciergeText: 'Our team will schedule a 15-minute screen share and connect WhatsApp for you.',
        conciergeCta: 'Request setup help',
        conciergeEmailSubject: 'WhatsApp setup help requested',
        conciergeEmailGreeting: 'Hi Sylva team,',
        conciergeEmailBody: 'I need help connecting WhatsApp.',
        connectWhatsApp: "Connect WhatsApp",
        disconnectWhatsApp: "Disconnect WhatsApp",
        reconnectWhatsApp: "Reconnect WhatsApp",
        reconnect: "Reconnect",
        sessionDisconnected: "Your WhatsApp session has been disconnected. Reconnect to continue messaging.",
        scanQRCode: "Scan QR Code",
        connectionStatus: "Connection Status",
        connected: "Connected",
        connecting: "Connecting...",
        disconnected: "Disconnected",
        qrCodeExpired: "QR code expired, please refresh",
        sessionCreated: "WhatsApp session created successfully",
        sessionCreateError: "Failed to create WhatsApp session",
        sessionStarted: "WhatsApp session started",
        sessionStartError: "Failed to start WhatsApp session",
        sessionStopped: "WhatsApp session stopped",
        sessionStopError: "Failed to stop WhatsApp session",
        sessionDeleted: "WhatsApp session deleted",
        sessionDeleteError: "Failed to delete WhatsApp session",
        sessionRestarted: "WhatsApp session restarted",
        sessionRestartError: "Failed to restart WhatsApp session",
        qrCodeError: "Failed to load QR code",
        refreshQRCode: "Refresh QR Code",
        scanWithPhone: "Scan this QR code with your WhatsApp mobile app",
        openWhatsApp: "Open WhatsApp on your phone",
        goToSettings: "Go to Settings > Linked Devices",
        tapLinkDevice: "Tap 'Link a Device'",
        scanCode: "Scan this code to connect",
        whatsappBusiness: "WhatsApp Business",
        connectWhatsappDesc: "Connect your WhatsApp Business account to manage conversations.",
        whatsappConnected: "WhatsApp Connected",
        whatsappConnectedDesc: "Your WhatsApp Business account has been connected successfully.",
        whatsappConnectionError: "Failed to connect WhatsApp Business account.",
        whatsappConnectionCancelled: "WhatsApp connection was cancelled.",
        whatsappTimeoutTitle: 'Connection timed out',
        whatsappTimeoutDesc: 'The WhatsApp connection took too long. Please try again and make sure you click "Finish" at the end of the Meta popup.',
        facebookSdkNotLoaded: "Facebook SDK is not loaded yet. Please try again.",
        whatsappNotConfigured: "WhatsApp integration is not configured.",
        serviceWindowActive: "24h window active",
        serviceWindowExpired: "Window expired",
        noTemplatesAvailable: "No WhatsApp templates available. Please create templates in your WhatsApp Business Manager.",
        manageTemplates: "Manage templates",
        loadingTemplates: "Loading templates...",
        showTemplates: "Show templates",
        hideTemplates: "Hide templates",
        sendTemplate: "Send template",
        openInWhatsApp: "Open in WhatsApp",
        openInWhatsAppBusinessHint: "To send a free-form message, open the conversation in WhatsApp Business app.",
        serviceWindowExpiredDesktopHint: "The 24-hour service window has expired. Send a template or use WhatsApp Business app for free-form messages.",
        approvedTemplates: "Approved Templates",
        serviceWindowExpiredTitle: "Customer Service Window Expired",
        serviceWindowExpiredMessage: "The 24-hour customer service window has expired. You can only send approved WhatsApp templates until the customer messages you again.",
        templateLoadError: "WhatsApp Not Configured",
        templateLoadErrorMessage: "WhatsApp Business integration is not properly set up. Please contact your administrator.",
        fillTemplateParameters: "Fill in the template parameters",
        parameter: "Parameter",
        example: "example",
        enterValue: "Enter value",
        messagePreview: "Preview",
        setupPrompt: "Connect WhatsApp to send messages",
        setupLink: "Set up",
      },

      conversationMessages: {
        noPhoneNumberTitle: "No Phone Number",
        noPhoneNumberMessage: "This customer doesn't have a phone number. Outbound messaging is not possible.",
      },

      capacity: "Capacity",

      // Common
      save: "Save",
      edit: "Edit",
      delete: "Delete",
      send: "Send",
      sending: "Sending...",
      cancelVisit: "Cancel Visit",
      success: "Success",
      info: "Info",
      actions: "Actions",
      copySchedulingLink: "Copy Scheduling Link",

      // AI Chatbot
      chatbotTitle: "AI Assistant",
      chatbotPlaceholder: "Ask me anything...",
      chatbotSend: "Send",
      chatbotThinking: "Thinking...",
      chatbotError: "Sorry, I encountered an error. Please try again.",
      chatbotAcceptSuggestion: "Accept",
      chatbotDeclineSuggestion: "Decline",
      chatbotSuggestionAccepted: "Accepted",
      chatbotSuggestionDeclined: "Declined",
      chatbotActionConfirm: "Confirm Action",
      chatbotBlockTimeSuccess: "Time blocked successfully",
      chatbotBlockTimeError: "Failed to block time",
      chatbotSuggestionCreated: "I've created a time blocking suggestion for you.",
      chatbotBlockTimeSuggestion: "Block Time Suggestion",
      chatbotTeamMembers: "Team members",
      chatbotBlockedViaAI: "Blocked via AI assistant suggestion",
      chatbotUnderstood: "Understood. Let me know if you need help with anything else.",
      chatbotResponseCutOff: "I apologize, but my response was cut off. Could you please try asking your question again?",
      chatbotMalformedRequest: "Sorry, I had trouble processing your request. Could you please specify: what date and times would you like me to block?",
      chatbotNeedMoreInfo: "I understand your request, but I need more specific information to assist you properly.",
      chatbotGenericError: "I apologize, but I couldn't process your request at this time.",

      // Messages
      createSuccess: "Created successfully!",
      updateSuccess: "Updated successfully!",
      deleteSuccess: "Deleted successfully!",
      templateDeleted: "Template has been deleted successfully.",
      createError: "Failed to create. Please try again.",
      updateError: "Failed to update. Please try again.",
      deleteError: "Failed to delete. Please try again.",

      // Authentication & Login
      loginFailed: "Login Failed",
      staffLogin: {
        emailOrUsername: "Email or username",
        salon: "Salon",
        pickSalon: "Please enter your salon to continue",
        enterSalon: "Your salon name",
        invalidCredentials: "Login details are incorrect",
        readOnlyAppointment: "View only — you can edit only your own appointments.",
        admin: {
          title: "Login & access",
          description:
            "Give this team member their own login. They will only see and manage their own calendar — never the inbox, customers, analytics or settings.",
          usernameLabel: "Username",
          usernamePlaceholder: "e.g. anna",
          usernameHint: "3–30 characters: lowercase letters, digits, . _ -",
          usernameInvalid:
            "Username must be 3–30 characters and may only contain lowercase letters, digits and . _ -",
          passwordLabel: "Password",
          newPasswordPlaceholder: "New password",
          passwordHint: "At least {{min}} characters.",
          passwordTooShort: "Password must be at least {{min}} characters.",
          createButton: "Create login",
          loginCreated: "Login created",
          loginCreatedBody:
            "The team member can now sign in with their username and password.",
          resetPasswordLabel: "Reset password",
          resetPasswordButton: "Reset password",
          passwordReset: "Password reset",
          passwordResetBody: "The new password is active immediately.",
          statusActive: "Active",
          statusInactive: "Deactivated",
          activeToggleLabel: "Login active",
          activeToggleHint: "Turn off to block this login immediately.",
          activated: "Login activated",
          activatedBody: "The team member can sign in again.",
          deactivated: "Login deactivated",
          deactivatedBody: "The login is blocked immediately.",
          deactivateConfirmTitle: "Deactivate login?",
          deactivateConfirmBody:
            "The team member will be signed out and can no longer log in until you reactivate the login.",
          deactivateConfirmAction: "Deactivate",
          scopeLabel: "Staff see the whole salon calendar",
          scopeHint:
            "On: staff can view all appointments (read-only). Off: each staff member sees only their own. Applies to all staff logins in this salon.",
          scopeSaved: "Setting saved",
          scopeSaveError: "Could not save the setting. Please try again.",
          genericError: "Something went wrong. Please try again.",
          sectionTitle: "Team & access",
          sectionDescription: "Create and manage logins for your team. Staff see only their own calendar and customers; admins have full access.",
          sectionNavDescription: "Logins, roles & permissions",
          salonScopesTitle: "What staff can see",
          customerScopeLabel: "Staff can use the customer list",
          customerScopeHint: "On: staff see and edit the full customer list. Off: staff have no customer list (they still see customer names on their own appointments).",
          membersTitle: "Team logins",
          noMembers: "No team members yet. Add them under Availability → Calendar profiles.",
          noLogin: "No login",
          roleLabel: "Role",
          roleStaff: "Staff (own calendar only)",
          roleAdmin: "Admin (full access)",
          roleChanged: "Role updated",
          noPermission: "Only the owner or an admin can manage team access.",
          roleBadgeStaff: "Staff",
          roleBadgeAdmin: "Admin",
          ownerNote: "Studio owner — full access, managed via their own account.",
          nameRequired: "Please enter a name.",
          addMember: "Add team member",
          addMemberTitle: "New team member",
          namePlaceholder: "e.g. Yara",
          createMemberButton: "Create + give login",
          addMemberHint: "Default working hours are applied — refine them later under Availability.",
        },
      },
      signupFailed: "Signup Failed",
      passwordsMustMatch: "Please make sure your passwords match.",
      welcomeBack: "Welcome back!",
      loginSuccessful: "You have been logged in successfully.",
      passwordMismatch: "Password Mismatch",
      accountCreated: "Account Created!",
      checkEmailVerification: "Please check your email to verify your account.",
      getStarted: "Get Started",
      login: "Login",
      signUp: "Sign Up",
      password: "Password",
      confirmPassword: "Confirm Password",
      forgotPassword: "Forgot Password?",
      resetPassword: "Reset Password",
      resetPasswordDescription: "Enter your email address and we'll send you a link to reset your password.",
      sendResetLink: "Send Reset Link",
      resetLinkSent: "Reset Link Sent",
      checkEmailReset: "Check your email for password reset instructions.",
      setPassword: "Set Password",
      setPasswordDescription: "Choose a secure password for your account.",
      savePassword: "Save Password",
      passwordSet: "Password Set",
      passwordSetSuccess: "Your password has been set successfully.",
      newPassword: "New Password",
      enterNewPassword: "Enter new password",
      confirmNewPassword: "Re-enter new password",
      setupPasswordTitle: "Set Up Your Password",
      setupPasswordDescription: "Welcome! Please set up a secure password for your account to continue.",
      passwordMismatchTitle: "Passwords Don't Match",
      passwordMismatchDescription: "The passwords you entered don't match. Please try again.",
      passwordTooShortTitle: "Password Too Short",
      passwordTooShortDescription: "Your password must be at least 8 characters long.",
      passwordSetSuccessTitle: "Password Set Successfully",
      passwordSetSuccessDescription: "Your password has been set. You can now access your account.",
      passwordSetErrorTitle: "Password Setup Failed",
      passwordSetErrorDescription: "There was an error setting up your password. Please try again.",
      passwordRequirements: "Password must be at least 8 characters long.",
      settingPassword: "Setting password...",
      linkExpiredTitle: "Link Expired",
      linkExpiredDescription: "This password setup link has expired or is invalid. Please request a new invitation or reset your password.",
      goToLogin: "Go to Login Page",
      forgotPasswordLink: "Forgot Password?",
      passwordTooShort: "Password must be at least 8 characters long.",
      invalidResetToken: "Invalid or expired reset token. Please request a new password reset.",
      passwordResetFailed: "Password reset failed. Please try again.",
      backToLogin: "Back to Login",
      youWillBeRedirectedToTheDashboardShortly: "You will be redirected to the dashboard shortly...",

      // Booking & Appointments
      bookingFailed: "Booking Failed",
      failedToCreateCustomer: "Failed to create customer. Please try again.",
      failedToCreateAppointment:
        "Failed to create appointment. Please try again.",
      pleaseSelectCustomer: "Please select a customer",
      pleaseSelectDate: "Please select a date",
      pleaseSelectTime: "Please select a time",
      selectExistingCustomer:
        "Select existing customer or enter new details below",
      specialRequestsPlaceholder:
        "Any special requests, allergies, or preferences...",
      pleaseTryAgain: "Please try again.",
      pleaseSelectServiceType: "Please select a service type",
      failedToSignOut: "Failed to sign out. Please try again.",
      customerNotFound: "Customer not found. Please select a valid customer.",
      selectServiceStep: "Select Service",
      resourceCreated: "Resource Created",
      resourceCreatedSuccessfully: "Resource created successfully!",

      // Booking Links
      bookingLinks: "Booking Links",
      bookingLinksDescription: "Generate and share booking links for your studio, specific services, or team members",
      studioBookingLink: "Studio Booking Link",
      serviceBookingLink: "Service Booking Link",
      teamMemberBookingLink: "Team Member Booking Link",
      studioBookingLinkDescription:
        "Link for customers to book any service at your studio",
      serviceBookingLinkDescription: "Direct link to book a specific service",
      teamMemberBookingLinkDescription:
        "Link to book with a specific team member",

      bookingLink: "Booking Link",
      bookingUrl: "Booking URL",
      externalBookingUrl: "External Booking URL",
      externalBookingUrlDescription: "Enter your external booking system URL. Visitors to your Sylva booking page will be redirected here.",
      externalBookingUrlSaved: "External booking URL saved successfully",
      failedToSaveExternalBookingUrl: "Failed to save external booking URL",
      sylvaPortalUrl: "Your Sylva Booking Page",
      sylvaPortalUrlDescription: "This is your Sylva booking page URL. When an external URL is set above, visitors will be redirected there.",
      copyAllLinks: "Copy All Links",
      allLinksCopied: "All booking links copied as Markdown",
      studio: "Studio",
      service: "Service",
      uncategorized: "Uncategorized",
      teamMember: "Team Member",

      // Languages
      english: "English",
      german: "German",

      // Templates Module

      manageTemplatesDescription:
        "Manage intake forms and email templates for your appointments",
      intakeForms: "Intake Forms",

      quickActions: "Quick Actions",
      tasks: "Tasks",
      tasksDescription: "Manage all tasks from conversations",
      searchTasks: "Search tasks...",
      allTasks: "All",
      pendingTasks: "Pending",
      completedTasks: "Completed",
      noTasksFound: "No tasks found",
      task: "Task",
      createdAt: "Created",
      reopen: "Reopen",
      taskReopened: "Task reopened",
      failedToReopenTask: "Failed to reopen task",
      noPendingTasks: "No pending tasks",
      viewConversation: "View conversation",
      markAsDone: "Mark as done",
      taskCompleted: "Task completed",
      failedToCompleteTask: "Failed to complete task",
      taskFor: "Customer",

      // Intake Forms
      intakeFormsDescription:
        "Collect additional information from customers before appointments",
      createForm: "Create Form",
      noFormsYet: "No Forms Yet",
      createFirstFormDescription:
        "Create your first intake form to collect information from customers",

      searchForms: "Search forms...",
      allTypes: "All Types",
      allStatus: "All Status",

      customForm: "Custom Form",
      termsConditions: "Terms & Conditions",
      medicalInformation: "Medical Information",
      duplicate: "Duplicate",
      formDuplicated: "Form Duplicated",
      formDeleted: "Form Deleted",

      // Form Builder
      editForm: "Edit Form",
      createNewForm: "Create New Form",
      formSettings: "Form Settings",

      date: "Date",
      time: "Time",

      noFieldsYet: "No fields yet",
      addFieldsDescription:
        "Add fields from the panel on the left to start building your form.",

      formNameRequired: "Form name is required",
      formMustHaveFields: "Form must have at least one field",
      formCreated: "Form Created",
      formUpdated: "Form Updated",
      savingForm: "Saving...",
      creating: "Creating...",
      updating: "Updating...",
      deleting: "Deleting...",
      cancelling: "Cancelling...",
      deletingCustomers: "Deleting customers...",
      update: "Update",
      saveForm: "Save Form",

      // Form Preview

      validationError: "Validation Error",
      pleaseCheckYourInput: "Please check your input and try again",

      selectAnOption: "Select an option",

      // Form Builder - Additional Labels
      formNameLabel: "Form Name",
      formDescriptionLabel: "Description",
      formTypeLabel: "Form Type",
      activeLabel: "Active",

      // Form Builder - Placeholders
      formNamePlaceholder: "e.g., Medical History Form",
      formDescriptionPlaceholder: "Brief description of this form",

      // Form Builder - Type Options
      customFormOption: "Custom Form",
      termsConditionsOption: "Terms & Conditions",
      medicalInformationOption: "Medical Information",

      // Form Builder - Field Actions
      addFieldLabel: "Add Field",
      fieldPropertiesLabel: "Field Properties",
      labelLabel: "Label",
      placeholderLabel: "Placeholder",
      requiredLabel: "Required",
      optionsLabel: "Options",
      addOptionLabel: "Add Option",
      minLengthLabel: "Min Length",
      maxLengthLabel: "Max Length",
      submitFormLabel: "Submit Form",
      selectFieldToEditLabel: "Select a field to edit its properties",

      // Form List Actions
      previewAction: "Preview",
      editAction: "Edit",
      duplicateAction: "Duplicate",
      deleteAction: "Delete",
      createYourFirstFormAction: "Create Your First Form",

      // Form List Messages
      noFormsMatchFilter: "No Forms Match Filter",
      adjustSearchFilter: "Try adjusting your search or filter criteria",

      // Email Templates
      notificationTemplates: "Notification Templates",
      manageAutomatedMessages: "Manage automated messages for appointments",
      addTemplate: "Add Template",
      templateName: "Template Name",
      messageType: "Message Type",
      sms: "SMS",

      appointmentConfirmed: "Appointment Confirmed",
      appointmentReminder: "Appointment Reminder",
      postAppointmentFollowup: "Post-Appointment Follow-up",
      appointmentCancelled: "Appointment Cancelled",
      appointmentRescheduled: "Appointment Rescheduled",
      marketingConsentVerification: "Marketing Consent Verification",
      appointmentCancelledAndNotified:
        "Appointment cancelled and customer notified",
      appointmentDeletedAndNotified:
        "Appointment deleted and customer notified",
      appointmentRescheduledAndNotified:
        "Appointment rescheduled and customer notified",

      completed: "Completed",
      cancelled: "Cancelled",
      no_show: "No Show",

      templateCreated: "Template Created",
      templateUpdated: "Template Updated",
      notificationTemplateUpdatedSuccessfully:
        "Notification template has been updated successfully.",
      createTemplate: "Create Template",
      updateTemplate: "Update Template",
      noTemplatesYet: "No Templates Yet",

      // Template bulk operations
      templateSelected: "template selected",
      templatesSelected: "templates selected",
      deleteTemplates: "Delete Templates",
      bulkDeleteConfirmation:
        "Are you sure you want to delete {{count}} templates? This action cannot be undone.",
      bulkDeleteSuccess: "{{count}} templates deleted successfully",
      bulkDuplicateSuccess: "{{count}} templates duplicated successfully",
      bulkDeletePartialFailure:
        "{{failed}} of {{total}} templates could not be deleted",
      bulkDuplicatePartialFailure:
        "{{failed}} of {{total}} templates could not be duplicated",
      clearSelection: "Clear Selection",

      // Template Assignments

      // Form Assignment

      intakeFormAssignedSuccessfully:
        "Intake form has been assigned successfully.",

      // Form Field Types

      // Form Validation

      // Status and Actions

      appointmentDeletedSuccess: "Appointment deleted successfully",
      appointmentNotFound: "Appointment not found",
      noResource: "No resource",
      confirmed: "Confirmed",
      appointmentNotes: "Appointment Notes",
      updateAppointment: "Update Appointment",
      appointmentSummary: "Appointment Summary",
      noResourcesAvailable: "No resources available",
      noCategory: "No category",
      warning: "Warning",
      exportCustomers: "Export Customers",
      emailAddress: "Email Address",
      errorLoadingAppointment: "Error loading appointment",
      failedToCopy: "Failed to copy",
      activeUsers: "Active Users",

      // Dashboard
      recentServices: "Recent Services",

      createNewService: "Create New Service",
      addNewService: "Add a new service",
      addTeamMember: "Add Team Member",
      inviteNewStaffMember: "Invite a new staff member",
      viewBookings: "View Bookings",
      checkTodaysAppointments: "Check today's appointments",
      noServicesConfigured: "No services configured yet",

      // Appointment Types
      servicesServices: "Services & Appointment Types",
      manageServiceOfferings: "Manage your service offerings",
      editService: "Edit Service",
      editServiceDescription: "Update service information and pricing details",

      bufferBefore: "Buffer Before (min)",
      bufferAfter: "Buffer After (min)",
      colorHelp:
        "Determines the block color in the calendar for internal differentiation",
      visibility: "Visibility",
      private: "Private",
      isPrivate: "Private",
      public: "Public",
      privateHelp: "Only available via direct booking link",
      publicHelp: "Visible in public booking widget",
      currency: "Currency",
      serviceDetails: "Service Details",
      createService: "Create Service",
      updateService: "Update Service",
      serviceUpdatedSuccess: "Service updated successfully",
      serviceCreatedSuccess: "Service created successfully",

      // Required Resources

      resourceAdded: "Resource Added",

      resourceRemoved: "Resource Removed",

      failedToUpdateResourceAssignment: "Failed to update resource assignment",

      // Multi-select resources
      // Internal admin console (Sylva staff only)
      adminConsoleTitle: "Admin console",
      adminConsoleSubtitle: "Every tenant at a glance, and the switch that takes one out of service.",
      adminMetricRevenue: "Revenue (7 days)",
      adminMetricNewCustomers: "New customers (7 days)",
      adminMetricNewLeads: "New leads (7 days)",
      adminMetricNewReviews: "New reviews (7 days)",
      adminFilterSearch: "Search tenant",
      adminFilterStatusAll: "All statuses",
      adminFilterStatusActive: "Active only",
      adminFilterStatusDeactivated: "Deactivated only",
      adminFilterPackageAll: "All packages",
      adminChartTitle: "Comparison",
      adminChartSubtitle: "Top 15 of the current selection",
      adminTableTitle: "Tenants",
      adminColTenant: "Tenant",
      adminColPackage: "Package",
      adminColRating: "Rating",
      adminColAnswerRate: "Answer rate",
      adminColLastAppointment: "Last appointment",
      adminColLastMessage: "Last message",
      adminColActive: "Active",
      adminBadgeDeactivated: "Deactivated",
      adminNoTenants: "No tenant matches this filter",
      adminNoAccess: "This area is for Sylva staff.",
      adminTenantDeactivated: "Tenant deactivated",
      adminTenantReactivated: "Tenant reactivated",
      adminToggleFailed: "Could not switch the tenant. Details are in the browser console.",
      resourceAssignment: "Resource Assignment",
      teamMemberAssignment: "Team Member Assignment",
      selectTeamMembersForService:
        "Choose who performs this service. At least one person is required – otherwise it cannot be booked online.",
      teamMemberRequired:
        "Assign at least one team member, otherwise this service cannot be booked.",

      selectResourcesRequiredForService:
        "Select resources required for this service. Resources are items like rooms, equipment, or materials needed to perform the service.",
      resourceAssignedToService: "Resource has been assigned to this service",
      resourceUnassignedFromService:
        "Resource has been unassigned from this service",

      autoAssignedResources: "These resources will be automatically assigned",
      noResourcesConfiguredForService:
        "No resources are configured for this service",
      serviceUpdated: "Service Updated",
      serviceCreated: "Service Created",

      somethingWentWrong: "Something went wrong. Please try again.",
      noServicesYet: "No Services Yet",
      createFirstService:
        "Create your first service to get started",
      addFirstService: "Add Your First Service",

      deleteService: "Delete Service",

      deleteServiceFailed: "Failed to delete service. Please try again.",
      serviceDeletedSuccessfully: "Service deleted successfully",
      duplicateServiceFailed: "Failed to duplicate service. Please try again.",
      serviceDuplicatedSuccessfully: "Service duplicated successfully",
      noServicesFound: "No services found",
      servicesFound: "{{count}} services found",
      showDeletedServices: "Show deleted services",
      deleted: "Deleted",
      serviceSelected: "service selected",
      servicesSelected: "services selected",
      selected: "selected",
      selectAll: "Select All",
      deselectAll: "Deselect All",
      showingEntries: "Showing {{from}} to {{to}} of {{total}} entries",
      entriesPerPage: "Entries per page",
      page: "Page",
      of: "of",
      previous: "Previous",
      next: "Next",
      bulkDeleteServices: "Delete Services",

      bulkDuplicateServices: "Duplicate Services",

      bulkDeleteServicesFailed: "Failed to delete services. Please try again.",
      bulkDuplicateServicesFailed:
        "Failed to duplicate services. Please try again.",
      duplicating: "Duplicating...",

      // Categories

      // Team Members
      manageStaffAvailability: "Manage your staff and their availability",
      editTeamMember: "Edit Team Member",

      fullName: "Full Name",

      weeklyAvailability: "Weekly Availability",
      monday: "Monday",
      tuesday: "Tuesday",
      wednesday: "Wednesday",
      thursday: "Thursday",
      friday: "Friday",
      saturday: "Saturday",
      sunday: "Sunday",

      to: "To:",
      add: "Add",
      teamMemberUpdated: "Team Member Updated",
      teamMemberAdded: "Team Member Added",
      teamMemberDeleted: "Team Member Deleted",
      teamMemberDeletedSuccess: "Team member has been deleted successfully",
      deleteTeamMember: "Delete Team Member",
      confirmDeleteTitle: "Confirm Delete",
      confirmDeleteTeamMember: "Are you sure you want to delete {{name}}? This action cannot be undone.",
      cannotDeleteTeamMemberFutureAppointments: "Cannot delete {{name}}. They have {{count}} future appointment(s). Please cancel or reschedule these appointments first.",
      paused: "Paused",
      pauseTeamMember: "Pause",
      endPause: "End pause",
      pausedIndefinitely: "Paused (until further notice)",
      pausedUntil: "Paused until {{date}}",
      pauseDialogTitle: "Pause team member",
      pauseDialogDescription: "While paused, {{name}} is not bookable by customers on any channel and their login is disabled. They stay visible internally and all settings and existing appointments are kept.",
      pauseDurationLabel: "Duration",
      pauseIndefiniteOption: "Until further notice",
      pauseUntilOption: "Pause until a date",
      pauseReasonLabel: "Reason (optional)",
      pauseReasonNone: "No reason",
      pauseReasonVacation: "Vacation",
      pauseReasonParentalLeave: "Parental leave",
      pauseReasonSickness: "Sickness",
      pauseReasonOther: "Other",
      confirmPause: "Pause",
      teamMemberPaused: "Team member paused",
      teamMemberPausedSuccess: "{{name}} has been paused",
      teamMemberResumed: "Pause ended",
      teamMemberResumedSuccess: "{{name}} is bookable again",
      pauseError: "Could not pause the team member",
      resumeError: "Could not end the pause",
      selectServicesDescription:
        "Select which services this team member can provide",
      noServicesSelectedTitle: "No services selected",
      noServicesSelectedWarning:
        "Customers cannot book this team member — neither by name nor via \"any available team member\". Internal booking in the calendar stays possible.",
      daysWeek: "days/week",
      noTeamMembersYet: "No Team Members Yet",
      addFirstTeamMember:
        "Add your first team member to start scheduling appointments",
      addFirstTeamMemberButton: "Add Your First Team Member",

      // Customers

      editCustomer: "Edit Customer",
      addNewCustomer: "Add New Customer",
      addCustomer: "Add Customer",
      phoneNumber: "Phone Number",
      phoneRequired: "Phone Number *",

      marketingPreferences: "Marketing Preferences",
      marketingPreferencesHint: "Set how the customer would like to receive updates and promotions. These preferences can be changed at any time.",
      emailMarketing: "Email",
      emailMarketingExport: "Email Marketing",
      whatsappMarketing: "WhatsApp",
      whatsappMarketingExport: "WhatsApp Marketing",
      whatsappNotifications: "WhatsApp notifications",
      notes: "Notes",

      customerUpdated: "Customer Updated",
      customerAdded: "Customer Added",

      // Enhanced Customer Management
      filterAndSort: "Filter & Sort",
      categories: "Categories",
      sortBy: "Sort by",
      activeFilters: "Active Filters",
      noCategories: "No categories available",
      dateCreated: "Date Created",
      dateCreatedNewestFirst: "Date Created (newest first)",
      dateCreatedOldestFirst: "Date Created (oldest first)",
      serviceNameAZ: "Service Name (A-Z)",
      serviceNameZA: "Service Name (Z-A)",
      categoryAZ: "Category (A-Z)",
      categoryZA: "Category (Z-A)",
      durationShortestFirst: "Duration (shortest first)",
      durationLongestFirst: "Duration (longest first)",
      priceLowestFirst: "Price (lowest first)",
      priceHighestFirst: "Price (highest first)",
      firstNameAZ: "First Name (A-Z)",
      firstNameZA: "First Name (Z-A)",
      lastNameAZ: "Last Name (A-Z)",
      lastNameZA: "Last Name (Z-A)",
      emailAZ: "Email (A-Z)",
      emailZA: "Email (Z-A)",
      phoneAZ: "Phone (A-Z)",
      phoneZA: "Phone (Z-A)",
      at: "at",
      createAppointment: "Create Appointment",
      scheduleNewAppointment: "Schedule a new appointment",
      markTimeAsUnavailable: "Mark time as unavailable",

      exportSelected: "Export Selected",
      deleteSelected: "Delete Selected",
      exportAll: "Export All",
      importCSV: "Import CSV",
      viewProfile: "View Profile",
      scheduleAppointment: "Schedule Appointment",
      marketing: "Marketing",

      confirmDeleteCustomer: "Are you sure you want to delete this customer?",

      bulkDeleteCustomers: "Delete Customers",

      deleteCustomer: "Delete Customer",

      customerDeleted: "Customer Deleted",
      customerDeletedDescription: "Customer has been deleted successfully.",
      customersDeleted: "Customers Deleted",
      customersDeletedDescription:
        "{{count}} customers have been deleted successfully.",

      customerUpdatedDescription:
        "Customer information has been updated successfully.",
      customerAddedDescription: "New customer has been added successfully.",
      duplicateCustomerError: "A customer with this phone number and first name already exists",
      duplicateCustomerErrorWithName: "Customer {{name}} already exists with this phone number and first name",

      // Missing customer-related keys
      customersFound: "{{count}} customers found",
      customersFoundWithTotal: "{{filtered}} of {{total}} customers found",
      filterBy: "Filter by",
      allCustomers: "All Customers",
      leadsOnly: "Leads Only",
      customersWithAppointments: "Customers with Appointments",
      lead: "Lead",
      birthdaysNext7Days: "Birthday in next 7 days",
      customerType: "Type",
      customerTypes: "Customer Types",
      clearAll: "Clear All",
      selectedCustomers: "{{count}} customers selected",
      customerSelected: "customer selected",
      customersSelected: "customers selected",
      deleteCustomerConfirmation:
        "Are you sure you want to delete {{name}}? This action cannot be undone.",
      bulkDeleteCustomersConfirmation:
        "Are you sure you want to delete {{count}} customers? This action cannot be undone.",

      customerProfile: "Customer Profile",

      notesPlaceholder:
        "Customer preferences, allergies, special requirements...",
      customer: "customer",

      // Customer Profile
      customerDetails: "Customer Details",
      customerData: "Customer Data",
      appointmentHistory: "Appointment History",
      registrationForms: "Registration Forms",
      registrationFormsPlaceholder: "No registration forms completed yet",
      registrationFormsDescription: "Customer intake and registration forms",
      basicInformation: "Basic Information",
      internalNotes: "Internal Notes",
      emailMarketingDescription: "Allow email marketing communications",
      whatsappMarketingDescription: "Allow WhatsApp marketing communications",
      optedIn: "Opted In",
      optedOut: "Opted Out",
      customerInformation: "Customer Information",
      accountInformation: "Account Information",
      customerSince: "Customer Since",
      lastUpdated: "Last Updated",
      customerStatus: "Customer Status",

      noPastAppointments: "No past appointments",
      appointmentHistoryWillAppearHere: "Appointment history will appear here",
      rescheduleAppointment: "Reschedule Appointment",
      deleteAppointment: "Delete Appointment",
      markAsNoShow: "Mark as No-Show",
      unmarkNoShow: "Unmark No-Show",
      appointmentMarkedAsNoShow: "Appointment marked as no-show",
      appointmentUnmarkedAsNoShow: "Appointment unmarked as no-show",
      rescheduleFeatureComingSoon: "Reschedule feature coming soon",
      deleteFeatureComingSoon: "Delete feature coming soon",
      selectNewDateTime: "Select New Date & Time",
      currentAppointment: "Current Appointment",
      rescheduleSuccessful: "Appointment Rescheduled Successfully",
      appointmentRescheduledSuccessfully: "Your appointment has been rescheduled successfully. You will receive a confirmation email shortly.",
      noAvailableSlots: "No available slots in selected date range",
      rescheduleError: "Failed to reschedule appointment",
      goBack: "Go Back",

      planAppointment: "Plan Appointment",

      print: "Print",
      customerUpdatedSuccessfully: "Customer updated successfully",
      customerUpdateFailed: "Failed to update customer",
      unsavedChanges: "You have unsaved changes",
      changesSaved: "Changes saved",

      customerDeletedSuccessfully: "Customer deleted successfully",
      customerDeleteFailed: "Failed to delete customer",
      cannotDeleteCustomer: "Cannot Delete Customer",
      customerHasFutureAppointments: "Customer has future appointments. Please cancel or reschedule future appointments before deleting the customer.",
      cannotDeleteSomeCustomers: "Cannot Delete Some Customers",
      someCustomersHaveFutureAppointments: "Some customers have future appointments. Please cancel or reschedule future appointments before deleting the customers.",
      printWindowBlocked: "Print window was blocked by browser",
      editFeatureAvailableInDataTab: "Edit feature available in Data tab",

      // Review Request
      requestReview: "Request Review",
      requestReviewConfirmation: "Send a review request email to {{name}} at {{email}}?",
      sendReviewRequest: "Send Request",
      reviewRequestSent: "Review request sent successfully",
      reviewRequestFailed: "Failed to send review request",
      customerHasNoEmail: "Customer has no email address",
      lastReviewRequestSent: "Last review request sent on {{date}}",
      googleSetupRequired: "Google Setup required",
      reviewAlreadyRequested: "Review already requested",
      errorLoadingAppointments: "Error loading appointments",
      noUpcomingAppointments: "No upcoming appointments",
      scheduleFirstAppointment: "Schedule first appointment",

      // CSV Import/Export
      exportScope: "Export Scope",
      exportAllCustomers: "All customers",
      exportSelectedCustomers: "Selected customers only",
      includeFields: "Include Fields",
      createdDate: "Created Date",

      noCustomersToExport: "No customers to export",
      exportSuccess: "Export Successful",

      exportError: "Export Failed",
      exportErrorDescription: "Failed to export customers. Please try again.",
      importCustomers: "Import Customers",
      importServices: "Import Services",
      importKeywords: "Import Keywords",
      csvServiceImportInstructions:
        "Make sure your CSV includes title and duration columns. Description, category, price and other fields are optional.",
      csvKeywordImportInstructions:
        "Make sure your CSV includes phrase and searchType columns. Search type must be 'organic' or 'local_finder'.",
      importingServices: "Importing Services",
      importingKeywords: "Importing Keywords",
      importXServices: "Import {{count}} Service(s)",
      importXKeywords: "Import {{count}} Keyword(s)",
      selectCSVFile: "Select CSV File",
      dragDropOrClick: "Drag and drop a CSV file here, or click to select",
      csvImportInstructions:
        "Make sure your CSV includes firstName and lastName columns. Email, phone, and other fields are optional.",
      downloadTemplate: "Download Template",
      importPreview: "Import Preview",
      validRecords: "valid record(s)",
      errors: "Errors",
      empty: "empty",
      importErrorsFound:
        "Some records have errors and will be skipped during import.",
      row: "Row",

      selectDifferentFile: "Select Different File",

      importingCustomers: "Importing Customers",
      importingCustomerXOfY: "Importing customer {{current}} of {{total}}: {{name}}",
      importProgress: "Progress",
      importComplete: "Import Complete",
      importCompleteDescription:
        "Your customers have been imported successfully.",
      importSuccessWithDuplicates: "{{successful}} customers imported successfully. {{duplicates}} duplicates were skipped.",
      importSuccessful: "{{count}} customers imported successfully",
      importDuplicatesSkipped: "{{count}} duplicate customers skipped",
      importFailed: "{{count}} customers failed to import",
      done: "Done",
      invalidFileType: "Invalid File Type",
      pleaseSelectCSVFile: "Please select a CSV file.",
      fileParsingError: "File Parsing Error",
      fileParsingErrorDescription:
        "Unable to parse the CSV file. Please check the format.",
      importSuccess: "Import Successful",
      enterValidValue: "Enter valid value",
      applyEdits: "Apply Edits",
      editsApplied: "Edits Applied",
      dataRevalidated: "Data has been re-validated with your changes",

      importError: "Import Failed",
      importErrorDescription: "Failed to import customers. Please try again.",

      // CSV Validation Messages
      csvValidation: {
        invalidEmailFormat: "Invalid email format",
        invalidPhoneFormat: "Invalid phone format",
        invalidDateFormat: "Invalid date format",
        invalidDate: "Invalid date",
        invalidGender: "Invalid gender value. Use: m/f/d or male/female/diverse",
        fieldRequired: "This field is required",
        invalidDuration: "Duration must be a positive number",
        invalidPrice: "Price must be a positive number",
        invalidBuffer: "Buffer time must be a positive number",
        invalidSearchType: "Search type must be 'organic' or 'local_finder'"
      },

      // CSV Template
      csvTemplate: {
        filename: "customer_import_template",
        yes: "true",
        no: "false",
        prefersAfternoon: "Prefers afternoon appointments",
        allergicNote: "Allergic to certain products",
        serviceFilename: "appointment_type_import_template",
        keywordFilename: "keyword_import_template",
        serviceExamples: {
          massage: "Relaxing Massage",
          massageDesc: "60-minute full body relaxation massage",
          wellness: "Wellness",
          facial: "Facial Treatment",
          facialDesc: "Deep cleansing facial with moisturizing",
          skincare: "Skincare"
        }
      },

      // Resources
      manageRoomsEquipment:
        "Manage rooms, equipment, and other bookable resources",
      addResource: "Add Resource",
      editResource: "Edit Resource",
      createNewResource: "Create New Resource",
      resourceName: "Resource Name",
      resourceNamePlaceholder: "e.g. Massage Room A, Laser Machine",

      briefDescription: "Brief description",
      resourceUpdated: "Resource Updated",
      resourceUpdatedSuccess: "Resource has been updated successfully.",
      deleteResource: "Delete Resource",
      resourceDeleted: "Resource Deleted",
      resourceDeletedSuccess: "Resource has been deleted successfully.",
      deleteResourceConfirmation: "Are you sure you want to delete this resource? This action cannot be undone.",

      // Resource Details and Services Tabs
      resourceDetails: "Resource Details",
      assignedServices: "Assigned Services",

      // Improved Capacity Description
      capacityExplanation:
        "How many appointments can happen simultaneously (e.g., Group treatment room: 5, Individual room: 1)",

      // Legacy translations (keeping for compatibility)

      selectServicesForResource: "Services that require this resource:",
      chooseWhichServicesCanUseThisResource:
        "Services that require this resource",
      selectServicesForTemplate: "Select services for this template",
      capacityLabel: "Capacity:",
      availableFor: "Required for:",
      noServicesAssigned: "No services assigned",
      servicesUpdated: "Services Updated",
      serviceAssignmentsUpdated:
        "Service assignments have been updated successfully.",
      failedToUpdateServiceAssignment: "Failed to update service assignment.",

      // Common terms
      resource: "Resource",

      // KPI Tiles
      websiteVisits: "Website Visits",
      googleBusinessViews: "Google Business Views",
      weeklyCustomers: "Weekly Customers",
      weeklyLeads: "Weekly Leads",
      weeklyRevenue: "Weekly Revenue",
      totalLeads: "Total Leads",
      weeklyWebsiteVisits: "Weekly Website Visitors",
      leadsDescription: "Leads have entered their data in the booking form but haven't booked an appointment yet. We recommend contacting leads.",
      vsLastWeek: "vs. last week",

      // KPI Tooltips
      websiteVisitsTooltip: "Number of people who visited your website. More visits usually mean more potential customers. Try to improve your website's search ranking or social media presence to get more visits.",
      googleBusinessViewsTooltip: "How many times people looked at your Google Business profile. This helps people find your business when they search locally. Keep your Google Business profile updated with photos and information.",
      totalCustomersTooltip: "Total number of customers who have booked appointments with you. These are people who have actually scheduled services. Focus on providing great service to keep them coming back.",
      weeklyCustomersTooltip: "New customers from the last 7 days — contacts added in the last 7 days who have booked at least one appointment.",
      totalLeadsTooltip: "People who showed interest but haven't booked yet. They might have filled out a contact form or requested information. Contact these leads quickly to turn them into customers.",
      weeklyLeadsTooltip: "New leads from the last 7 days — people who showed interest but haven't booked an appointment yet. Tip: reach out to turn them into customers. Click the tile to open the list.",
      weeklyRevenueTooltip: "Money earned from appointments this week. This shows how your business is performing financially. Try to increase this by booking more appointments or offering higher-value services.",
      weeklyWebsiteVisitsTooltip: "Number of unique website visitors this week. Track your online presence and marketing effectiveness.",
      weeklyLeadsMessaged: "Leads Messaged by Sylvia (7 days)",
      weeklyLeadsMessagedTooltip: "Number of leads that Sylvia sent the welcome message to in the last 7 days.",
      weeklyConversationsEngaged: "Replies Received (7 days)",
      weeklyConversationsEngagedTooltip: "Number of leads that replied after Sylvia sent them the welcome message. This shows how effective your outreach is at starting conversations.",

      // Acquisition Chart
      acquisitionChart: "Contacts Acquired",
      acquisitionChartDescription: "New contacts over time, split by whether they have booked",
      booked: "Customers",
      notBooked: "Leads",
      noAcquisitionData: "No acquisition data yet",
      noAcquisitionDataDescription: "Data will appear here once you start acquiring contacts",
      daily: "Daily",
      weekly: "Weekly",
      timeRange: "Time Range",
      last7Days: "7 Days",
      last30Days: "30 Days",
      last90Days: "90 Days",
      last12Months: "12 Months",
      allTime: "All",

      // Calendar
      searchAppointments: "Search appointments...",
      filter: "Filter",
      filters: "Filters",
      day: "Day",
      blockTime: "Block Time",
      blockTimeDescription: "Block a time period to prevent new appointments from being scheduled",
      editBlockedTime: "Edit Blocked Time",
      editBlockedTimeDescription: "Modify the details of this blocked time period",
      blockedPeriodCreatedSuccessfully: "Blocked period created successfully",
      blockedPeriodUpdatedSuccessfully: "Blocked period updated successfully",
      blockedPeriodDeletedSuccessfully: "Blocked period deleted successfully",
      blockedPeriodMoved: "Blocked period moved successfully",
      failedToMoveBlockedPeriod: "Failed to move blocked period",
      confirmDeleteBlockedPeriod:
        "Are you sure you want to delete this blocked period? This action cannot be undone.",
      deleteBlockedPeriodTitle: "Delete Blocked Period",
      appointmentConflictWarning: "Appointment Conflict Warning",
      blockTimeConflictDescription: "There are existing appointments that conflict with the time period you are trying to block.",
      conflictWarningMessage: "{{count}} appointment(s) found on {{date}} that overlap with your blocked time:",
      conflictActionRequired: "Please ensure you notify affected customers or arrange for another team member to handle these appointments.",
      proceedAnyway: "Proceed Anyway",
      errorCheckingConflicts: "Error checking for conflicts. Please try again.",
      addAppointment: "Add Appointment",
      editAppointment: "Edit Appointment",
      noTeamMembers: "No team members available",

      // Time Blocking
      blockTitle: "Title",
      blockTitleRequired: "Title is required",
      dateRequired: "Date is required",
      timeRequired: "Time is required",
      endTimeAfterStartTime: "End time must be after start time",
      blockTitlePlaceholder: "e.g., Lunch Break, Team Meeting",
      blockDescriptionPlaceholder:
        "Additional details about this blocked time...",
      periodType: "Period Type",
      selectPeriodType: "Select period type",
      break: "Break",
      holiday: "Holiday",
      unavailable: "Unavailable",
      maintenance: "Maintenance",
      partOfMultiTeamMemberVisit: "Part of multi-team-member visit",
      cannotDragMultiTeamMemberVisit: "This visit has multiple team members and cannot be dragged. Please edit the visit to reschedule.",
      startTime: "Start Time",
      endTime: "End Time",
      appliesTo: "Applies To",
      selectAppliesTo: "Select who this applies to",
      allTeamMembersAndResources: "All team members and resources",
      allTeamMembers: "All team members",
      specificTeamMembers: "Specific team members",
      specificResources: "Specific resources",
      selectTeamMembers: "Select Team Members",
      selectResources: "Select Resources",
      selectAllMembers: "Select All",
      selectAtLeastOneTeamMember: "Please select at least one team member",

      // Calendar - Months

      // Calendar - Day abbreviations

      // Drag and Drop
      appointmentUpdated: "Appointment updated successfully",
      appointmentCreated: "Appointment created successfully",
      appointmentConflict:
        "Appointment conflict: Selected time slot is already occupied",
      failedToUpdateAppointment: "Failed to update appointment",
      appointmentDeleted: "Appointment deleted successfully",
      failedToDeleteAppointment: "Failed to delete appointment",
      confirmDeleteAppointment:
        "Are you sure you want to delete this appointment? The customer will be notified and this action cannot be undone.",
      deleteAppointmentTitle: "Delete Appointment",
      confirmCancelVisit:
        "Are you sure you want to cancel this visit? All appointments in this visit will be cancelled and the customer will be notified.",
      cancelNotificationEnabled: "The customer will be notified of the cancellation.",
      cancelNotificationDisabled: "The customer will not be notified.",
      cancelVisitTitle: "Cancel Visit",
      visitUpdated: "Visit updated successfully",
      failedToUpdateVisit: "Failed to update visit",
      visitCancelled: "Visit cancelled successfully",
      failedToCancelVisit: "Failed to cancel visit",

      // Conflict Detection
      teamMemberConflict: "Team member is already scheduled at this time",
      teamMemberScheduledConflict: "{{teamMemberName}} is already scheduled for another appointment at this time",
      resourceCapacityConflict: "Resource is at full capacity",
      resourceCapacityConflictDetailed: "{{resourceName}} is at full capacity ({{capacity}} appointments)",

      conflictValidationError:
        "Unable to validate appointment conflicts. Please try again.",
      resourceNotFound: "Resource not found",

      // Appointment Reschedule Confirmation
      confirmReschedule: "Confirm Reschedule",
      confirmRescheduleMessage:
        "Are you sure you want to reschedule this appointment? The customer will be notified of the time change.",
      rescheduleNotificationEnabled: "The customer will be notified of the time change.",
      rescheduleNotificationDisabled: "The customer will not be notified.",
      createNotificationEnabled: "The customer will be notified about the new appointment.",
      createNotificationDisabled: "The customer will not be notified.",
      notificationEnabled: "Notification enabled",
      notificationDisabled: "No notification",
      currentTime: "Current Time",
      newTime: "New Time",
      appointmentTime: "Appointment Time",
      schedulingConflicts: "Scheduling Conflicts",
      schedulingWarnings: "Scheduling Warnings",
      teamMemberServiceWarning: "{{teamMemberName}} does not offer the following services: {{serviceNames}}",
      cannotReschedule: "Cannot Reschedule",
      confirmAppointmentCreation: "Confirm Appointment Creation",
      cannotCreate: "Cannot Create",
      confirmCreate: "Confirm & Create",
      confirmAppointmentUpdate: "Confirm Appointment Update",
      cannotUpdate: "Cannot Update",
      confirmUpdate: "Confirm & Update",
      updateNotificationEnabled: "The customer will be notified about the appointment changes.",
      updateNotificationDisabled: "The customer will not be notified.",
      serviceChanges: "Service Changes",
      teamMemberChanges: "Team Member Changes",
      added: "Added",
      removed: "Removed",
      noChangesDetected: "No changes detected",

      // Appointment Modal
      selectCustomer: "Select a customer",
      selectService: "Select a service",
      selectTeamMember: "Select team member",
      selectTeamMemberImperativ: "Select a team member",
      pleaseSelectTeamMember: "Please select a team member",
      noTeamMembersAvailable: "No team members available",
      noTeamMembersForService: "No team members can provide this service",
      noTeamMembersAvailableForService:
        "No team members are available for this service",
      selectResource: "Select resource",

      appointmentNotesPlaceholder: "Additional notes for this appointment...",

      // Time units

      beforeAppointment: "before appointment",

      // Email Template Creation Form
      createNewTemplate: "Create New Template",
      editTemplate: "Edit Template",
      templateNameLabel: "Template Name",
      templateNamePlaceholder: "e.g. Appointment Confirmation Email",
      messageTypeLabel: "Message Type",
      emailOption: "Email",
      triggerEventLabel: "Trigger Event",
      emailSubjectLabel: "Email Subject",
      emailSubjectPlaceholder: "Subject line for the email",
      messageContentLabel: "Message Content",
      messageContentPlaceholder: "Write your message here...",
      availablePlaceholdersLabel: "Available Placeholders",
      creatingTemplate: "Creating...",
      updatingTemplate: "Updating...",

      // Template Assignment Form

      // Template Assignment Tabs

      // Template Assignment Validation

      // Template Assignment Step

      saveAndAssign: "Save & Assign",
      createAndAssign: "Create & Assign",

      // Email Template Validation
      templateNameRequired: "Template name is required",
      emailSubjectRequired: "Email subject is required",
      templateMustHaveContent: "Template must have either text or HTML content",

      templateCopySuffix: " (Copy)",
      copyOf: "Copy of",

      // Template Content Labels
      subjectLabel: "Subject:",
      messageLabel: "Message:",

      // Placeholder Translations
      placeholderCustomerName: "Customer Name",
      placeholderCustomerEmail: "Customer Email",
      placeholderCustomerPhone: "Customer Phone",
      placeholderAppointmentDate: "Appointment Date",
      placeholderAppointmentTime: "Appointment Time",
      placeholderService: "Appointment Type",
      placeholderTeamMemberName: "Team Member Name",
      placeholderBusinessName: "Business Name",
      placeholderBusinessAddress: "Business Address",
      placeholderBusinessPhone: "Business Phone",
      placeholderBusinessEmail: "Business Email",

      // Notification Preferences

      settingsUpdated: "Settings Updated",

      notificationTypeUpdated: "Notification setting updated",

      failedToUpdateSettings: "Failed to update settings. Please try again.",
      loadingSettings: "Loading settings",
      noPreferencesFound: "No preferences found",
      preferencesWillBeCreated:
        "Notification preferences will be created automatically",
      savingSettings: "Saving settings",
      manageNotificationSystem:
        "Manage your notification preferences and email templates",
      adminNotificationSettings:
        "Manage when you receive notifications about business events",
      preferences: "Preferences",

      // Admin Notification Settings
      emailNotifications: "Email Notifications",
      receiveEmailNotifications: "Receive email notifications",
      enableDisableEmailNotifications:
        "Enable or disable email notifications for business events",
      adminNotificationTypes: "Notification Types",
      neuerTermin: "Neuer Termin",
      neuerTerminDescription: "Notify when new appointments are booked",
      terminVerschoben: "Termin verschoben",
      terminVerschobenDescription: "Notify when appointments are rescheduled",
      terminAbgesagt: "Termin abgesagt",
      terminAbgesagtDescription: "Notify when appointments are cancelled",
      notificationScope: "Notification Scope",
      whichAppointments: "Which appointments do you want to be notified about?",
      allAppointmentsDescription:
        "Get notified about all appointments in your business",
      assignedAppointmentsOnly: "Assigned appointments only",
      assignedAppointmentsOnlyDescription:
        "Only get notified about appointments assigned to you",

      emailAddressOverride: "Email address override",
      emailAddressOverrideDescription:
        "Leave empty to use your account email address",
      useAccountEmail: "Use account email",
      notificationScopeUpdated: "Notification scope updated",
      emailNotificationsEnabled: "Email notifications enabled",
      emailNotificationsDisabled: "Email notifications disabled",

      // Public Booking Page
      loadingBookingOptions: "Loading booking options...",
      bookingNotAvailable: "Booking Not Available",
      pleaseCheckUrl: "Please check the URL or contact the business directly.",
      selectYourService: "Select a service",
      selectPreferredDateTime: "Select your preferred date and time",
      selectDateTime: "Select Date & Time",
      selectDateTimeImperativ: "Select a date and time",
      yourDetails: "Your Details",
      haveYouVisitedUsBefore: "Have you visited us before?",
      itWillBeMyFirstTime: "It will be my first time",
      ihaveVisitedBefore: "I have visited before",
      continue: "Continue",
      failedToSaveCustomerData: "Failed to save customer data. Please try again.",
      chooseYourService: "Choose Your Service",
      chooseTheServiceYouWant: "Choose the service you want to book",
      chooseServiceYouWantToBook: "Choose the service you want to book",
      chooseYourPreferredTeamMember: "Choose your preferred team member",
      chooseYourPreferredDateTime: "Choose your preferred date and time",
      enterYourContactDetails: "Enter your contact details",
      almostDone: "Almost done! Please provide your contact details",

      loadingAvailableTimes: "Loading available times...",
      noAvailableTimesDate: "No available times for this date.",
      pleaseSelectDifferentDate: "Please select a different date.",
      completeYourBooking: "Complete Your Booking",
      justFewDetailsAllSet: "Just a few details and you're all set!",
      backToDateSelection: "Back to date selection",
      bookingSummary: "Booking Summary",
      bookingConfirmed: "Booking Confirmed",

      appointmentScheduledSuccessfully:
        "Your appointment has been successfully scheduled!",
      appointmentDetails: "Appointment Details",
      contactInformation: "Contact Information",
      whatsNext: "What's Next?",
      confirmationEmailSent:
        "A confirmation email has been sent to your email address",
      addToCalendarReminder:
        "Add this appointment to your calendar for reminders",
      contactForChanges:
        "Contact us if you need to make any changes to your appointment",

      with: "with",
      firstNameRequired: "First Name *",
      lastNameRequired: "Last Name *",
      emailRequired: "Email *",
      phoneNumberOptional: "Phone Number",
      additionalNotesOptional: "Additional Notes (Optional)",
      receiveAppointmentReminders:
        "I'd like to receive appointment reminders and occasional updates via e-mail or whatsapp",
      confirmBooking: "Confirm Booking",
      creatingBooking: "Creating Booking...",
      yourInformation: "Your Information",
      firstName: "First Name",
      lastName: "Last Name",
      enterYourFirstName: "Enter your first name",
      enterYourLastName: "Enter your last name",
      enterYourEmail: "Enter your email",
      enterYourPhone: "Enter your phone number",
      firstNameRequiredError: "First name is required",
      lastNameRequiredError: "Last name is required",
      emailRequiredError: "Email is required",
      phoneRequiredError: "Phone number is required",
      contactMethodRequired: "Please provide either email or phone number",
      contactPermissionText: "I agree to be contacted about special offers and updates",
      invalidEmail: "Invalid email address",
      invalidPhone: "Invalid phone number",
      publicService: "Public Service",
      anyAvailableTeamMember: "Any Available Team Member",
      firstAvailableTeamMember: "First available team member will be assigned",
      confirmYourBooking: "Confirm Your Booking",
      bookingService: "Service",
      bookingTeamMember: "Team Member",
      bookingDate: "Date",
      bookingTime: "Time",
      bookingDuration: "Duration",
      booking: "Booking",
      anyTeamMemberRequested: "Any available team member requested",
      appointmentBooked: "Appointment booked successfully",
      bookingError: "Error booking appointment. Please try again.",
      availableTimes: "Available times",
      bookingConfirmation: "Confirmation",
      verificationEmailSent: "Verification Email Sent",
      pleaseCheckYourEmailToConfirmSubscription: "A verification email has been sent to the customer to confirm their marketing preferences",
      emailVerified: "Email Verified",
      marketingConsentNotificationTitle: "Marketing Consent Email",
      marketingConsentNotificationMessage: "{{customerName}} will receive an email to confirm their {{consentType}} preferences.",
      and: "and",
      willReceiveVerificationEmail: "Will receive a verification email",
      emailMarketingConsent: "Email marketing communications",
      whatsappMarketingConsent: "WhatsApp marketing communications",
      consentVerificationExpires: "The verification link expires in 48 hours",
      marketingConsentGDPRNote: "This ensures compliance with GDPR regulations by obtaining explicit consent from the customer.",
      sendVerificationEmail: "Send Verification Email",
      bookingConfirmedTitle: "Booking Confirmed!",
      bookingSuccessMessage: "Thank you {{name}}, your appointment has been successfully booked.",
      yourAppointmentDetails: "Your Appointment Details",
      confirmationEmailSentMessage: "A confirmation email has been sent to your email address with all the appointment details.",
      bookAnotherAppointment: "Book Another Appointment",
      backToHome: "Back to Home",
      backToWebsite: "Back to Website",
      verificationPending: "Verification Pending",
      resendVerification: "Resend Verification",
      emailNotVerified: "not verified",
      failedToSendVerificationEmail: "Failed to send verification email",

      // Marketing Consent Verification Page
      marketingConsent: {
        verifying: "Verifying your consent...",
        verificationSuccess: "Verification Successful!",
        subscriptionConfirmed: "Your interest in occasional updates has been confirmed.",
        youWillReceive: "You will receive",
        appointmentReminders: "Appointment reminders and confirmations",
        specialOffers: "Special offers and promotions",
        newsUpdates: "News and updates about our services",
        alreadyVerified: "Already Verified",
        subscriptionAlreadyConfirmed: "Your interest has already been confirmed.",
        verificationFailed: "Verification Failed",
        invalidOrExpiredLink: "The verification link is invalid or has expired.",
        contactSupport: "Please contact support if you need assistance.",
        canCloseWindow: "You can close this window now."
      },


      // Notification Status Values
      pending: "Pending",

      // Customer Appointment Management
      manageAppointment: "Manage Appointment",
      invalidLink: "Invalid link",
      invalidOrExpiredAppointmentLink: "Invalid or expired link",
      failedToLoadAppointment: "Failed to load appointment",
      appointmentCancelledSuccessfully: "Appointment cancelled successfully",
      failedToCancelAppointment: "Failed to cancel appointment",
      pleaseContactUsToReschedule: "Please contact us to reschedule your appointment",
      appointmentManagementInfo: "You can reschedule or cancel your appointment using the buttons below",
      cancelAppointment: "Cancel Appointment",
      appointmentHasBeenCancelled: "This appointment has been cancelled",
      upcoming: "Upcoming",
      failedToScheduleReminders: "Failed to schedule reminders",
      failedToCleanupNotifications: "Failed to cleanup old notifications",
      failedToUpdateNotificationPreferences:
        "Failed to update notification preferences",
      failedToBulkUpdatePreferences: "Failed to bulk update preferences",

      // Customer Notification Preferences
      notificationPreferencesUpdated: "Notification Preferences Updated",
      customerNotificationPreferencesUpdatedSuccessfully:
        "Customer notification preferences updated successfully",
      bulkUpdateComplete: "Bulk Update Complete",

      // Queue Statistics

      pendingNotifications: "Pending",

      // Email Service

      emailsSent: "Emails Sent",

      // Template Management

      // Notification Engine Settings
      notificationEngineSettings: "Notification Engine Settings",

      // Advanced Settings

      // Multi-language Support

      // Real-time Updates

      // Categories
      searchOrCreateCategory: "Search or create category",
      createCategory: "Create category",
      createNewCategory: "Create new category",
      categoryName: "Category name",
      categoryDescription: "Category description",
      categoryNameRequired: "Category name is required",
      categoryRequired: "Category is required",
      categoryCreated: "Category created successfully",
      categoryCreationFailed: "Failed to create category",
      manageCategories: "Manage categories",
      manageCategoriesDescription:
        "Rename categories or remove ones you no longer need.",
      noCategoriesYet: "No categories yet.",
      oneService: "1 service",
      nServices: "{count} services",
      rename: "Rename",
      categoryNameTaken: "A category with this name already exists",
      categoryUpdated: "Category renamed",
      categoryUpdateFailed: "Failed to rename category",
      categoryDeleted: "Category deleted",
      categoryDeletionFailed: "Failed to delete category",
      categoryInUseCannotDelete:
        "Still in use - move its services to another category first",
      categoryDeleteHint:
        "Only categories without services can be deleted.",
      deleteCategory: "Delete category",
      deleteCategoryConfirm:
        "Delete the category \"{name}\"? This cannot be undone.",

      // Additional missing translation keys for hardcoded strings
      bookAnAppointment: "Book an Appointment",
      scheduleYourNextServiceAppointment:
        "Schedule your next service appointment",
      bookYourAppointment: "Book Your Appointment",
      dateTime: "Date & Time",
      details: "Details",
      continueToDateTime: "Continue to Date & Time",

      staffMemberRequired: "Staff Member *",
      selectStaffMember: "Select staff member",
      anyAvailableStaff: "Any available staff",
      selectDate: "Select Date",
      selectTime: "Select time",
      continueToDetails: "Continue to Details",

      specialRequestsOrNotes: "Special Requests or Notes",
      bookingSummaryTitle: "Booking Summary",
      serviceCost: "Service",
      durationLabel: "Duration",
      priceLabel: "Price",
      dateLabel: "Date",
      timeLabel: "Time",
      staffLabel: "Staff",
      confirmBookingButton: "Confirm Booking",
      appointmentBookedTitle: "Appointment Booked!",

      // Missing service-related keys
      bookingLinkCopied: "Booking link copied to clipboard",
      servicesDeletedSuccessfully: "{{count}} services deleted successfully",
      servicesDuplicatedSuccessfully:
        "{{count}} services duplicated successfully",
      bulkDeleteServicesConfirmation:
        "Are you sure you want to delete {{count}} services? This action cannot be undone.",
      bulkDuplicateServicesConfirmation:
        "Are you sure you want to duplicate {{count}} services?",
      schedulingLinkCopied: "Scheduling link copied to clipboard",
      bufferTime: "Buffer Time",
      deleteServiceConfirmation:
        "Are you sure you want to delete this service? This action cannot be undone.",

      // Appointment and booking related
      appointmentConfirmedFor: "Appointment confirmed for {{name}}",
      fieldIsRequired: "This field is required",
      exportSuccessDescription: "Your data has been exported successfully",
      exportSummary: "Export Summary",
      importSuccessDescription: "Your data has been imported successfully",
      andXMore: "and {{count}} more",
      importXCustomers: "Import {{count}} customers",
      availableTimesTitle: "Available Times",
      slotsAvailable: "{{count}} slot",
      slotsAvailable_one: "{{count}} slot",
      slotsAvailable_other: "{{count}} slots",
      availabilityForTeamMember: "Availability for {{name}}",

      // Notification related
      updatedPreferencesForCustomers:
        "Updated preferences for {{count}} customers",
      invalidPlaceholders: "Invalid placeholders found",
      retriedNotifications: "Retried {{count}} failed notifications",
      processedEmails: "Processed {{count}} emails",
      scheduledReminders: "Scheduled {{count}} reminders",
      cleanedUpNotifications: "Cleaned up {{count}} old notifications",
      serviceResourceCapacity: "Appointment type resource capacity",
      serviceResourceCapacityDetailed: "{{resourceName}} is required for this service but is at full capacity ({{capacity}} appointments)",

      // Auth
      createAccount: "Create Account",
      noTenantSelected: "No Tenant selected",

      // Error Messages
      error: "Error",
      more: "More",
      failedToLoadAppointments: "Failed to load appointments",
      fileSizeMustBeLessThan2mb: "File size must be less than 2MB",
      errorUnknownError: "Unknown error",
      errorPleaseFixTheErrorsBeforeSubmitting: "Please fix the errors before submitting.",
      errorFailedToCreateAppointment: "Failed to create appointment",
      errorFailedToUpdateAppointment: "Failed to update appointment",
      errorFailedToRescheduleAppointment: "Failed to reschedule appointment",
      missingTenantIdOrCustomerId: "Missing tenant ID or customer ID",
      errorFailedToFetchResources: "Failed to fetch resources",
      errorAnErrorOccurred: "An error occurred",
      errorFailedToLoadCalendar: "Failed to load calendar",

      // Time formats
      timeFormatHourMinute: "h:mm a",
      timeFormatFull: "h:mm a",
      dateFormat: "MM/dd/yyyy",
      dateFormatShort: "EEE, MMM d",
      dateFormatLong: "EEEE, MMMM d, yyyy",
      dateFormatMonthYear: "MMMM yyyy",
      dateFormatWeekdayShort: "EEE",
      dateFormatWeekdayLong: "EEEE",
      dateFormatMonthYearShort: "MMM yyyy",
      dateFormatDayNumeric: "d",

      // Notifications
      failedToClearNotificationQueue: "Failed to clear notification queue",
      clearAllNotificationsConfirm:
        "Are you sure you want to clear ALL notifications from the queue? This action cannot be undone.",

      // New translation keys for hardcoded strings
      chooseAService: "Choose a service",
      nextDateTime: "Next: Date & Time",
      nextYourDetails: "Next: Your Details",
      anySpecialRequestsOrInformation: "Any special requests or information...",
      calendarNotFoundMessage:
        "We couldn't find the calendar you're looking for. Please check the URL or contact the business directly.",
      bookYourAppointmentWithEase:
        "Book your appointment with ease. Choose your preferred date and time.",
      bookAppointmentNow: "Book Appointment Now",
      somethingWentWrongPleaseTryAgain:
        "Something went wrong. Please try again.",
      addYourFirstResource: "Add Your First Resource",
      noResourcesYet: "No Resources Yet",
      addRoomsEquipmentOrOtherBookableResources: "Add rooms, equipment, or other bookable resources",
      returnToHome: "Return to Home",
      noTenantSelectedPleaseSelectATenantFirst:
        "No tenant selected. Please select a tenant first.",
      formSubmittedSuccessfully: "Your form has been submitted successfully.",
      submissionError: "Submission Error",
      failedToSubmitFormPleaseTryAgain:
        "Failed to submit form. Please try again.",
      submitForm: "Submit Form",
      submitting: "Submitting...",
      goToPreviousPage: "Go to previous page",
      goToNextPage: "Go to next page",
      errorAnalysis: "Error Analysis",
      smtpConnectionFailed: "SMTP connection failed",
      authenticationFailed: "Authentication failed",
      retryFailed: "Retry Failed",
      thisWeek: "This Week",
      tomorrow: "Tomorrow",
      dateOfTheAppointment: "Date of the appointment",
      timeOfTheAppointment: "Time of the appointment",
      appointmentDurationInMinutes: "Appointment duration in minutes",
      emailSubjectIsRequired: "Email subject is required",
      emailSendingFailed: "Email sending failed",
      emailDeliveryFailed: "Email delivery failed",
      failedToFetchResources: "Failed to fetch resources",
      failedToLoadCalendar: "Failed to load calendar",
      unknownError: "Unknown error",
      anErrorOccurred: "An error occurred",
      pleaseFixTheErrorsBeforeSubmitting:
        "Please fix the errors before submitting.",
      resourceIsAlreadyBookedForThisTimeSlot:
        "Resource is already booked for this time slot",
      errorCheckingResourceAvailability: "Error checking resource availability",
      emailsWouldAppearInProduction:
        "Emails that would be sent in production will appear here",

      // Common
      profile: "Profile",
      viewAll: "View All",
      analyzed: "analyzed",
      discovered: "Discovered",
      website: "Website",
      primary: "Primary",
      optional: "optional",
      url: "URL",
      trend: "Trend",
      rankingType: "Ranking Type",
      export: "Export",
      exporting: "Exporting...",

      // Common form fields
      status: "Status",
      remove: "Remove",
      addService: "Add another service",
      assignDifferentTeamMembers: "Assign different team members per appointment",
      teamMemberNotRelatedToService: "This team member is not assigned to all selected services",
      serviceName: "Service Name",
      category: "Category",
      selectCategory: "Select Category",
      title: "Title",
      active: "Active",
      inactive: "Inactive",
      previousAnalysisRuns: "Previous Analysis Runs",
      imported: "Imported",
      results: "Results",
      yourRankingsForTopKeywords: "Your rankings for top keywords",
      location: "Location",
      archive: "Archive",
    },
  },
  de: {
    translation: {
      // Locale
      locale: "de-DE",
      // Billing
      billing: {
        title: "Abrechnung",
        sidebarDescription: "Rechnungen, Zahlung & Abo",
        description: "Verwalte deine Rechnungen, dein Zahlungsmittel und dein Abo sicher über das Stripe-Kundenportal.",
        featureInvoices: "Rechnungen ansehen & herunterladen",
        featurePayment: "Zahlungsmittel ändern",
        featureCancel: "Abo verwalten",
        manageButton: "Rechnungen & Zahlung verwalten",
        openError: "Das Kundenportal konnte nicht geöffnet werden. Bitte versuche es erneut.",
        noAccountTitle: "Noch keine Abrechnung hinterlegt",
        noAccountBody: "Für dieses Studio ist noch kein Zahlungskonto verknüpft. Melde dich bei uns, wenn du Fragen zur Abrechnung hast.",
      },
      // Referral program
      referral: {
        nav: "Weiterempfehlen",
        title: "Sylva weiterempfehlen",
        subtitle: "Empfiehl Sylva an ein anderes Studio – ihr bekommt beide einen Gratis-Monat.",
        howItWorksTitle: "So funktioniert's",
        step1: "Teile deinen persönlichen Link oder Code mit einem anderen Studio.",
        step2: "Das Studio registriert sich über deinen Link.",
        step3: "Sobald es zahlender Kunde wird, bekommt ihr beide 1 Monat gratis.",
        yourLinkTitle: "Dein Empfehlungslink",
        yourCodeLabel: "Dein Code",
        copyLink: "Link kopieren",
        copyCode: "Code kopieren",
        shareWhatsApp: "Per WhatsApp teilen",
        shareEmail: "Per E-Mail teilen",
        linkCopied: "Link kopiert",
        codeCopied: "Code kopiert",
        shareMessage: "Hey! Ich nutze Sylva als KI-Assistent für mein Studio und bin begeistert. Wenn du dich über meinen Link anmeldest, bekommen wir beide einen Monat gratis: {{link}}",
        emailSubject: "Eine Empfehlung für dein Studio: Sylva",
        emailCopied: "E-Mail-Text kopiert – füge ihn in deine E-Mail ein.",
        rewardsTitle: "Deine Empfehlungen",
        statInvited: "Eingeladen",
        statActive: "Aktiv",
        statMonthsEarned: "Monate verdient",
        emptyState: "Noch keine Empfehlungen. Teile deinen Link, um loszulegen!",
        rewardNote: "Der Gratis-Monat wird gutgeschrieben, sobald das geworbene Studio zahlender Kunde wird.",
        loadError: "Empfehlungsdaten konnten nicht geladen werden.",
        ahaTitle: "Starke Woche! 🎉",
        ahaBody: "Du hast diese Woche {{reason}} – Glückwunsch! Kennst du ein Studio, das auch durchstarten will? Empfehlt euch gegenseitig, dann bekommt ihr beide einen Gratis-Monat.",
        ahaReasonCustomers_one: "einen neuen Kunden gewonnen",
        ahaReasonCustomers_other: "{{count}} neue Kunden gewonnen",
        ahaReasonReviews_one: "eine neue Bewertung erhalten",
        ahaReasonReviews_other: "{{count}} neue Bewertungen erhalten",
        ahaAnd: "und",
        ahaCta: "Studio weiterempfehlen",
        ahaDismiss: "Ausblenden",
        signupStudioName: "Studio-Name",
        signupStudioNamePlaceholder: "z. B. Hair & Style Berlin",
        signupReferralCode: "Empfehlungscode (optional)",
        signupReferralPlaceholder: "z. B. ABC1234",
        signupReferralApplied: "Empfehlungscode übernommen – ihr bekommt beide einen Gratis-Monat.",
        signupAcceptTerms: "Ich akzeptiere die AGB.",
        signupStudioRequired: "Bitte gib deinen Studio-Namen ein.",
        signupTermsRequired: "Bitte akzeptiere die AGB, um fortzufahren.",
        signupError: "Registrierung fehlgeschlagen. Bitte versuche es erneut.",
        signupEmailExists: "Mit dieser E-Mail-Adresse gibt es bereits ein Konto. Bitte melde dich stattdessen an oder verwende eine andere E-Mail-Adresse.",
        signupSignInHint: "Dein Konto wurde erstellt – bitte melde dich an.",
      },
      // PWA
      pwa: {
        newContentAvailable: "Eine neue Version ist verfügbar. Klicken Sie auf Neu laden, um zu aktualisieren.",
        reload: "Neu laden",
        installTitle: "Sylva‑App installieren",
        installDescription: "Installieren Sie diese App auf Ihrem Gerät für eine bessere Nutzung und Offline‑Zugriff.",
        installCta: "App installieren"
      },

      // Header
      selectBusiness: "Geschäft auswählen",
      signOut: "Abmelden",
      // Ladezustaende beim Start der App (Layout). ACHTUNG: Uebersetzungen
      // stehen hier, nicht in src/locales/ -- das Verzeichnis haengt nicht an i18next.
      reloadPage: "Neu laden",
      stillLoadingTitle: "Das dauert länger als gewöhnlich",
      stillLoadingBody: "Die Verbindung zum Server ist gerade langsam. Wir versuchen es weiter — du kannst die Seite auch neu laden.",
      connectionProblemTitle: "Keine Verbindung zum Server",
      connectionProblemBody: "Deine Daten konnten nicht geladen werden. Das liegt an einer Störung, nicht an deinem Konto. Bitte versuche es erneut.",
      aiAssistant: "KI-Assistent",
      createNewBusiness: "Neues Geschäft erstellen",
      businessName: "Geschäftsname",
      urlSlug: "URL-Slug",
      cancel: "Abbrechen",
      create: "Erstellen",
      copy: "Kopieren",
      copied: "Kopiert",

      // Navigation
      dashboard: "Übersicht",
      statsOverview: "Überblick",
      sidebarControl: "Seitenleiste",
      sidebarExpanded: "Erweitert",
      sidebarCollapsed: "Eingeklappt",
      sidebarExpandOnHover: "Bei Hover erweitern",
      interestedSince: "Interessiert seit",
      interest: "Interesse",
      call: "Anrufen",
      message: "Nachricht",
      leadStatusHint: "Interessent – hat noch keinen Termin gebucht. Wird automatisch zum Kunden, sobald ein Termin gebucht wird.",
      customerStatusHint: "Kunde – hat mindestens einen Termin gebucht.",
      importedStatusHint: "Importiert – aus WhatsApp oder Import übernommen, noch kein Termin.",
      appointments: "Termine",
      analytics: "Auswertungen",
      keywords: "Keywords",
      keyword: "Keyword",
      addKeyword: "Keyword hinzufügen",
      editKeyword: "Keyword bearbeiten",
      deleteKeyword: "Keyword löschen",
      phrase: "Begriff",
      searchType: "Suchtyp",
      localFinder: "Local Finder",
      organic: "Organisch",
      keywordDeleted: "Keyword erfolgreich gelöscht",
      keywordCreated: "Keyword erfolgreich erstellt",
      keywordUpdated: "Keyword erfolgreich aktualisiert",
      deleteKeywordConfirm: "Möchten Sie dieses Keyword wirklich löschen?",
      enterKeywordPhrase: "Keyword-Begriff eingeben",
      noKeywordsYet: "Noch keine Keywords",
      manageRankingKeywords: "Ranking-Keywords für Ihr Unternehmen verwalten",
      analyticsDescription: "Verfolgen Sie Ihre Geschäftsleistung und Kundengewinnung",
      acquisition: "Kundengewinnung",
      acquisitionDescription: "Wie viele Interessenten und Kunden Sie pro Tag oder Woche gewonnen haben",
      keywordsOverviewDescription: "Visuelle Übersicht Ihrer Keyword-Rankings im Zeitverlauf",
      serankingDescription: "Keyword-Daten aus SEranking-Analysen",
      ahrefsDescription: "Keyword-Daten aus Ahrefs-Analysen",
      serviceAreasDescription: "Geografische Gebiete für lokale SEO-Ausrichtung",
      addFirstKeyword: "Fügen Sie Ihr erstes Ranking-Keyword hinzu, um loszulegen",
      editKeywordDetails: "Bearbeiten Sie die Keyword-Details unten.",
      addNewKeyword: "Fügen Sie ein neues Keyword hinzu, um Rankings zu verfolgen.",
      keywordsOverview: "Übersicht",
      organicPositions: "Organische Positionen",
      organicPositionsDescription: "Anzahl organischer Keywords nach Position im Zeitverlauf",
      localFinderPositions: "Local Finder Positionen",
      localFinderPositionsDescription: "Anzahl Local Finder Keywords nach Position im Zeitverlauf",
      noRankingDataYet: "Noch keine Daten erfasst",
      noRankingDataDescription: "Ranking-Daten werden hier angezeigt, sobald Ihre Keywords verfolgt werden",
      currentRanking: "Aktuelles Ranking",
      monthChange: "30T Änderung",
      searchVolume: "Suchvolumen",
      competition: "Wettbewerb",
      noChange: "Keine Änderung",
      noKeywordsFound: "Keine Keywords gefunden",
      collapse: "Einklappen",
      expand: "Ausklappen",

      // SEranking Keywords
      serankingAnalytics: "SEranking Analytics",
      volume: "Volumen",
      position: "Position",
      positionChange: "Änderung",
      traffic: "Traffic",
      trafficPercent: "Traffic %",
      cpc: "CPC",
      block: "Block",
      additionalInfo: "Info",
      trafficTooltip: "Geschätzter Traffic von diesem Keyword",
      trafficPercentTooltip: "Prozentsatz des Gesamt-Traffics von diesem Keyword",
      cpcTooltip: "Geschätzte Kosten-pro-Klick für bezahlte Werbung",
      blockTooltip: "SERP-Feature-Typ (organisch, Featured Snippet, etc.)",

      // Ahrefs Keywords
      ahrefsAnalytics: "Ahrefs Analytics",
      keywordDifficulty: "Schwierigkeit",
      paidTraffic: "Bezahlter Traffic",
      serpFeatures: "SERP Features",
      organicTrafficTooltip: "Geschätzter monatlicher organischer Traffic von diesem Keyword",
      paidTrafficTooltip: "Geschätzter monatlicher bezahlter Traffic von diesem Keyword",
      serpFeaturesTooltip: "Anzahl der SERP-Features für dieses Keyword",
      difficultyLowHigh: "Schwierigkeit: Niedrig bis Hoch",
      difficultyHighLow: "Schwierigkeit: Hoch bis Niedrig",

      // Sorting
      phraseAZ: "Begriff A-Z",
      phraseZA: "Begriff Z-A",
      searchTypeAsc: "Suchtyp (Aufsteigend)",
      searchTypeDesc: "Suchtyp (Absteigend)",
      rankingBestFirst: "Bestes Ranking zuerst",
      rankingWorstFirst: "Schlechtestes Ranking zuerst",
      improvementAsc: "Am wenigsten verbessert zuerst",
      improvementDesc: "Am meisten verbessert zuerst",
      volumeLowHigh: "Volumen: Niedrig bis Hoch",
      volumeHighLow: "Volumen: Hoch bis Niedrig",
      competitionLowHigh: "Wettbewerb: Niedrig bis Hoch",
      competitionHighLow: "Wettbewerb: Hoch bis Niedrig",

      // Pagination
      showingResults: "Zeige {{from}}-{{to}} von {{total}} Ergebnissen",

      // Service Areas
      serviceAreas: "Servicebereiche",
      addServiceArea: "Servicebereich hinzufügen",
      editServiceArea: "Servicebereich bearbeiten",
      deleteServiceArea: "Servicebereich löschen",
      geoTargetCode: "Geo-Zielcode",
      searchCity: "Stadt suchen...",
      searchCityDescription: "Suchen Sie nach einer Stadt, um ihren Geo-Zielcode von Google Ads zu erhalten",
      serviceAreaCreated: "Servicebereich erfolgreich erstellt",
      serviceAreaUpdated: "Servicebereich erfolgreich aktualisiert",
      serviceAreaDeleted: "Servicebereich erfolgreich gelöscht",
      noServiceAreasYet: "Noch keine Servicebereiche",
      addFirstServiceArea: "Fügen Sie Ihren ersten Servicebereich hinzu, um loszulegen",
      orEnterManually: "Oder manuell eingeben",
      cityName: "Stadtname",
      manualCityDescription: "Geben Sie einen Stadtnamen ohne Geo-Zielcode ein",
      noResults: "Keine Ergebnisse gefunden",
      notSet: "Nicht gesetzt",

      availability: "Verfügbarkeiten",
      availabilityManagement: "Verfügbarkeitsverwaltung",
      roomsResources: "Ressourcen",
      globalConstraints: "Globale Einschränkungen",
      organizationWideRules:
        "Organisationsweite Buchungsregeln und Einschränkungen",
      calendarProfiles: "Kalenderprofile",
      minAdvanceBooking: "Mindestvorlaufzeit für Buchungen",
      maxAdvanceBooking: "Maximaler Vorlauf für Buchungen",
      startTimeIntervals: "Startzeitintervalle",
      customerPermissions: "Kundenberechtigungen",
      customerCanCancel: "Kunden können Termine absagen",
      customerCanReschedule: "Kunden können Termine verschieben",
      customerCanEditForms: "Kunden können Formulare bearbeiten",
      timezoneSetting: "Zeitzoneneinstellung",
      businessHours: "Öffnungszeiten",
      generalInformation: "Allgemein",
      pricingAndDuration: "Preis & Dauer",
      appearance: "Darstellung",
      bookingRules: "Buchungsregeln",
      bookingPortalSettings: "Buchungsportal",
      bookingNoticeEnabled: "Hinweis auf Buchungsseite anzeigen",
      bookingNoticeEnabledHelp: "Wenn aktiviert, wird ein Hinweis auf der ersten Seite der Terminbuchung angezeigt.",
      bookingNoticeText: "Hinweistext",
      bookingNoticeTextHelp: "Trage hier wichtige Hinweise zur Terminbuchung für deine Kunden ein. Dieser Hinweis erscheint im Buchungsportal auf der ersten Seite.",
      bookingNoticeTextPlaceholder: "Geben Sie den Hinweistext ein, der Ihren Kunden angezeigt werden soll...",
      cancellationPolicyTitle: "Stornobedingungen",
      cancellationPolicyEnabled: "Gebühren-Hinweis bei kurzfristiger Absage anzeigen",
      cancellationPolicyEnabledHelp: "Wenn ein Kunde kurz vor dem Termin online absagt, weist ihn das Buchungsportal darauf hin, dass eine Ausfallgebühr anfallen kann. Nur ein Hinweis — Sylva zieht die Gebühr nicht ein; du kassierst sie selbst.",
      cancellationCutoffHours: "Kurzfristigkeitsgrenze",
      cancellationCutoffHoursHelp: "Absagen weniger als so viele Stunden vor dem Termin gelten als kurzfristig.",
      cancellationFeePercent: "Ausfallgebühr",
      cancellationFeePercentHelp: "Prozentsatz des Terminpreises, der als mögliche Gebühr angezeigt wird.",
      cancellationPolicyText: "Bedingungstext (optional)",
      cancellationPolicyTextPlaceholder: "z. B. Bei Absagen weniger als 24 Stunden vorher berechnen wir 50 % des Leistungspreises.",
      cancellationPolicyTextHelp: "Wird dem Kunden beim Stornieren und bei der Buchung angezeigt. Leer lassen für einen Standardtext.",
      hours: "Stunden",
      days: "Tage",
      minutes: "Minuten",
      every30Minutes: "Alle 30 Minuten",
      autoBasedOnDuration: "Automatisch basierend auf Dienstleistungs-Dauer",
      customInterval: "Benutzerdefiniertes Intervall",

      addTimeRange: "Zeitbereich hinzufügen",

      editDeadline: "Bearbeitungsfrist",
      customers: "Kunden",
      resources: "Ressourcen",
      calendar: "Kalender",
      conversations: "Unterhaltungen",

      error: "Fehler",
      more: "weitere",

      // Email Template Types
      confirmation: "Bestätigung",
      reminder: "Erinnerung",
      reschedule: "Verschiebung",
      cancellation: "Stornierung",
      followup: "Nachfassen",

      // Notification Types
      customerAppointmentConfirmed: "Kundentermin bestätigt",
      customerAppointmentReminder: "Kundentermin-Erinnerung",
      customerAppointmentRescheduled: "Kundentermin verschoben",
      customerAppointmentCancelled: "Kundentermin abgesagt",
      adminNewAppointment: "Admin neuer Termin",
      adminAppointmentRescheduled: "Admin Termin verschoben",
      adminAppointmentCancelled: "Admin Termin storniert",

      // Email Preview
      preview: "Vorschau",
      htmlCode: "HTML Code",
      desktop: "Desktop",
      mobile: "Mobil",
      emailSubject: "E-Mail Betreff",
      emailBody: "E-Mail Inhalt",
      textVersion: "Textversion",
      textVersionDescription:
        "Nur-Text-Version für E-Mail-Clients, die kein HTML unterstützen",
      htmlSource: "HTML Quellcode",
      sampleDataUsed: "Verwendete Beispieldaten",
      sampleDataUsedDescription:
        "Die folgenden Platzhalter-Werte wurden in dieser Vorschau verwendet",
      sendTest: "Test senden",
      installDefaultTemplates: "Standard-Vorlagen installieren",
      getStartedWithTemplates:
        "Beginnen Sie mit professionellen E-Mail-Vorlagen oder erstellen Sie Ihre eigenen benutzerdefinierten Vorlagen.",
      createCustomTemplate: "Benutzerdefinierte Vorlage erstellen",
      backToTemplates: "Zurück zu Vorlagen",
      templatePreview: "Vorlagen-Vorschau",
      today: "Heute",
      inDays: "in {{days}} Tagen",
      month: "Monat",
      week: "Woche",
      twoDays: "2 Tage",
      dayShort: "T",
      weekShort: "W",
      sun: "So",
      mon: "Mo",
      tue: "Di",
      wed: "Mi",
      thu: "Do",
      fri: "Fr",
      sat: "Sa",

      scheduleAndManageAppointments: "Termine planen und verwalten",
      newAppointment: "Neuer Termin",
      bookAppointment: "Termin buchen",
      legend: "Legende",
      templates: "Vorlagen",
      notifications: "Benachrichtigungen",
      settings: "Einstellungen",
      manageTenantSettings: "Verwalten Sie Ihre Geschäftseinstellungen",

      studioSettings: "Studio Einstellungen",
      studioSettingsDescription: "Konfigurieren Sie Studionamen, Branding und Kontaktinformationen",
      "whatsappSettings.title": "WhatsApp",
      "whatsappSettings.description": "WhatsApp-Agent und Willkommensnachrichten konfigurieren",
      "phoneSettings.title": "Telefon",
      "phoneSettings.description": "Sprachagent und Rufumleitung konfigurieren",
      studioInformation: "Studio Informationen",
      studioLogo: "Studio Logo",
      uploadLogo: "Logo hochladen",
      logoUploadSuccess: "Logo erfolgreich hochgeladen",
      logoUploadError: "Fehler beim Hochladen des Logos",
      removeLogo: "Logo entfernen",
      supportedFormats: "Unterstützte Formate: JPG, PNG (max 2MB)",
      dragDropLogoOrClick: "Bild hier ablegen oder klicken zum Auswählen",
      localization: "Lokalisierung",
      localizationDescription: "Sprache, Währung und Zeitzone festlegen",
      localizationPreferences: "Lokalisierungseinstellungen",
      language: "Sprache",
      selectLanguage: "Sprache auswählen",
      selectCurrency: "Währung auswählen",
      selectTimezone: "Zeitzone auswählen",
      timezone: "Zeitzone",
      saveSettings: "Einstellungen speichern",
      saving: "Speichern...",
      settingsUpdatedSuccessfully: "Einstellungen erfolgreich aktualisiert",
      settingsUpdateFailed: "Fehler beim Aktualisieren der Einstellungen",

      // Availability Sidebar
      selectAvailabilityCategory: "Wählen Sie eine Verfügbarkeitskategorie",
      manageRoomsAndResources: "Räume und Ressourcen für Buchungen verwalten",
      manageBusinessHours: "Öffnungszeiten für jeden Tag festlegen",
      manageBookingLinks: "Links für Online-Buchungen teilen",

      // Settings Sidebar
      selectSettingCategory: "Wählen Sie eine Einstellungskategorie",
      selectAnalyticsCategory: "Wählen Sie eine Analyse-Kategorie",

      // Auswertungen-Modul (Reiter)
      auswComingSoon: "Bald verfügbar",
      auswOverview: "Überblick",
      auswOverviewDescription: "Wie dein Salon gerade läuft – von der Sichtbarkeit bis zum Umsatz.",
      auswRevenue: "Umsatz",
      auswRevenueDescription: "Umsatzverlauf, nach Dienstleistung und nach Mitarbeiter.",
      auswCustomers: "Kunden",
      auswCustomersDescription: "Neu vs. Stamm, Wiederkehrrate und Kunden, die du zurückholen solltest.",
      auswUtilisation: "Auslastung",
      auswUtilisationDescription: "Wie gut du gebucht bist, schwache Zeiten und No-Show-Quote.",
      auswOutstandingFees: "Ausfälle",
      auswOutstandingFeesDescription: "No-Shows und kurzfristige Absagen, die du noch berechnen kannst.",
      feeOpenTotal: "Offene Gebühren gesamt",
      feeOpenCount: "Offene Fälle",
      feeInfoOnly: "Nur Hinweis — du kassierst selbst",
      feeOnlyOpen: "Nur offene",
      feeNoneOpen: "Keine offenen Ausfallgebühren in diesem Zeitraum 🎉",
      feeNoneAtAll: "Keine No-Shows oder kurzfristigen Absagen in diesem Zeitraum 🎉",
      feeTypeNoShow: "No-Show",
      feeTypeShortNotice: "Kurzfristige Absage",
      feeStatusOutstanding: "Offen",
      feeStatusCollected: "Bezahlt",
      feeStatusWaived: "Erlassen",
      feeMarkCollected: "Bezahlt",
      feeMarkWaived: "Erlassen",
      feeReopen: "Wieder öffnen",
      auswVisibility: "Online-Sichtbarkeit",
      auswVisibilityDescription: "Lohnt sich deine Online-Präsenz? Google, Website und Rankings.",
      // Funnel
      funnelTitle: "Von der Sichtbarkeit zum Umsatz",
      funnelHint: "Aufrufe gesamt = Google-Profil-Aufrufe + Website-Besuche (letzte 7 Tage); Buchungen und Umsatz beziehen sich auf den gewählten Zeitraum.",
      funnelVisits: "Website-Besuche",
      funnelViews: "Google-Aufrufe",
      funnelReach: "Aufrufe gesamt",
      ovRevenue: "Umsatz",
      ovRevenueTip: "Umsatz aus Buchungen im gewählten Zeitraum (gebuchter Service-Preis, ohne Stornos).",
      ovBookingsTip: "Abgeschlossene Buchungen im gewählten Zeitraum.",
      ovAvgTicketTip: "Durchschnittlicher Umsatz pro Buchung.",
      ovNewCustomers: "Neukunden",
      ovNewCustomersTip: "Kunden mit ihrem allerersten Termin im gewählten Zeitraum.",
      ovVsPrev: "vs. Vorperiode",
      ovPerBooking: "pro Buchung",
      ovInPeriod: "in diesem Zeitraum",
      funnelActions: "Anrufe & Klicks",
      funnelBookings: "Buchungen",
      funnelRevenue: "Umsatz",
      // Umsatzstärkste Kunden
      topCustomersTitle: "Umsatzstärkste Kunden",
      topCustomersTeaser: "Wer am meisten bringt – und wer gerade abzuwandern droht.",
      topCustomersNote: "Rangliste mit Umsatz, Besuchen, Ø Bon und Win-back-Hinweis.",
      viewInRevenueTab: "Im Umsatz-Reiter ansehen",
      // Umsatz-Reiter Abschnitte
      auswRevenueTrend: "Umsatzverlauf",
      auswRevenueByService: "Umsatz nach Dienstleistung",
      auswRevenueByStaff: "Umsatz nach Mitarbeiter",
      // Kunden-Reiter Abschnitte
      auswNewVsReturning: "Neu- vs. Stammkunden",
      auswRepeatRate: "Wiederkehrrate",
      auswWinbackList: "Win-back-Liste",
      auswWinbackNote: "Umsatzstarke Kunden, die länger nicht da waren.",
      auswVisitFrequency: "Besuchsfrequenz",
      auswAcquisitionSource: "Akquise-Quelle",
      kpiRepeatRate: "Stammkunden-Quote",
      kpiAvgVisits: "Ø Besuche/Kunde",
      kpiActiveCustomers: "Aktive Kunden",
      legendNew: "Neukunden",
      legendReturning: "Stammkunden",
      sourceUnknown: "Unbekannt",
      sourceCustomers: "{{n}} Kunden",
      // Auslastung-Reiter Abschnitte
      auswUtilisationRate: "Auslastungsgrad",
      auswUtilisationByStaffNote: "Gebuchte vs. verfügbare Stunden – nach Mitarbeiter filterbar.",
      auswWeakTimes: "Schwache Tage & Zeiten",
      auswNoShowRate: "No-Show- & Stornoquote",
      kpiNoShowRate: "No-Show-Quote",
      kpiCancelRate: "Storno-Quote",
      auswUtilisationByStaff: "Auslastung nach Mitarbeiter",
      heatmapNote: "Dunkler = mehr Buchungen.",
      heatmapBookings: "Buchungen",
      heatmapPeak: "stärkste Zeit",
      utilEstimate: "Schätzung auf Basis der wöchentlichen Verfügbarkeit.",
      // Sichtbarkeit Unteransichten
      visProfile: "Google-Profil",
      visHeadline: "Bist du sichtbar? Wofür dich Kunden finden.",
      visSearchTitle: "Wofür dich Kunden bei Google finden",
      visSearchExplain: "Die Begriffe, mit denen Leute letzten Monat bei Google auf dein Profil gestoßen sind.",
      visSeenTimes: "{{n}}× gesehen",
      visSearchMonth: "Stand: {{month}}",
      visSearchEmpty: "Noch keine Suchbegriff-Daten von Google.",
      visMapsTitle: "Deine Google-Maps-Platzierung",
      visMapsComing: "Wir richten die laufende Messung deiner Maps-Platzierung für deine Hauptbegriffe ein — bald siehst du hier, auf welchem Platz du z. B. bei „Friseur [Stadt]“ stehst.",
      visAdvanced: "Erweitert: SEO-Details",
      // Umsatz-Reiter — Kennzahlen, Aufschlüsselung, Top-Kunden
      auswTotalRevenue: "Gesamtumsatz",
      auswAvgTicket: "Ø Bon",
      auswNoData: "Für diesen Zeitraum noch keine Daten.",
      auswBookingsCount: "{{n}} Buchungen",
      dimService: "Nach Dienstleistung",
      dimStaff: "Nach Mitarbeiter",
      tcCustomer: "Kunde",
      tcRevenue: "Umsatz",
      tcVisits: "Besuche",
      tcAvgTicket: "Ø Bon",
      tcLastVisit: "Letzter Besuch",
      tcFavService: "Lieblings-Service",
      tcHint: "Hinweis",
      tcUnnamed: "Unbenannt",
      tcActions: "Aktionen",
      tcCall: "Anrufen",
      tcOpenProfile: "Profil öffnen",
      auswWinbackMonths: "⚠️ seit {{months}} Mon. nicht da",
      // Bewertungen-Reiter
      auswReviews: "Bewertungen",
      auswReviewsDescription: "Deine Google-Bewertungen filtern, lesen und verstehen.",
      revNotConnected: "Verbinde dein Google-Unternehmensprofil, um hier Bewertungen zu sehen.",
      revSortNewest: "Neueste",
      revSortOldest: "Älteste",
      revAllStars: "Alle Sterne",
      revAll: "Alle",
      revAnswered: "Beantwortet",
      revUnanswered: "Unbeantwortet",
      revSearchPlaceholder: "Bewertungen durchsuchen…",
      revSentimentTitle: "Sentiment-Analyse",
      revSentimentNote: "KI-Auswertung von positivem / neutralem / negativem Feedback — kommt als Nächstes.",
      revSentimentExplain: "Wir erkennen automatisch, ob eine Bewertung positiv, neutral oder negativ klingt — so siehst du die Stimmung deiner Kunden auf einen Blick.",
      revAllAnswers: "Alle Status",
      revAllSentiment: "Alle Stimmungen",
      revNoReviews: "Keine Bewertungen passen zu diesen Filtern.",
      revReplyLabel: "Deine Antwort",
      revNotAnswered: "Noch nicht beantwortet",
      revSentimentPositive: "Positiv",
      revSentimentNeutral: "Neutral",
      revSentimentNegative: "Negativ",
      revSentimentEmpty: "Noch kein Sentiment ausgewertet — starte die Analyse für die Verteilung.",
      revAnalyzing: "Analysiere…",
      revAnalyzePending: "{{n}} analysieren",
      revReply: "Antworten",
      revReplyPlaceholder: "Antwort schreiben…",
      revSend: "Antwort posten",
      revSending: "Wird gepostet…",
      revCancel: "Abbrechen",
      revReplyError: "Antwort konnte nicht gepostet werden. Bitte erneut versuchen.",
      revDraftHint: "KI-Vorschlag vorausgefüllt — vor dem Posten anpassen.",
      revEdit: "Bearbeiten",
      revUpdate: "Antwort aktualisieren",
      revDelete: "Löschen",
      revAutoAnswered: "Automatisch beantwortet",
      revHeldReason: "Wartet auf dich: {{reason}}",
      revHeldRisk: "heikle Formulierung in der Bewertung",
      revHeldFailed: "Posten bei Google fehlgeschlagen",
      revHeldKillSwitch: "Automatik ist gestoppt",
      revHeldDisabled: "Automatik ist ausgeschaltet",
      revHeldStars: "zu wenige Sterne, bleibt immer manuell",
      revHeldNegative: "der Text wirkt negativ",
      revHeldOld: "die Bewertung ist älter als 14 Tage",
      revHeldBefore: "stammt aus der Zeit vor der Aktivierung",
      revHeldRateHour: "Stundenlimit erreicht",
      revHeldRateDay: "Tageslimit erreicht",
      revHeldDraft: "der Entwurf hat die Sicherheitsprüfung nicht bestanden",
      gbpAutoReplyTitle: "Bewertungen automatisch beantworten",
      gbpAutoReplyDescription:
        "Positive Bewertungen werden von allein beantwortet, innerhalb von etwa 10 Minuten. Kritische Bewertungen und alles, bei dem sich das System nicht sicher ist, warten immer auf einen Menschen.",
      gbpAutoReplyMinStars: "Automatisch beantworten ab",
      gbpAutoReplyFromFour: "4 Sternen und mehr",
      gbpAutoReplyFromFive: "nur 5 Sternen",
      gbpAutoReplyBacklogNote:
        "Nur Bewertungen, die ab jetzt eingehen, werden automatisch beantwortet. Ältere Bewertungen bleiben unangetastet.",
      gbpAutoReplyOn:
        "Neue positive Bewertungen werden ab jetzt innerhalb von etwa 10 Minuten automatisch beantwortet.",
      gbpAutoReplyOff: "Bewertungen warten wieder auf deine Freigabe.",
      insTitle: "KI-Insights",
      insGenerate: "Insights generieren",
      insRefresh: "Aktualisieren",
      insGenerating: "Wird erstellt…",
      insEmpty: "Noch keine Insights — erstelle eine Zusammenfassung all deiner Bewertungen.",
      insStrengths: "Das lieben deine Kunden",
      insImprovements: "Verbesserungspotenzial",
      insRecommendations: "Empfehlungen",
      insStaff: "Gelobte Mitarbeiter",
      insBasedOn: "Basierend auf {{n}} Bewertungen",

      googleBusinessProfile: "Google Unternehmensprofil",
      googleBusinessProfileDescription: "Verbinden und verwalten Sie Ihr Google Unternehmensprofil",
      gbpIntegration: "Google Unternehmensprofil",
      gbpIntegrationDescription: "Verbinden Sie Ihr Google Unternehmensprofil zur Rezensionsverwaltung",
      googleSettings: "Google",
      googleSettingsDescription: "Bewertungslink, Ranking & automatische Bewertungsantworten",
      googleReviewLinkSection: "Bewertungslink & Ranking",
      googleReviewRepliesSection: "Bewertungen automatisch beantworten",
      gbpProfileViews: "Google Profilaufrufe",
      gbpAvgRating: "Ø Bewertung",
      gbpNewReviews: "Neue Bewertungen",
      gbpAnswerRate: "Antwortquote",
      gbpFromReviews: "aus {{count}} Bewertungen",
      gbpAnsweredOf: "{{answered}} von {{total}} beantwortet",
      gbpProfileViewsTooltip: "Wie oft dein Google-Profil diese Woche in Google Suche und Maps angezeigt wurde. Googles Daten hinken etwa 2-3 Tage hinterher.",
      gbpAvgRatingTooltip: "Der Durchschnitt aller deiner Google-Sterne-Bewertungen.",
      gbpNewReviewsTooltip: "Neue Google-Bewertungen der letzten 7 Tage, im Vergleich zur Woche davor.",
      gbpAnswerRateTooltip: "Anteil deiner Bewertungen, die bereits beantwortet sind. Sylva beantwortet deine Bewertungen automatisch.",
      gbpConnected: "Mit Google Unternehmensprofil verbunden",
      gbpConnectionFailed: "Verbindung mit Google Unternehmensprofil fehlgeschlagen",
      gbpDisconnected: "Google Unternehmensprofil getrennt",
      connectGBP: "Google Unternehmensprofil verbinden",
      gbpDescription: "Verbinden Sie Ihr Google Unternehmensprofil, um Bewertungen zu verwalten, auf Kunden zu antworten und Updates direkt von diesem Dashboard zu posten.",
      connectedOn: "Verbunden am",
      availableFeatures: "Verfügbare Funktionen:",
      gbpFeatureReviews: "Bewertungen ansehen und beantworten",
      gbpFeaturePosts: "Beiträge und Updates erstellen",
      gbpFeatureNotifications: "Benachrichtigungen über neue Bewertungen erhalten",
      gbpChangeProfile: "Profil ändern",
      gbpLocationSelected: "Google Unternehmensprofil gespeichert",
      gbpSelectLocationTitle: "Richtiges Google-Profil auswählen",
      gbpSelectLocationDescription: "Dieses Google-Konto verwaltet mehrere Unternehmensprofile. Wählen Sie das Profil, das zu diesem Kunden gehört.",
      gbpSuggestedMatch: "Empfohlen",
      gbpNoProfilesFound: "Für dieses Konto wurden keine Google-Profile gefunden.",
      disconnect: "Trennen",
      gbpNotConnected: "Nicht verbunden",
      gbpConnectPrompt: "Verbinden Sie Ihr Google Unternehmensprofil, um Bewertungsverwaltung und Posting-Funktionen freizuschalten.",
      gbpOAuthNote: "Sie werden zu Google weitergeleitet, um den Zugriff auf Ihr Unternehmensprofil zu autorisieren.",
      disconnectGBPTitle: "Google Unternehmensprofil trennen?",
      disconnectGBPDescription: "Dies entfernt die Verbindung zu Ihrem Google Unternehmensprofil. Sie können Bewertungen nicht mehr verwalten oder Beiträge erstellen, bis Sie sich erneut verbinden.",
      notificationPreferencesDescription: "Konfigurieren Sie, wie Sie Benachrichtigungen erhalten",
      manageTeamMembers: "Teammitglieder verwalten",

      // Notification Preferences
      notificationPreferences: {
        notificationPreferencesTitle: "Benachrichtigungseinstellungen",
        configureNotifications: "Benachrichtigungen konfigurieren",
        myNotificationPreferences: "Benachrichtigungseinstellungen",
        emailAddresses: "E-Mail-Adressen",
        emailAddressRequired: "E-Mail-Adresse ist erforderlich",
        invalidEmailAddress: "Bitte geben Sie eine gültige E-Mail-Adresse ein",
        addEmailError: "Fehler beim Hinzufügen der E-Mail-Adresse",
        emailAdded: "E-Mail-Adresse erfolgreich hinzugefügt",
        deleteEmailError: "Fehler beim Entfernen der E-Mail-Adresse",
        emailDeleted: "E-Mail-Adresse erfolgreich entfernt",
        toggleEmailError: "Fehler beim Aktualisieren der Benachrichtigungseinstellungen",
        noEmailsConfigured: "Noch keine E-Mail-Adressen konfiguriert",
        enterEmail: "E-Mail-Adresse eingeben",
        addEmail: "E-Mail hinzufügen",
        removeEmail: "E-Mail entfernen",
        toggleNotifications: "Benachrichtigungen umschalten",
        createdBy: "Eingerichtet von",
        emailDescription: "Sie erhalten Benachrichtigungen über neue Termine, Stornierungen und Umplanungen an diese E-Mail-Adressen.",
        comingSoon: "Demnächst verfügbar:",
        smsComingSoon: "SMS-Benachrichtigungen",
        whatsappComingSoon: "WhatsApp-Benachrichtigungen",
      },

      notificationHistory: {
        tenantNotifications: "Alle Benachrichtigungen",
        historyDescription: "Alle Benachrichtigungen in Ihrem Unternehmen anzeigen",
        noNotifications: "Noch keine Benachrichtigungen gesendet",
        recipient: "An",
        created: "Erstellt",
        sent: "Gesendet",
        errorStatus: "Fehler",
        unknownRecipient: "Unbekannter Empfänger",
      },

      // Voice Agent Settings
      voiceAgent: {
        title: "Sprachagent",
        description: "Konfigurieren Sie Ihren automatisierten Sprachassistenten für eingehende Anrufe",
        enableAgent: "Sprachagent aktivieren",
        enableAgentDescription: "Erlauben Sie dem Sprachagenten, eingehende Anrufe automatisch entgegenzunehmen und zu bearbeiten",
        phoneNumber: "Telefonnummer",
        phoneNumberDescription: "Dies ist Ihre zugewiesene Telefonnummer. Leiten Sie Ihre Geschäftsanrufe an diese Nummer weiter, um den Sprachagenten zu aktivieren.",
        phoneNumberUpdated: "Telefonnummer erfolgreich aktualisiert",
        failedToUpdatePhoneNumber: "Telefonnummer konnte nicht aktualisiert werden",
        copyPhoneNumber: "Kopieren",
        phoneNumberCopied: "Telefonnummer in Zwischenablage kopiert",
        agentAvailableHeading: "Ihr Sprachagent ist unter dieser Nummer erreichbar",
        agentAvailableDescription: "Leiten Sie Anrufe an diese Nummer weiter, damit der Agent Ihre Kunden automatisch betreut.",
        proRequired: "Plus-Plan erforderlich",
        proRequiredDescription: "Upgraden Sie auf den Plus-Plan, um eine Telefonnummer für Ihren Sprachagenten zu erhalten.",
        lastSync: "Zuletzt synchronisiert",
        syncNow: "Jetzt synchronisieren",
        syncing: "Synchronisiere...",
        syncNowDescription: "Manuell Synchronisierung auslösen, um den Agenten mit den neuesten Dienstleistungen und Teammitgliedern zu aktualisieren",
        syncSuccess: "Sprachagent erfolgreich synchronisiert",
        syncError: "Synchronisierung des Sprachagenten fehlgeschlagen",
        statusUpdated: "Status des Sprachagenten aktualisiert",
        failedToUpdateStatus: "Status des Sprachagenten konnte nicht aktualisiert werden",
        statusSynced: "Synchronisiert",
        statusPending: "Synchronisiere...",
        statusError: "Synchronisierung fehlgeschlagen",
        statusNeverSynced: "Nicht synchronisiert",
        assistantInfo: "Assistenten-Informationen",
        assistantId: "Assistenten-ID",
        voiceProvider: "Sprachanbieter",
        modelProvider: "KI-Modell",
        configurationUpdated: "Konfiguration erfolgreich aktualisiert",
        failedToUpdateConfiguration: "Konfiguration konnte nicht aktualisiert werden",
      },

      callForwarding: {
        title: "Rufumleitung einrichten",
        description: "Leiten Sie Ihre Anrufe zu Sylvia weiter, damit keine wichtigen Kundenanfragen verloren gehen",
        setupGuide: "Rufumleitung einrichten",
        setupGuideDescription: "Schritt-für-Schritt Anleitung zur Einrichtung der Rufumleitung",
        dialCodeInstructions: "So richten Sie die Rufumleitung ein",
        step1: "Nehmen Sie Ihr Telefon zur Hand",
        step2: "Wählen Sie einen der unten stehenden Codes",
        step3: "Drücken Sie die grüne Anruftaste",
        forwardingOptions: "Rufumleitungs-Optionen",
        chooseOption: "Wählen Sie eine der folgenden Optionen:",
        sylviaPhoneNumber: "Sylvias Telefonnummer",
        method1Title: "Methode 1: Über Einstellungen",
        method2Title: "Methode 2: Über Tastencode",
        dialCodeDescription: "Wählen Sie einen der folgenden Codes auf Ihrem Telefon:",

        // Android instructions
        android: {
          step1: "Öffnen Sie die Telefon-App",
          step2: "Tippen Sie auf das Menü (drei Punkte) und wählen Sie 'Einstellungen'",
          step3: "Wählen Sie 'Anrufe' und dann 'Rufumleitung'",
          step4: "Wählen Sie die gewünschte Option (z.B. 'Immer umleiten', 'Bei Besetzt')",
          step5: "Geben Sie Sylvias Telefonnummer ein:",
        },

        // iPhone instructions
        iphone: {
          step1: "Öffnen Sie die 'Einstellungen'",
          step2: "Wählen Sie 'Telefon'",
          step3: "Tippen Sie auf 'Rufweiterleitung' und aktivieren Sie den Schalter",
          step4: "Geben Sie Sylvias Telefonnummer ein:",
        },

        // Landline instructions
        landlineInstructions: "Rufumleitung per Tastencode",
        landlineDescription2: "Nehmen Sie den Hörer ab und wählen Sie einen der folgenden Codes:",

        // Forwarding types
        alwaysForward: "Alle Anrufe weiterleiten",
        alwaysForwardDescription: "Leitet alle eingehenden Anrufe sofort an Sylvia weiter",
        busyForward: "Bei Besetzt weiterleiten",
        busyForwardDescription: "Leitet Anrufe weiter, wenn Ihre Leitung besetzt ist",
        noAnswerForward: "Bei Nichtannahme weiterleiten",
        noAnswerForwardDescription: "Leitet Anrufe weiter, wenn Sie nicht abnehmen",
        unreachableForward: "Bei Nichterreichbarkeit weiterleiten",
        unreachableForwardDescription: "Leitet Anrufe weiter, wenn Sie nicht erreichbar sind",

        // Deactivation
        deactivateTitle: "Rufumleitung deaktivieren",
        deactivateDescription: "Um die Rufumleitung wieder auszuschalten:",
        deactivateAll: "Alle Rufumleitungen deaktivieren",
        checkStatus: "Aktuellen Status prüfen",

        // Important notes
        importantNotes: "Wichtige Hinweise",
        note1: "Die Rufumleitung ist in der Regel kostenlos, kann aber je nach Anbieter und Tarif variieren",
        note2: "Nach der Einrichtung rufen Sie Ihre eigene Nummer an, um zu testen, ob Sylvia abnimmt",
      },

      retry: "Erneut versuchen",
      viewSettings: "Einstellungen anzeigen",

      // Studio/Tenant Setup
      welcomeToSylva: "Willkommen bei Sylva",
      accountBeingSetUp: "Ihr Konto wird eingerichtet.",
      workspaceAccessPending: "Sie erhalten Zugang zu Ihrem Arbeitsbereich, sobald er bereit ist.",
      setupYourStudio: "Lassen Sie uns Ihr Studio einrichten",
      studioName: "Studio Name",
      studioNamePlaceholder: "Geben Sie Ihren Studio-Namen ein",
      ownerEmail: "Inhaber E-Mail",
      ownerEmailPlaceholder: "inhaber@beispiel.de",
      ownerEmailHelp: "E-Mail-Adresse für Benachrichtigungen",
      primaryColor: "Primärfarbe",
      primaryColorHelp: "Diese Farbe wird für Buttons in kundenseitigen E-Mails verwendet",
      businessAddress: "Geschäftsadresse",
      businessAddressPlaceholder: "Hauptstraße 123",
      businessAddressHelp: "Diese Adresse wird in Kunden-E-Mails mit einem Link zu Google Maps angezeigt",
      city: "Stadt",
      cityPlaceholder: "Ihre Stadt",
      studioSlug: "Studio URL",
      studioSlugPlaceholder: "mein-studio",
      studioSlugHelp: "Dies wird in Ihrer öffentlichen Buchungs-URL verwendet",
      createStudio: "Studio erstellen",

      // Google Profile
      connectGoogleProfile: "Google-Profil verbinden",
      googleProfileConnected: "Google-Profil verbunden",
      searchGoogleBusiness: "Suchen Sie nach Ihrem Unternehmen",
      search: "Suchen",
      searchBusinessPlaceholder: "Geben Sie Ihren Firmennamen und Standort ein",
      selectYourBusiness: "Wählen Sie Ihr Unternehmen aus den Ergebnissen",
      changeGoogleProfile: "Profil ändern",
      viewOnGoogleMaps: "Auf Google Maps anzeigen",
      googleProfileHelp: "Verbinden Sie Ihr Google Business Profil, um Rankings zu verfolgen und Ihre Online-Präsenz zu verwalten",
      searching: "Suche läuft...",
      noResultsFound: "Keine Unternehmen gefunden. Versuchen Sie einen anderen Suchbegriff.",
      disconnectGoogleProfile: "Trennen",
      googleProfileDescription: "Verknüpfen Sie Ihr Google Business Profil, um lokales SEO-Tracking und Analysen zu aktivieren",

      // Member Management
      teamManagement: "Team-Verwaltung",
      teamManagementDescription: "Verwalten Sie Teammitglieder und Einladungen für Ihre Organisation",
      inviteNewOwner: "Neuen Inhaber einladen",
      inviteOwner: "Inhaber einladen",
      inviteOwnerDescription: "Laden Sie einen anderen Inhaber ein, um bei der Verwaltung dieser Organisation zu helfen",
      memberEmailAddress: "E-Mail-Adresse",
      enterEmailAddress: "E-Mail-Adresse eingeben",
      sendInvitation: "Einladung senden",
      sendingInvitation: "Einladung wird gesendet...",
      pendingInvitations: "Ausstehende Einladungen",
      currentMembers: "Aktuelle Mitglieder",
      invitedOn: "Eingeladen am",
      joinedOn: "Beigetreten am",
      by: "von",
      revokeInvitation: "Einladung widerrufen",
      revokeInvitationConfirmation: "Sind Sie sicher, dass Sie die Einladung für {{email}} widerrufen möchten? Diese Person kann Ihrer Organisation nicht mehr mit dieser Einladung beitreten.",
      removeMember: "Mitglied entfernen",
      removeMemberConfirmation: "Sind Sie sicher, dass Sie {{name}} aus Ihrer Organisation entfernen möchten? Diese Person verliert den Zugriff auf alle Daten und Einstellungen.",
      noMembersYet: "Noch keine Teammitglieder",
      inviteFirstMember: "Laden Sie Ihr erstes Teammitglied ein, um mit der Zusammenarbeit zu beginnen",

      // Member Management Success/Error Messages
      invitationSentSuccessfully: "Einladung erfolgreich gesendet",
      failedToSendInvitation: "Fehler beim Senden der Einladung",
      memberRemovedSuccessfully: "Mitglied erfolgreich entfernt",
      failedToRemoveMember: "Fehler beim Entfernen des Mitglieds",
      invitationRevokedSuccessfully: "Einladung erfolgreich widerrufen",
      failedToRevokeInvitation: "Fehler beim Widerrufen der Einladung",
      failedToLoadMembers: "Fehler beim Laden der Teammitglieder",
      userAlreadyMember: "Dieser Benutzer ist bereits Mitglied Ihrer Organisation",
      userAlreadyInvited: "Dieser Benutzer wurde bereits eingeladen",
      memberEmailRequired: "E-Mail-Adresse ist erforderlich",
      memberInvalidEmailFormat: "Bitte geben Sie eine gültige E-Mail-Adresse ein",

      // Roles
      owner: "Inhaber",
      admin: "Administrator",
      manager: "Manager",
      member: "Mitglied",
      viewer: "Betrachter",
      creatingStudio: "Studio wird erstellt...",
      studioCreatedSuccessfully: "Studio erfolgreich erstellt",
      studioCreationFailed: "Fehler beim Erstellen des Studios",
      studioNameRequired: "Studio-Name ist erforderlich",
      studioSlugRequired: "Studio-URL ist erforderlich",

      // Dashboard
      overview: "Alles Wichtige auf einen Blick",

      totalCustomers: "Kunden gesamt",

      upcomingAppointments: "Kommende Termine",
      pastAppointments: "Vergangene Termine",
      todaysAppointments: "Heutige Termine",
      noAppointmentsToday: "Keine Termine für heute geplant",
      unassigned: "Nicht zugewiesen",

      // Appointments
      services: "Dienstleistungen",

      duration: "Dauer",
      price: "Preis",
      priceOnRequest: "Nach Vereinbarung",
      description: "Beschreibung",
      color: "Farbe",

      manageAllAppointments: "Alle Ihre Termine verwalten",
      allAppointments: "Alle Termine",
      noAppointments: "Keine Termine gefunden",
      searchServices: "Dienstleistungen suchen...",

      // Team
      teamMembers: "Teammitglieder",

      updateTeamMember: "Teammitglied aktualisieren",
      editTeamMemberDescription:
        "Teammitglied-Informationen und Verfügbarkeit aktualisieren",
      teamMemberUpdatedSuccess: "Teammitglied wurde erfolgreich aktualisiert",
      teamMemberAddedSuccess: "Teammitglied wurde erfolgreich hinzugefügt",
      back: "Zurück",
      teamMemberDetails: "Teammitglied-Details",
      email: "E-Mail",
      role: "Rolle",
      openDirectSchedulingLink: "Direkter Terminbuchungslink öffnen",
      teamMemberNotAvailable: "Teammitglied ist nicht verfügbar",
      noServicesAvailable: "Keine Dienstleistungen verfügbar",
      teamMemberNoServices:
        "Teammitglied hat keine Dienstleistungen zugewiesen",

      // Customers
      newCustomer: "Kunde hinzufügen",
      name: "Name",
      firstNameIsRequired: "Vorname ist erforderlich",
      lastNameIsRequired: "Nachname ist erforderlich",

      phone: "Telefon",
      birthdate: "Geburtsdatum",
      gender: "Geschlecht",
      male: "Männlich",
      female: "Weiblich",
      diverse: "Divers",
      genderOptional: "Geschlecht (optional)",
      street: "Straße",
      zipCode: "PLZ",
      emailOptional: "E-Mail (optional)",
      phoneOptional: "Telefon (optional)",
      phoneFormatExample: "Format: +49221123456",
      searchCustomers: "Kunden suchen...",
      createNew: "Neu erstellen",
      createNewCustomer: "Neuen Kunden erstellen",
      createCustomer: "Kunde erstellen",
      customerCreated: "Kunde erfolgreich erstellt",
      noCustomersFound: "Keine Kunden gefunden",

      // Conversations
      noConversations: "Keine Unterhaltungen",
      searchConversations: "Unterhaltungen suchen...",
      callInProgress: "Anruf läuft",
      callInProgressDescription: "Echtzeit-Transkript erscheint unten",
      aiPausedTitle: "Sylvia ist für diesen Chat pausiert.",
      aiPausedMessage: "Sylvia wird {{time}} auf neue Nachrichten wieder automatisch antworten.",
      customerTyping: "Kunde schreibt...",
      agentTyping: "Agent schreibt...",
      customersList: "Kunden",
      noCustomersWithConversations: "Keine Kunden mit Unterhaltungen",
      selectCustomerToViewMessages: "Wählen Sie einen Kunden aus, um Nachrichten anzuzeigen",
      conversationCount: "{{count}} Unterhaltung",
      conversationCount_plural: "{{count}} Unterhaltungen",
      channelVoice: "Anruf",
      clickToViewTranscript: "Klicken Sie, um das Transkript anzuzeigen",
      viewTranscript: "Einsehen",
      voiceCallTranscript: "Anruftranskript",
      noTranscriptAvailable: "Kein Transkript verfügbar",
      fullTranscript: "Vollständiges Transkript",
      sendMessage: "Nachricht senden",
      typeMessage: "Nachricht eingeben...",
      sendVia: "Senden per",
      channelSms: "SMS",
      channelWhatsapp: "WhatsApp",
      channelWeb: "Web",
      channelAvailable: "Verfügbar",
      channelNotConfigured: "Nicht konfiguriert",
      suggestedReplies: "Vorgeschlagene Antworten",
      generatingSuggestions: "Generiere Vorschläge...",
      showSuggestions: "Vorschläge anzeigen",
      whatsappConversation: "WhatsApp",
      chatbotConversation: "Chatbot",
      smsConversation: "SMS",
      allConversations: "Alle Unterhaltungen",
      unreadConversations: "Ungelesen",
      readConversations: "Gelesen",
      conversationWith: "Unterhaltung mit",
      lastMessage: "Letzte Nachricht",
      messageSent: "Gesendet",
      messageFailed: "Fehlgeschlagen",
      messagePending: "Wird gesendet...",
      messageQueued: "In Warteschlange",
      messageSending: "Wird gesendet",
      messageDelivered: "Zugestellt",
      messageRead: "Gelesen",
      messageUndeliverable: "Nicht zustellbar",
      sentBySylvia: "Automatisch von Sylvia gesendet",
      callSummary: "Anrufzusammenfassung",
      errorCode: "Fehlercode",
      loadingConversations: "Unterhaltungen laden...",
      yesterday: "Gestern",
      selectConversation: "Wählen Sie eine Unterhaltung aus, um Nachrichten anzuzeigen",
      chatbotReadOnly: "Chatbot-Unterhaltungen sind schreibgeschützt",
      all: "Alle",
      unread: "Ungelesen",
      leads: "Interessenten",
      whatsappChats: "WhatsApp Chats",
      existingCustomer: "Bestandskunde",
      errorLoadingChats: "Fehler beim Laden der Chats",
      noChatsWithCustomers: "Keine Chats mit bestehenden Kunden",
      chatsWillAppearWhenCustomersMessage: "Chats erscheinen hier, wenn bestehende Kunden Nachrichten senden",
      you: "Sie",
      loading: "Lädt...",
      whatsappChatSelected: "WhatsApp Chat ausgewählt",
      implementChatInterface: "Chat-Oberfläche kommt bald - zeigt derzeit WAHA-Integration",
      conversationSettings: "Unterhaltungseinstellungen",
      "conversationSettings.overallSettings": "Allgemeine Einstellungen",
      "conversationSettings.overallSettingsDescription": "Konfigurieren Sie, wie der Agent mit Ihren Kunden kommuniziert",
      "conversationSettings.whatsappSettingsDescription": "WhatsApp-Nachrichteneinstellungen konfigurieren",
      "conversationSettings.phoneSettings": "Telefon",
      "conversationSettings.phoneSettingsDescription": "Sylvias Telefonnummer und Rufumleitungsanleitung",
      welcomeMessage: "Willkommensnachricht",
      enableWelcomeMessage: "Willkommensnachricht aktivieren",
      welcomeMessageDescription: "Automatisch eine Willkommensnachricht an neue Interessenten per WhatsApp senden",
      selectTemplate: "Vorlage auswählen",
      agentMode: "Agenten-Modus",
      enableAgentMode: "Automatische Antworten aktivieren",
      agentModeDescription: "Wenn aktiviert, antwortet der Agent automatisch auf Kundennachrichten",
      agentModeDisabledDescription: "Wenn deaktiviert, schlägt der Agent nur Antworten vor, die Mitarbeiter überprüfen können",
      templateNotFound: "Vorlage nicht gefunden",
      templateNotFoundDescription: "Die ausgewählte WhatsApp-Vorlage existiert nicht. Bitte wählen Sie eine andere Vorlage oder erstellen Sie diese in Ihrem Meta Business-Konto.",
      templatePendingDescription: "Diese Vorlage wartet auf Genehmigung durch Meta. Sie kann erst nach der Genehmigung verwendet werden.",
      noTemplatesDescription: "Es sind keine WhatsApp-Vorlagen konfiguriert. Bitte erstellen Sie zuerst Vorlagen in Ihrem Meta Business-Konto.",
      customerFormality: "Kundenansprache",
      customerFormalityDescription: "Wähle wie du deine Kunden auf Deutsch ansprichst.",
      formalAddress: "Formell (Sie)",
      informalAddress: "Informell (Du)",
      manageTemplatesInMeta: "Verwalten Sie Ihre WhatsApp-Nachrichtenvorlagen im Meta Business Manager",
      metaBillingRequired: "Meta-Konto-Abrechnung erforderlich",
      metaBillingDescription: "Um Willkommensnachrichten zu senden, müssen Sie eine Zahlungsmethode zu Ihrem Meta Business-Konto hinzufügen.",
      openBillingHub: "Zahlungsmethode hinzufügen",
      detailedInstructions: "Detaillierte Anleitung",
      createWelcomeTemplate: "Willkommensvorlage erstellen",
      createWelcomeTemplateModalTitle: "WhatsApp-Willkommensvorlage erstellen",
      createWelcomeTemplateModalDescription: "Dies erstellt eine neue Willkommensnachrichten-Vorlage in Ihrem WhatsApp Business-Konto. Die Vorlage wird zur Überprüfung an Meta gesendet.",
      templateLanguage: "Sprache",
      templateCategory: "Kategorie",
      templateCategoryMarketing: "Marketing",
      websiteButton: "Website-Button",
      websiteButtonUrl: "Website-URL",
      createTemplateButton: "Vorlage erstellen",
      templateCreatedSuccessTitle: "Vorlage erstellt",
      templateCreatedSuccessDescription: "Die Willkommensvorlage wurde erstellt und zur Überprüfung an Meta gesendet. Sie steht nach der Genehmigung zur Verfügung.",
      templateCreationFailedTitle: "Vorlagenerstellung fehlgeschlagen",
      templateCreationFailedDescription: "Die Willkommensvorlage konnte nicht erstellt werden. Bitte versuchen Sie es erneut oder kontaktieren Sie den Support.",
      templateAlreadyExists: "Eine Vorlage mit diesem Namen existiert bereits",
      missingWebsiteUrl: "Bitte fügen Sie zuerst eine Website-URL zu Ihren Geschäftseinstellungen hinzu",
      selectFormalityFirst: "Bitte wählen Sie zuerst eine Kundenansprache aus",

      // WhatsApp Integration
      whatsapp: {
        preflightTitle: 'Vor dem Start',
        preflightSubtitle: 'Bitte bestätigen Sie jeden Punkt, um typische Probleme beim Setup zu vermeiden.',
        preflightItem1: 'Meine Geschäfts-Telefonnummer ist aktuell NICHT in der WhatsApp oder WhatsApp Business App auf einem Handy aktiv.',
        preflightItem1Hint: 'Falls doch, schlägt das Setup fehl. Löschen Sie den WhatsApp-Account auf dem Handy zuerst — siehe Reset-Video unten.',
        preflightItem2: 'Ich habe einen Meta Business Manager / Business Portfolio (oder bin damit einverstanden, dass Meta während des Setups einen erstellt).',
        preflightItem2Link: 'Meta Business Suite öffnen',
        preflightItem3: 'Ich habe meine Facebook-Login-Daten bereit (privater Account, der die Firma verwaltet).',
        preflightAllRequired: 'Bitte bestätigen Sie alle Punkte, um fortzufahren.',
        connectionActive: 'Verbunden',
        connectionExpired: 'Verbindung abgelaufen — bitte erneut verbinden',
        connectionError: 'Verbindungsproblem — bitte prüfen',
        connectionUnknown: 'Verbindungsstatus unbekannt',
        connectedNumber: 'Verbundene Nummer-ID',
        lastChecked: 'Zuletzt geprüft',
        recheckNow: 'Jetzt prüfen',
        rechecking: 'Prüfe...',
        conciergeTitle: 'Brauchen Sie Hilfe? Wir richten es für Sie ein',
        conciergeText: 'Unser Team vereinbart einen 15-minütigen Screen-Share und verbindet WhatsApp für Sie.',
        conciergeCta: 'Einrichtungshilfe anfordern',
        conciergeEmailSubject: 'WhatsApp Setup Hilfe angefragt',
        conciergeEmailGreeting: 'Hallo Sylva Team,',
        conciergeEmailBody: 'ich benötige Hilfe beim WhatsApp-Setup.',
        connectWhatsApp: "WhatsApp verbinden",
        disconnectWhatsApp: "WhatsApp trennen",
        reconnectWhatsApp: "WhatsApp erneut verbinden",
        reconnect: "Erneut verbinden",
        sessionDisconnected: "Ihre WhatsApp-Sitzung wurde getrennt. Verbinden Sie sich erneut, um weiter zu chatten.",
        scanQRCode: "QR-Code scannen",
        connectionStatus: "Verbindungsstatus",
        connected: "Verbunden",
        connecting: "Verbinde...",
        disconnected: "Getrennt",
        qrCodeExpired: "QR-Code abgelaufen, bitte aktualisieren",
        sessionCreated: "WhatsApp-Sitzung erfolgreich erstellt",
        sessionCreateError: "WhatsApp-Sitzung konnte nicht erstellt werden",
        sessionStarted: "WhatsApp-Sitzung gestartet",
        sessionStartError: "WhatsApp-Sitzung konnte nicht gestartet werden",
        sessionStopped: "WhatsApp-Sitzung gestoppt",
        sessionStopError: "WhatsApp-Sitzung konnte nicht gestoppt werden",
        sessionDeleted: "WhatsApp-Sitzung gelöscht",
        sessionDeleteError: "WhatsApp-Sitzung konnte nicht gelöscht werden",
        sessionRestarted: "WhatsApp-Sitzung neu gestartet",
        sessionRestartError: "WhatsApp-Sitzung konnte nicht neu gestartet werden",
        qrCodeError: "QR-Code konnte nicht geladen werden",
        refreshQRCode: "QR-Code aktualisieren",
        scanWithPhone: "Scannen Sie diesen QR-Code mit Ihrer WhatsApp-App",
        openWhatsApp: "Öffnen Sie WhatsApp auf Ihrem Telefon",
        goToSettings: "Gehen Sie zu Einstellungen > Verknüpfte Geräte",
        tapLinkDevice: "Tippen Sie auf 'Gerät verknüpfen'",
        scanCode: "Scannen Sie diesen Code zum Verbinden",
        whatsappBusiness: "WhatsApp Business",
        connectWhatsappDesc: "Verbinden Sie Ihr WhatsApp Business-Konto, um Unterhaltungen zu verwalten.",
        whatsappConnected: "WhatsApp verbunden",
        whatsappConnectedDesc: "Ihr WhatsApp Business-Konto wurde erfolgreich verbunden.",
        whatsappConnectionError: "WhatsApp Business-Konto konnte nicht verbunden werden.",
        whatsappConnectionCancelled: "WhatsApp-Verbindung wurde abgebrochen.",
        whatsappTimeoutTitle: 'Zeitüberschreitung bei der Verbindung',
        whatsappTimeoutDesc: 'Die WhatsApp-Verbindung hat zu lange gedauert. Bitte versuchen Sie es erneut und klicken Sie am Ende des Meta-Popups auf "Fertigstellen".',
        facebookSdkNotLoaded: "Facebook SDK ist noch nicht geladen. Bitte versuchen Sie es erneut.",
        whatsappNotConfigured: "WhatsApp-Integration ist nicht konfiguriert.",
        serviceWindowActive: "24h Fenster aktiv",
        serviceWindowExpired: "Fenster abgelaufen",
        noTemplatesAvailable: "Keine WhatsApp-Vorlagen verfügbar. Bitte erstellen Sie Vorlagen in Ihrem WhatsApp Business Manager.",
        manageTemplates: "Vorlagen verwalten",
        loadingTemplates: "Lade Vorlagen...",
        showTemplates: "Vorlagen anzeigen",
        hideTemplates: "Vorlagen ausblenden",
        sendTemplate: "Vorlage senden",
        openInWhatsApp: "In WhatsApp öffnen",
        openInWhatsAppBusinessHint: "Um eine freie Nachricht zu senden, öffnen Sie die Unterhaltung in der WhatsApp Business App.",
        serviceWindowExpiredDesktopHint: "Das 24-Stunden-Fenster ist abgelaufen. Senden Sie eine Vorlage oder nutzen Sie die WhatsApp Business App für freie Nachrichten.",
        approvedTemplates: "Genehmigte Vorlagen",
        serviceWindowExpiredTitle: "Kundenservice-Fenster abgelaufen",
        serviceWindowExpiredMessage: "Das 24-Stunden-Kundenservice-Fenster ist abgelaufen. Sie können nur genehmigte WhatsApp-Vorlagen senden, bis der Kunde Ihnen erneut schreibt.",
        templateLoadError: "WhatsApp nicht konfiguriert",
        templateLoadErrorMessage: "WhatsApp Business-Integration ist nicht korrekt eingerichtet. Bitte kontaktieren Sie Ihren Administrator.",
        fillTemplateParameters: "Füllen Sie die Vorlagenparameter aus",
        parameter: "Parameter",
        example: "Beispiel",
        enterValue: "Wert eingeben",
        messagePreview: "Vorschau",
        setupPrompt: "WhatsApp verbinden, um Nachrichten zu senden",
        setupLink: "Einrichten",
      },

      conversationMessages: {
        noPhoneNumberTitle: "Keine Telefonnummer",
        noPhoneNumberMessage: "Dieser Kunde hat keine Telefonnummer. Ausgehende Nachrichten sind nicht möglich.",
      },

      capacity: "Kapazität",

      // Common
      save: "Speichern",
      edit: "Bearbeiten",
      delete: "Löschen",
      send: "Senden",
      sending: "Wird gesendet...",
      cancelVisit: "Termin stornieren",
      success: "Erfolg",
      info: "Info",
      actions: "Aktionen",
      copySchedulingLink: "Buchungslink kopieren",

      // AI Chatbot
      chatbotTitle: "KI-Assistent",
      chatbotPlaceholder: "Frag mich etwas...",
      chatbotSend: "Senden",
      chatbotThinking: "Denke...",
      chatbotError: "Entschuldigung, ich habe einen Fehler festgestellt. Bitte versuchen Sie es erneut.",
      chatbotAcceptSuggestion: "Akzeptieren",
      chatbotDeclineSuggestion: "Ablehnen",
      chatbotSuggestionAccepted: "Akzeptiert",
      chatbotSuggestionDeclined: "Abgelehnt",
      chatbotActionConfirm: "Aktion bestätigen",
      chatbotBlockTimeSuccess: "Zeit erfolgreich blockiert",
      chatbotBlockTimeError: "Zeit konnte nicht blockiert werden",
      chatbotSuggestionCreated: "Ich habe einen Zeitblockierungsvorschlag für Sie erstellt.",
      chatbotBlockTimeSuggestion: "Zeitblockierungsvorschlag",
      chatbotTeamMembers: "Teammitglieder",
      chatbotBlockedViaAI: "Blockiert über KI-Assistenten-Vorschlag",
      chatbotUnderstood: "Verstanden. Lassen Sie mich wissen, wenn Sie weitere Hilfe benötigen.",
      chatbotResponseCutOff: "Entschuldigung, meine Antwort wurde abgeschnitten. Könnten Sie bitte Ihre Frage noch einmal stellen?",
      chatbotMalformedRequest: "Entschuldigung, ich hatte ein Problem beim Verarbeiten Ihrer Anfrage. Könnten Sie bitte spezifizieren: für welches Datum und welche Uhrzeit möchten Sie die Zeit blockieren?",
      chatbotNeedMoreInfo: "Ich verstehe Ihre Anfrage, aber ich benötige spezifischere Informationen, um Ihnen richtig zu helfen.",
      chatbotGenericError: "Entschuldigung, ich konnte Ihre Anfrage zu diesem Zeitpunkt nicht bearbeiten.",

      // Messages
      createSuccess: "Erfolgreich erstellt!",
      updateSuccess: "Erfolgreich aktualisiert!",
      deleteSuccess: "Erfolgreich gelöscht!",
      templateDeleted: "Vorlage wurde erfolgreich gelöscht.",
      createError: "Erstellung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      updateError:
        "Aktualisierung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      deleteError: "Löschung fehlgeschlagen. Bitte versuchen Sie es erneut.",

      // Authentication & Login
      loginFailed: "Anmeldung fehlgeschlagen",
      staffLogin: {
        emailOrUsername: "E-Mail oder Benutzername",
        salon: "Salon",
        pickSalon: "Bitte gib deinen Salon ein, um fortzufahren",
        enterSalon: "Name deines Salons",
        invalidCredentials: "Anmeldedaten sind nicht korrekt",
        readOnlyAppointment: "Nur Ansicht — du kannst nur eigene Termine bearbeiten.",
        admin: {
          title: "Login & Zugriff",
          description:
            "Gib diesem Teammitglied einen eigenen Login. Es sieht und verwaltet nur den eigenen Kalender — niemals Postfach, Kunden, Auswertungen oder Einstellungen.",
          usernameLabel: "Benutzername",
          usernamePlaceholder: "z. B. anna",
          usernameHint: "3–30 Zeichen: Kleinbuchstaben, Ziffern, . _ -",
          usernameInvalid:
            "Der Benutzername muss 3–30 Zeichen lang sein und darf nur Kleinbuchstaben, Ziffern sowie . _ - enthalten.",
          passwordLabel: "Passwort",
          newPasswordPlaceholder: "Neues Passwort",
          passwordHint: "Mindestens {{min}} Zeichen.",
          passwordTooShort:
            "Das Passwort muss mindestens {{min}} Zeichen lang sein.",
          createButton: "Login erstellen",
          loginCreated: "Login erstellt",
          loginCreatedBody:
            "Das Teammitglied kann sich jetzt mit Benutzername und Passwort anmelden.",
          resetPasswordLabel: "Passwort zurücksetzen",
          resetPasswordButton: "Zurücksetzen",
          passwordReset: "Passwort zurückgesetzt",
          passwordResetBody: "Das neue Passwort ist sofort aktiv.",
          statusActive: "Aktiv",
          statusInactive: "Deaktiviert",
          activeToggleLabel: "Login aktiv",
          activeToggleHint: "Ausschalten, um diesen Login sofort zu sperren.",
          activated: "Login aktiviert",
          activatedBody: "Das Teammitglied kann sich wieder anmelden.",
          deactivated: "Login deaktiviert",
          deactivatedBody: "Der Login ist sofort gesperrt.",
          deactivateConfirmTitle: "Login deaktivieren?",
          deactivateConfirmBody:
            "Das Teammitglied wird abgemeldet und kann sich nicht mehr anmelden, bis du den Login wieder aktivierst.",
          deactivateConfirmAction: "Deaktivieren",
          scopeLabel: "Mitarbeiter sehen den ganzen Salon-Kalender",
          scopeHint:
            "An: Mitarbeiter sehen alle Termine (nur lesen). Aus: Jeder Mitarbeiter sieht nur die eigenen. Gilt für alle Mitarbeiter-Logins in diesem Salon.",
          scopeSaved: "Einstellung gespeichert",
          scopeSaveError:
            "Einstellung konnte nicht gespeichert werden. Bitte erneut versuchen.",
          genericError: "Etwas ist schiefgelaufen. Bitte erneut versuchen.",
          sectionTitle: "Team & Zugriff",
          sectionDescription: "Lege Logins für dein Team an und verwalte sie. Mitarbeiter sehen nur ihren eigenen Kalender und ihre Kunden; Admins haben Vollzugriff.",
          sectionNavDescription: "Logins, Rollen & Rechte",
          salonScopesTitle: "Was Mitarbeiter sehen",
          customerScopeLabel: "Mitarbeiter dürfen die Kundenliste nutzen",
          customerScopeHint: "An: Mitarbeiter sehen und bearbeiten die ganze Kundenliste. Aus: Mitarbeiter haben keine Kundenliste (Kundennamen auf eigenen Terminen sehen sie weiterhin).",
          membersTitle: "Team-Logins",
          noMembers: "Noch keine Teammitglieder. Lege sie unter Verfügbarkeit → Kalenderprofile an.",
          noLogin: "Kein Login",
          roleLabel: "Rolle",
          roleStaff: "Mitarbeiter (nur eigener Kalender)",
          roleAdmin: "Admin (Vollzugriff)",
          roleChanged: "Rolle aktualisiert",
          noPermission: "Nur Inhaber oder Admins können Team-Zugriffe verwalten.",
          roleBadgeStaff: "Mitarbeiter",
          roleBadgeAdmin: "Admin",
          ownerNote: "Inhaber — Vollzugriff, wird über das eigene Konto verwaltet.",
          nameRequired: "Bitte einen Namen eingeben.",
          addMember: "Mitarbeiterin hinzufügen",
          addMemberTitle: "Neue Mitarbeiterin",
          namePlaceholder: "z. B. Yara",
          createMemberButton: "Anlegen + Login geben",
          addMemberHint: "Standard-Arbeitszeiten werden gesetzt — du kannst sie später unter Verfügbarkeit anpassen.",
        },
      },
      signupFailed: "Registrierung fehlgeschlagen",
      passwordsMustMatch:
        "Bitte stellen Sie sicher, dass Ihre Passwörter übereinstimmen.",
      welcomeBack: "Willkommen zurück!",
      loginSuccessful: "Sie wurden erfolgreich angemeldet.",
      passwordMismatch: "Passwort stimmt nicht überein",
      accountCreated: "Konto erstellt!",
      checkEmailVerification:
        "Bitte überprüfen Sie Ihre E-Mail, um Ihr Konto zu verifizieren.",
      getStarted: "Erste Schritte",
      login: "Anmelden",
      signUp: "Registrieren",
      password: "Passwort",
      confirmPassword: "Passwort bestätigen",
      forgotPassword: "Passwort vergessen?",
      resetPassword: "Passwort zurücksetzen",
      resetPasswordDescription: "Geben Sie Ihre E-Mail-Adresse ein und wir senden Ihnen einen Link zum Zurücksetzen Ihres Passworts.",
      sendResetLink: "Reset-Link senden",
      resetLinkSent: "Reset-Link gesendet",
      checkEmailReset: "Überprüfen Sie Ihre E-Mail für Anweisungen zum Zurücksetzen des Passworts.",
      setPassword: "Passwort festlegen",
      setPasswordDescription: "Wählen Sie ein sicheres Passwort für Ihr Konto.",
      savePassword: "Passwort speichern",
      passwordSet: "Passwort festgelegt",
      passwordSetSuccess: "Ihr Passwort wurde erfolgreich festgelegt.",
      newPassword: "Neues Passwort",
      enterNewPassword: "Neues Passwort eingeben",
      confirmNewPassword: "Neues Passwort erneut eingeben",
      setupPasswordTitle: "Passwort einrichten",
      setupPasswordDescription: "Willkommen! Bitte richten Sie ein sicheres Passwort für Ihr Konto ein, um fortzufahren.",
      passwordMismatchTitle: "Passwörter stimmen nicht überein",
      passwordMismatchDescription: "Die eingegebenen Passwörter stimmen nicht überein. Bitte versuchen Sie es erneut.",
      passwordTooShortTitle: "Passwort zu kurz",
      passwordTooShortDescription: "Ihr Passwort muss mindestens 8 Zeichen lang sein.",
      passwordSetSuccessTitle: "Passwort erfolgreich festgelegt",
      passwordSetSuccessDescription: "Ihr Passwort wurde festgelegt. Sie können nun auf Ihr Konto zugreifen.",
      passwordSetErrorTitle: "Passwort-Einrichtung fehlgeschlagen",
      passwordSetErrorDescription: "Beim Einrichten Ihres Passworts ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.",
      passwordRequirements: "Passwort muss mindestens 8 Zeichen lang sein.",
      settingPassword: "Passwort wird festgelegt...",
      linkExpiredTitle: "Link abgelaufen",
      linkExpiredDescription: "Der in der Willkommensemail versendete Link ist nur eine Zeit lang gültig. Der genutzte Link ist bereits abgelaufen. Fahren Sie bitte auf der Anmeldeseite fort.",
      goToLogin: "Zur Anmeldeseite",
      forgotPasswordLink: "Passwort vergessen?",
      passwordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein.",
      invalidResetToken: "Ungültiger oder abgelaufener Reset-Token. Bitte fordern Sie ein neues Passwort-Reset an.",
      passwordResetFailed: "Passwort-Reset fehlgeschlagen. Bitte versuchen Sie es erneut.",
      backToLogin: "Zurück zur Anmeldung",
      youWillBeRedirectedToTheDashboardShortly: "Sie werden in Kürze zum Dashboard weitergeleitet...",

      // Booking & Appointments
      bookingFailed: "Buchung fehlgeschlagen",
      failedToCreateCustomer:
        "Kunde konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
      failedToCreateAppointment:
        "Termin konnte nicht erstellt werden. Bitte versuchen Sie es erneut.",
      pleaseSelectCustomer: "Bitte wählen Sie einen Kunden aus",
      pleaseSelectDate: "Bitte wählen Sie ein Datum aus",
      pleaseSelectTime: "Bitte wählen Sie eine Uhrzeit aus",
      selectExistingCustomer:
        "Bestehenden Kunden auswählen oder neue Details unten eingeben",
      specialRequestsPlaceholder:
        "Besondere Wünsche, Allergien oder Vorlieben...",
      pleaseTryAgain: "Bitte versuchen Sie es erneut.",
      pleaseSelectServiceType: "Bitte wählen Sie einen Dienstleistungstyp aus",
      failedToSignOut:
        "Abmeldung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      customerNotFound: "Kunde nicht gefunden. Bitte wählen Sie einen gültigen Kunden aus.",
      selectServiceStep: "Dienstleistung auswählen",
      resourceCreated: "Ressource erstellt",
      resourceCreatedSuccessfully: "Ressource erfolgreich erstellt!",

      // Booking Links
      bookingLinks: "Buchungslinks",
      bookingLinksDescription: "Erstellen und teilen Sie Buchungslinks für Ihr Studio, bestimmte Dienstleistungen oder Teammitglieder",
      studioBookingLink: "Studio-Buchungslink",
      serviceBookingLink: "Dienstleistungs-Buchungslink",
      teamMemberBookingLink: "Teammitglied-Buchungslink",
      studioBookingLinkDescription:
        "Link für Kunden, um jede Dienstleistung in Ihrem Studio zu buchen",
      serviceBookingLinkDescription:
        "Direkter Link zur Buchung einer bestimmten Dienstleistung",
      teamMemberBookingLinkDescription:
        "Link zur Buchung mit einem bestimmten Teammitglied",

      bookingLink: "Buchungslink",
      bookingUrl: "Buchungs-URL",
      externalBookingUrl: "Externe Buchungs-URL",
      externalBookingUrlDescription: "Geben Sie die URL Ihres externen Buchungssystems ein. Besucher Ihrer Sylva-Buchungsseite werden dorthin weitergeleitet.",
      externalBookingUrlSaved: "Externe Buchungs-URL erfolgreich gespeichert",
      failedToSaveExternalBookingUrl: "Externe Buchungs-URL konnte nicht gespeichert werden",
      sylvaPortalUrl: "Ihre Sylva-Buchungsseite",
      sylvaPortalUrlDescription: "Dies ist die URL Ihrer Sylva-Buchungsseite. Wenn oben eine externe URL eingestellt ist, werden Besucher dorthin weitergeleitet.",
      copyAllLinks: "Alle Links kopieren",
      allLinksCopied: "Alle Buchungslinks als Markdown kopiert",
      studio: "Studio",
      service: "Dienstleistung",
      uncategorized: "Ohne Kategorie",
      teamMember: "Teammitglied",

      // Languages
      english: "Englisch",
      german: "Deutsch",

      // Templates Module

      manageTemplatesDescription:
        "Verwalten Sie Aufnahmeformulare und E-Mail-Vorlagen für Ihre Termine",
      intakeForms: "Aufnahmeformulare",

      quickActions: "Schnelle Aktionen",
      tasks: "Aufgaben",
      tasksDescription: "Alle Aufgaben aus Konversationen verwalten",
      searchTasks: "Aufgaben suchen...",
      allTasks: "Alle",
      pendingTasks: "Ausstehend",
      completedTasks: "Erledigt",
      noTasksFound: "Keine Aufgaben gefunden",
      task: "Aufgabe",
      createdAt: "Erstellt",
      reopen: "Wieder öffnen",
      taskReopened: "Aufgabe wieder geöffnet",
      failedToReopenTask: "Aufgabe konnte nicht wieder geöffnet werden",
      noPendingTasks: "Keine ausstehenden Aufgaben",
      viewConversation: "Unterhaltung anzeigen",
      markAsDone: "Als erledigt markieren",
      taskCompleted: "Aufgabe erledigt",
      failedToCompleteTask: "Aufgabe konnte nicht erledigt werden",
      taskFor: "Kunde",

      // Intake Forms
      intakeFormsDescription:
        "Sammeln Sie zusätzliche Informationen von Kunden vor Terminen",
      createForm: "Formular erstellen",
      noFormsYet: "Noch keine Formulare",
      createFirstFormDescription:
        "Erstellen Sie Ihr erstes Aufnahmeformular, um Informationen von Kunden zu sammeln",

      searchForms: "Formulare suchen...",
      allTypes: "Alle Typen",
      allStatus: "Alle Status",

      customForm: "Benutzerdefiniertes Formular",
      termsConditions: "Allgemeine Geschäftsbedingungen",
      medicalInformation: "Medizinische Informationen",
      duplicate: "Duplizieren",
      formDuplicated: "Formular dupliziert",
      formDeleted: "Formular gelöscht",

      // Form Builder
      editForm: "Formular bearbeiten",
      createNewForm: "Neues Formular erstellen",
      formSettings: "Formulareinstellungen",

      date: "Datum",
      time: "Zeit",

      noFieldsYet: "Noch keine Felder",
      addFieldsDescription:
        "Fügen Sie Felder aus dem Panel auf der linken Seite hinzu, um Ihr Formular zu erstellen.",

      formNameRequired: "Formularname ist erforderlich",
      formMustHaveFields: "Formular muss mindestens ein Feld haben",
      formCreated: "Formular erstellt",
      formUpdated: "Formular aktualisiert",
      savingForm: "Speichern...",
      creating: "Erstellen...",
      updating: "Aktualisieren...",
      deleting: "Löschen...",
      cancelling: "Stornieren...",
      deletingCustomers: "Kunden werden gelöscht...",
      update: "Aktualisieren",
      saveForm: "Formular speichern",

      // Form Preview

      validationError: "Validierungsfehler",
      pleaseCheckYourInput: "Bitte überprüfen Sie Ihre Eingabe und versuchen Sie es erneut",

      selectAnOption: "Wählen Sie eine Option",

      // Form Builder - Additional Labels
      formNameLabel: "Formularname",
      formDescriptionLabel: "Beschreibung",
      formTypeLabel: "Formulartyp",
      activeLabel: "Aktiv",

      // Form Builder - Placeholders
      formNamePlaceholder: "z.B. Krankengeschichte-Formular",
      formDescriptionPlaceholder: "Kurze Beschreibung dieses Formulars",

      // Form Builder - Type Options
      customFormOption: "Benutzerdefiniertes Formular",
      termsConditionsOption: "Allgemeine Geschäftsbedingungen",
      medicalInformationOption: "Medizinische Informationen",

      // Form Builder - Field Actions
      addFieldLabel: "Feld hinzufügen",
      fieldPropertiesLabel: "Feldeigenschaften",
      labelLabel: "Bezeichnung",
      placeholderLabel: "Platzhalter",
      requiredLabel: "Erforderlich",
      optionsLabel: "Optionen",
      addOptionLabel: "Option hinzufügen",
      minLengthLabel: "Mindestlänge",
      maxLengthLabel: "Maximale Länge",
      submitFormLabel: "Formular absenden",
      selectFieldToEditLabel:
        "Wählen Sie ein Feld aus, um seine Eigenschaften zu bearbeiten",

      // Form List Actions
      previewAction: "Vorschau",
      editAction: "Bearbeiten",
      duplicateAction: "Duplizieren",
      deleteAction: "Löschen",
      createYourFirstFormAction: "Erstellen Sie Ihr erstes Formular",

      // Form List Messages
      noFormsMatchFilter: "Keine Formulare entsprechen dem Filter",
      adjustSearchFilter:
        "Versuchen Sie, Ihre Such- oder Filterkriterien anzupassen",

      // Email Templates
      notificationTemplates: "Benachrichtigungsvorlagen",
      manageAutomatedMessages:
        "Verwalten Sie automatisierte Nachrichten für Termine",
      addTemplate: "Vorlage hinzufügen",
      templateName: "Vorlagenname",
      messageType: "Nachrichtentyp",
      sms: "SMS",

      appointmentConfirmed: "Termin bestätigt",
      appointmentReminder: "Terminerinnerung",
      postAppointmentFollowup: "Nachfassung nach Termin",
      appointmentCancelled: "Termin abgesagt",
      appointmentRescheduled: "Termin verschoben",
      marketingConsentVerification: "Marketing-Einwilligung Bestätigung",
      appointmentCancelledAndNotified:
        "Termin abgesagt und Kunde benachrichtigt",
      appointmentDeletedAndNotified: "Termin gelöscht und Kunde benachrichtigt",
      appointmentRescheduledAndNotified:
        "Termin verschoben und Kunde benachrichtigt",

      completed: "Abgeschlossen",
      cancelled: "Abgesagt",
      no_show: "No-Show",

      templateCreated: "Vorlage erstellt",
      templateUpdated: "Vorlage aktualisiert",
      notificationTemplateUpdatedSuccessfully:
        "Benachrichtigungsvorlage wurde erfolgreich aktualisiert.",
      createTemplate: "Vorlage erstellen",
      updateTemplate: "Vorlage aktualisieren",
      noTemplatesYet: "Noch keine Vorlagen",

      // Template bulk operations
      templateSelected: "Vorlage ausgewählt",
      templatesSelected: "Vorlagen ausgewählt",
      deleteTemplates: "Vorlagen löschen",
      bulkDeleteConfirmation:
        "Sind Sie sicher, dass Sie {{count}} Vorlagen löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      bulkDeleteSuccess: "{{count}} Vorlagen erfolgreich gelöscht",
      bulkDuplicateSuccess: "{{count}} Vorlagen erfolgreich dupliziert",
      bulkDeletePartialFailure:
        "{{failed}} von {{total}} Vorlagen konnten nicht gelöscht werden",
      bulkDuplicatePartialFailure:
        "{{failed}} von {{total}} Vorlagen konnten nicht dupliziert werden",
      clearSelection: "Auswahl aufheben",

      // Template Assignments

      // Form Assignment

      intakeFormAssignedSuccessfully:
        "Aufnahmeformular wurde erfolgreich zugewiesen.",

      // Form Field Types

      // Form Validation

      // Status and Actions

      appointmentDeletedSuccess: "Termin erfolgreich gelöscht",
      appointmentNotFound: "Termin nicht gefunden",
      noResource: "Keine Ressource",
      confirmed: "Bestätigt",
      appointmentNotes: "Terminnotizen",
      updateAppointment: "Termin aktualisieren",
      appointmentSummary: "Terminübersicht",
      noResourcesAvailable: "Keine Ressourcen verfügbar",
      noCategory: "Keine Kategorie",
      warning: "Warnung",
      exportCustomers: "Kunden exportieren",
      emailAddress: "E-Mail-Adresse",
      errorLoadingAppointment: "Fehler beim Laden des Termins",
      failedToCopy: "Kopieren fehlgeschlagen",
      activeUsers: "Aktive Benutzer",

      // Dashboard
      recentServices: "Aktuelle Dienstleistungen",

      createNewService: "Neue Dienstleistung erstellen",
      addNewService: "Neue Terminart hinzufügen",
      addTeamMember: "Teammitglied hinzufügen",
      inviteNewStaffMember: "Neues Teammitglied einladen",
      viewBookings: "Buchungen anzeigen",
      checkTodaysAppointments: "Heutige Termine prüfen",
      noServicesConfigured: "Noch keine Dienstleistungen konfiguriert",

      // Appointment Types
      servicesServices: "Dienstleistungen",
      manageServiceOfferings:
        "Verwalten Sie Ihr Angebot",
      editService: "Dienstleistung bearbeiten",
      editServiceDescription:
        "Dienstleistungs-Informationen und Preisdetails aktualisieren",

      bufferBefore: "Puffer davor (Min)",
      bufferAfter: "Puffer danach (Min)",
      colorHelp:
        "Bestimmt die Blockfarbe im Kalender zur internen Unterscheidung",
      visibility: "Sichtbarkeit",
      private: "Privat",
      isPrivate: "Privat",
      public: "Öffentlich",
      privateHelp: "Diese Dienstleistung ist nur über den direkten Buchungslink der Dienstleistung sichtbar",
      publicHelp: "Diese Dienstleistung ist über alle Buchungslinks sichtbar",
      currency: "Währung",
      serviceDetails: "Dienstleistungs-Details",
      createService: "Dienstleistung erstellen",
      updateService: "Dienstleistung aktualisieren",
      serviceUpdatedSuccess: "Dienstleistung erfolgreich aktualisiert",
      serviceCreatedSuccess: "Dienstleistung erfolgreich erstellt",

      // Required Resources

      resourceAdded: "Ressource hinzugefügt",

      resourceRemoved: "Ressource entfernt",

      failedToUpdateResourceAssignment:
        "Ressourcenzuweisung konnte nicht aktualisiert werden",

      // Multi-select resources
      // Interne Admin-Konsole (nur Sylva-Team)
      adminConsoleTitle: "Admin-Konsole",
      adminConsoleSubtitle: "Alle Mandanten auf einen Blick, samt Schalter zum Abschalten.",
      adminMetricRevenue: "Umsatz (7 Tage)",
      adminMetricNewCustomers: "Neue Kunden (7 Tage)",
      adminMetricNewLeads: "Neue Interessenten (7 Tage)",
      adminMetricNewReviews: "Neue Bewertungen (7 Tage)",
      adminFilterSearch: "Mandant suchen",
      adminFilterStatusAll: "Alle Status",
      adminFilterStatusActive: "Nur aktive",
      adminFilterStatusDeactivated: "Nur deaktivierte",
      adminFilterPackageAll: "Alle Pakete",
      adminChartTitle: "Vergleich",
      adminChartSubtitle: "Top 15 der aktuellen Auswahl",
      adminTableTitle: "Mandanten",
      adminColTenant: "Mandant",
      adminColPackage: "Paket",
      adminColRating: "Bewertung",
      adminColAnswerRate: "Antwortquote",
      adminColLastAppointment: "Letzter Termin",
      adminColLastMessage: "Letzte Nachricht",
      adminColActive: "Aktiv",
      adminBadgeDeactivated: "Deaktiviert",
      adminNoTenants: "Kein Mandant passt zu diesem Filter",
      adminNoAccess: "Dieser Bereich ist dem Sylva-Team vorbehalten.",
      adminTenantDeactivated: "Mandant deaktiviert",
      adminTenantReactivated: "Mandant wieder aktiviert",
      adminToggleFailed: "Umschalten fehlgeschlagen. Einzelheiten stehen in der Browser-Konsole.",
      resourceAssignment: "Ressourcenzuweisung",
      teamMemberAssignment: "Mitarbeiterzuweisung",
      selectTeamMembersForService:
        "Wähle aus, wer diese Dienstleistung durchführt. Mindestens eine Person ist nötig – sonst kann sie online nicht gebucht werden.",
      teamMemberRequired:
        "Weise mindestens ein Teammitglied zu, sonst kann diese Dienstleistung nicht gebucht werden.",

      selectResourcesRequiredForService:
        "Wählen Sie die für diese Dienstleistung erforderlichen Ressourcen aus. Ressourcen sind z.B. Räume, Ausstattung oder Materialien, die für die Durchführung der Dienstleistung benötigt werden.",
      resourceAssignedToService:
        "Ressource wurde dieser Dienstleistung zugewiesen",
      resourceUnassignedFromService:
        "Ressource wurde von dieser Dienstleistung entfernt",

      autoAssignedResources: "Diese Ressourcen werden automatisch zugewiesen",
      noResourcesConfiguredForService:
        "Für diese Dienstleistung sind keine Ressourcen konfiguriert",
      serviceUpdated: "Dienstleistung aktualisiert",
      serviceCreated: "Dienstleistung erstellt",

      somethingWentWrong:
        "Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.",
      noServicesYet: "Noch keine Dienstleistungen",
      createFirstService:
        "Erstellen Sie Ihre erste Terminart, um zu beginnen",
      addFirstService: "Erste Dienstleistung hinzufügen",

      deleteService: "Dienstleistung löschen",

      deleteServiceFailed:
        "Löschen der Dienstleistung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      serviceDeletedSuccessfully: "Dienstleistung erfolgreich gelöscht",
      duplicateServiceFailed:
        "Duplizieren der Dienstleistung fehlgeschlagen. Bitte versuchen Sie es erneut.",
      serviceDuplicatedSuccessfully: "Dienstleistung erfolgreich dupliziert",
      noServicesFound: "Keine Dienstleistungen gefunden",
      servicesFound: "{{count}} Dienstleistungen gefunden",
      showDeletedServices: "Gelöschte Dienstleistungen anzeigen",
      deleted: "Gelöscht",
      serviceSelected: "Dienstleistung ausgewählt",
      servicesSelected: "Dienstleistungen ausgewählt",
      selected: "ausgewählt",
      selectAll: "Alle auswählen",
      deselectAll: "Auswahl aufheben",
      showingEntries: "Zeige {{from}} bis {{to}} von {{total}} Einträgen",
      entriesPerPage: "Einträge pro Seite",
      page: "Seite",
      of: "von",
      previous: "Zurück",
      next: "Weiter",
      bulkDeleteServices: "Dienstleistungen löschen",

      bulkDuplicateServices: "Dienstleistungen duplizieren",

      bulkDeleteServicesFailed:
        "Löschen der Dienstleistungen fehlgeschlagen. Bitte versuchen Sie es erneut.",
      bulkDuplicateServicesFailed:
        "Duplizieren der Dienstleistungen fehlgeschlagen. Bitte versuchen Sie es erneut.",
      duplicating: "Duplizieren...",

      // Categories

      // Team Members
      manageStaffAvailability:
        "Verwalten Sie Ihr Personal und deren Verfügbarkeit",
      editTeamMember: "Teammitglied bearbeiten",

      fullName: "Vollständiger Name",

      weeklyAvailability: "Wöchentliche Verfügbarkeit",
      monday: "Montag",
      tuesday: "Dienstag",
      wednesday: "Mittwoch",
      thursday: "Donnerstag",
      friday: "Freitag",
      saturday: "Samstag",
      sunday: "Sonntag",

      to: "Bis:",
      add: "Hinzufügen",
      teamMemberUpdated: "Teammitglied aktualisiert",
      teamMemberAdded: "Teammitglied hinzugefügt",
      teamMemberDeleted: "Teammitglied gelöscht",
      teamMemberDeletedSuccess: "Teammitglied wurde erfolgreich gelöscht",
      deleteTeamMember: "Teammitglied löschen",
      confirmDeleteTitle: "Löschen bestätigen",
      confirmDeleteTeamMember: "Möchten Sie {{name}} wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.",
      cannotDeleteTeamMemberFutureAppointments: "{{name}} kann nicht gelöscht werden. Es gibt {{count}} zukünftige Termin(e). Bitte stornieren oder verschieben Sie diese Termine zuerst.",
      paused: "Pausiert",
      pauseTeamMember: "Pausieren",
      endPause: "Pause beenden",
      pausedIndefinitely: "Pausiert (bis auf Widerruf)",
      pausedUntil: "Pausiert bis {{date}}",
      pauseDialogTitle: "Teammitglied pausieren",
      pauseDialogDescription: "Während der Pause ist {{name}} auf keinem Kanal für Kunden buchbar und der Login ist deaktiviert. Intern bleibt die Person sichtbar; alle Einstellungen und bestehenden Termine bleiben erhalten.",
      pauseDurationLabel: "Dauer",
      pauseIndefiniteOption: "Bis auf Widerruf",
      pauseUntilOption: "Pausieren bis Datum",
      pauseReasonLabel: "Grund (optional)",
      pauseReasonNone: "Kein Grund",
      pauseReasonVacation: "Urlaub",
      pauseReasonParentalLeave: "Elternzeit",
      pauseReasonSickness: "Krankheit",
      pauseReasonOther: "Sonstiges",
      confirmPause: "Pausieren",
      teamMemberPaused: "Teammitglied pausiert",
      teamMemberPausedSuccess: "{{name}} wurde pausiert",
      teamMemberResumed: "Pause beendet",
      teamMemberResumedSuccess: "{{name}} ist wieder buchbar",
      pauseError: "Teammitglied konnte nicht pausiert werden",
      resumeError: "Pause konnte nicht beendet werden",
      selectServicesDescription:
        "Wählen Sie aus, welche Dienstleistungen dieses Teammitglied anbieten kann",
      noServicesSelectedTitle: "Keine Dienstleistungen ausgewählt",
      noServicesSelectedWarning:
        "Kunden können dieses Teammitglied nicht buchen — weder namentlich noch über „Beliebiges verfügbares Teammitglied\". Intern im Kalender bleibt die Buchung möglich.",
      daysWeek: "Tage/Woche",
      noTeamMembersYet: "Noch keine Teammitglieder",
      addFirstTeamMember:
        "Fügen Sie Ihr erstes Teammitglied hinzu, um mit der Terminplanung zu beginnen",
      addFirstTeamMemberButton: "Erstes Teammitglied hinzufügen",

      // Customers

      editCustomer: "Kunde bearbeiten",
      addNewCustomer: "Neuen Kunden hinzufügen",
      addCustomer: "Kunde hinzufügen",
      phoneNumber: "Telefonnummer",
      phoneRequired: "Telefonnummer *",

      marketingPreferences: "Marketing-Präferenzen",
      marketingPreferencesHint: "Legen Sie fest, wie der Kunde Updates und Angebote erhalten möchte.",
      emailMarketing: "E-Mail",
      emailMarketingExport: "E-Mail-Marketing",
      whatsappMarketing: "WhatsApp",
      whatsappMarketingExport: "WhatsApp-Marketing",
      whatsappNotifications: "WhatsApp-Benachrichtigungen",
      notes: "Notizen",

      customerUpdated: "Kunde aktualisiert",
      customerAdded: "Kunde hinzugefügt",

      // Enhanced Customer Management
      filterAndSort: "Filtern & Sortieren",
      categories: "Kategorien",
      sortBy: "Sortieren nach",
      activeFilters: "Aktive Filter",
      noCategories: "Keine Kategorien verfügbar",
      dateCreated: "Erstellungsdatum",
      dateCreatedNewestFirst: "Erstellungsdatum (neuste zuerst)",
      dateCreatedOldestFirst: "Erstellungsdatum (älteste zuerst)",
      serviceNameAZ: "Servicename (A-Z)",
      serviceNameZA: "Servicename (Z-A)",
      categoryAZ: "Kategorie (A-Z)",
      categoryZA: "Kategorie (Z-A)",
      durationShortestFirst: "Dauer (kürzeste zuerst)",
      durationLongestFirst: "Dauer (längste zuerst)",
      priceLowestFirst: "Preis (niedrigste zuerst)",
      priceHighestFirst: "Preis (höchste zuerst)",
      firstNameAZ: "Vorname (A-Z)",
      firstNameZA: "Vorname (Z-A)",
      lastNameAZ: "Nachname (A-Z)",
      lastNameZA: "Nachname (Z-A)",
      emailAZ: "E-Mail (A-Z)",
      emailZA: "E-Mail (Z-A)",
      phoneAZ: "Telefon (A-Z)",
      phoneZA: "Telefon (Z-A)",
      at: "um",
      createAppointment: "Termin erstellen",
      scheduleNewAppointment: "Einen neuen Termin planen",
      markTimeAsUnavailable: "Zeit als nicht verfügbar markieren",

      exportSelected: "Ausgewählte exportieren",
      deleteSelected: "Ausgewählte löschen",
      exportAll: "Alle exportieren",
      importCSV: "CSV importieren",
      viewProfile: "Profil anzeigen",
      scheduleAppointment: "Termin planen",
      marketing: "Marketing",

      confirmDeleteCustomer:
        "Sind Sie sicher, dass Sie diesen Kunden löschen möchten?",

      bulkDeleteCustomers: "Kunden löschen",

      deleteCustomer: "Kunde löschen",

      customerDeleted: "Kunde gelöscht",
      customerDeletedDescription: "Kunde wurde erfolgreich gelöscht.",
      customersDeleted: "Kunden gelöscht",
      customersDeletedDescription:
        "{{count}} Kunden wurden erfolgreich gelöscht.",

      customerUpdatedDescription:
        "Kundendaten wurden erfolgreich aktualisiert.",
      customerAddedDescription: "Neuer Kunde wurde erfolgreich hinzugefügt.",
      duplicateCustomerError: "Ein Kunde mit dieser Telefonnummer und diesem Vornamen existiert bereits",
      duplicateCustomerErrorWithName: "Kunde {{name}} existiert bereits mit dieser Telefonnummer und diesem Vornamen",

      // Missing customer-related keys
      customersFound: "{{count}} Kunden gefunden",
      customersFoundWithTotal: "{{filtered}} von {{total}} Kunden gefunden",
      filterBy: "Filtern nach",
      allCustomers: "Alle Kunden",
      leadsOnly: "Nur Interessenten",
      customersWithAppointments: "Kunden mit Terminen",
      lead: "Interessent",
      birthdaysNext7Days: "Geburtstag in den nächsten 7 Tagen",
      customerType: "Typ",
      customerTypes: "Kundentypen",
      clearAll: "Alle Filter aufheben",
      selectedCustomers: "{{count}} Kunden ausgewählt",
      customerSelected: "Kunde ausgewählt",
      customersSelected: "Kunden ausgewählt",
      deleteCustomerConfirmation:
        "Sind Sie sicher, dass Sie {{name}} löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      bulkDeleteCustomersConfirmation:
        "Sind Sie sicher, dass Sie {{count}} Kunden löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",

      customerProfile: "Kundenprofil",

      notesPlaceholder:
        "Kundenpräferenzen, Allergien, besondere Anforderungen...",
      customer: "Kunde",

      // Customer Profile
      customerDetails: "Kundendetails",
      customerData: "Kundendaten",
      appointmentHistory: "Terminhistorie",
      registrationForms: "Anmeldeformulare",
      registrationFormsPlaceholder: "Noch keine Anmeldeformulare ausgefüllt",
      registrationFormsDescription: "Kundenaufnahme- und Anmeldeformulare",
      basicInformation: "Grundinformationen",
      internalNotes: "Interne Notizen",
      emailMarketingDescription: "E-Mail-Marketing-Kommunikation erlauben",
      whatsappMarketingDescription: "WhatsApp-Marketing-Kommunikation erlauben",
      optedIn: "Angemeldet",
      optedOut: "Abgemeldet",
      customerInformation: "Kundeninformationen",
      accountInformation: "Kontoinformationen",
      customerSince: "Kunde seit",
      lastUpdated: "Zuletzt aktualisiert",
      customerStatus: "Kundenstatus",

      noPastAppointments: "Keine vergangenen Termine",
      appointmentHistoryWillAppearHere: "Terminhistorie wird hier angezeigt",
      rescheduleAppointment: "Termin umplanen",
      deleteAppointment: "Termin löschen",
      markAsNoShow: "No-Show markieren",
      unmarkNoShow: "Kein No-Show",
      appointmentMarkedAsNoShow: "Termin als No-Show markiert",
      appointmentUnmarkedAsNoShow: "Termin nicht mehr als No-Show markiert",
      rescheduleFeatureComingSoon: "Umplanungsfunktion kommt bald",
      deleteFeatureComingSoon: "Löschfunktion kommt bald",
      selectNewDateTime: "Neues Datum & Zeit auswählen",
      currentAppointment: "Aktueller Termin",
      rescheduleSuccessful: "Termin erfolgreich umgeplant",
      appointmentRescheduledSuccessfully: "Ihr Termin wurde erfolgreich umgeplant. Sie erhalten in Kürze eine Bestätigungs-E-Mail.",
      noAvailableSlots: "Keine verfügbaren Termine im ausgewählten Datumsbereich",
      rescheduleError: "Termin konnte nicht umgeplant werden",
      goBack: "Zurück",

      planAppointment: "Termin planen",

      print: "Drucken",
      customerUpdatedSuccessfully: "Kunde erfolgreich aktualisiert",
      customerUpdateFailed: "Kunde konnte nicht aktualisiert werden",
      unsavedChanges: "Ungespeicherte Änderungen",
      changesSaved: "Änderungen gespeichert",

      customerDeletedSuccessfully: "Kunde erfolgreich gelöscht",
      customerDeleteFailed: "Kunde konnte nicht gelöscht werden",
      cannotDeleteCustomer: "Kunde kann nicht gelöscht werden",
      customerHasFutureAppointments: "Kunde hat zukünftige Termine. Bitte stornieren oder verschieben Sie zukünftige Termine vor dem Löschen des Kunden.",
      cannotDeleteSomeCustomers: "Einige Kunden können nicht gelöscht werden",
      someCustomersHaveFutureAppointments: "Einige Kunden haben zukünftige Termine. Bitte stornieren oder verschieben Sie zukünftige Termine vor dem Löschen der Kunden.",
      printWindowBlocked: "Druckfenster wurde vom Browser blockiert",
      editFeatureAvailableInDataTab:
        "Bearbeitungsfunktion verfügbar im Daten-Tab",

      // Review Request
      requestReview: "Bewertung anfragen",
      requestReviewConfirmation: "Bewertungsanfrage per E-Mail an {{name}} ({{email}}) senden?",
      sendReviewRequest: "Anfrage senden",
      reviewRequestSent: "Bewertungsanfrage erfolgreich gesendet",
      reviewRequestFailed: "Bewertungsanfrage konnte nicht gesendet werden",
      customerHasNoEmail: "Kunde hat keine E-Mail-Adresse",
      lastReviewRequestSent: "Letzte Bewertungsanfrage gesendet am {{date}}",
      googleSetupRequired: "Google-Einrichtung erforderlich",
      reviewAlreadyRequested: "Bewertung bereits angefragt",
      errorLoadingAppointments: "Fehler beim Laden der Termine",
      noUpcomingAppointments: "Keine bevorstehenden Termine",
      scheduleFirstAppointment: "Ersten Termin planen",

      // CSV Import/Export
      exportScope: "Export-Umfang",
      exportAllCustomers: "Alle Kunden",
      exportSelectedCustomers: "Nur ausgewählte Kunden",
      includeFields: "Felder einschließen",
      createdDate: "Erstellungsdatum",

      noCustomersToExport: "Keine Kunden zum Exportieren",
      exportSuccess: "Export erfolgreich",

      exportError: "Export fehlgeschlagen",
      exportErrorDescription:
        "Kunden konnten nicht exportiert werden. Bitte versuchen Sie es erneut.",
      importCustomers: "Kunden importieren",
      importServices: "Dienstleistungen importieren",
      importKeywords: "Keywords importieren",
      csvServiceImportInstructions:
        "Stellen Sie sicher, dass Ihre CSV-Datei Titel- und Dauer-Spalten enthält. Beschreibung, Kategorie, Preis und andere Felder sind optional.",
      csvKeywordImportInstructions:
        "Stellen Sie sicher, dass Ihre CSV-Datei Begriff- und Suchtyp-Spalten enthält. Suchtyp muss 'organic' oder 'local_finder' sein.",
      importingServices: "Importiere Dienstleistungen",
      importingKeywords: "Importiere Keywords",
      importXServices: "{{count}} Dienstleistung(en) importieren",
      importXKeywords: "{{count}} Keyword(s) importieren",
      selectCSVFile: "CSV-Datei auswählen",
      dragDropOrClick: "CSV-Datei hier ablegen oder klicken zum Auswählen",
      csvImportInstructions:
        "Stellen Sie sicher, dass Ihre CSV-Datei Vorname- und Nachname-Spalten enthält. E-Mail, Telefon und andere Felder sind optional. Gerne können Sie unsere Vorlage unten für den Upload verwenden.",
      downloadTemplate: "Vorlage herunterladen",
      importPreview: "Import-Vorschau",
      validRecords: "gültige(r) Datensatz/Datensätze",
      errors: "Fehler",
      empty: "leer",
      importErrorsFound:
        "Einige Datensätze haben Fehler und werden beim Import übersprungen.",
      row: "Zeile",

      selectDifferentFile: "Andere Datei auswählen",

      importingCustomers: "Kunden importieren",
      importingCustomerXOfY: "Importiere Kunde {{current}} von {{total}}: {{name}}",
      importProgress: "Fortschritt",
      importComplete: "Import abgeschlossen",
      importCompleteDescription:
        "Ihre Kunden wurden erfolgreich importiert.",
      importSuccessWithDuplicates: "{{successful}} Kunden erfolgreich importiert. {{duplicates}} Duplikate wurden übersprungen.",
      importSuccessful: "{{count}} Kunden erfolgreich importiert",
      importDuplicatesSkipped: "{{count}} doppelte Kunden übersprungen",
      importFailed: "{{count}} Kunden konnten nicht importiert werden",
      done: "Fertig",
      invalidFileType: "Ungültiger Dateityp",
      pleaseSelectCSVFile: "Bitte wählen Sie eine CSV-Datei aus.",
      fileParsingError: "Datei-Parsing-Fehler",
      fileParsingErrorDescription:
        "Die CSV-Datei konnte nicht verarbeitet werden. Bitte überprüfen Sie das Format.",
      importSuccess: "Import erfolgreich",
      enterValidValue: "Gültigen Wert eingeben",
      applyEdits: "Änderungen anwenden",
      editsApplied: "Änderungen angewendet",
      dataRevalidated: "Daten wurden mit Ihren Änderungen erneut validiert",

      importError: "Import fehlgeschlagen",
      importErrorDescription:
        "Kunden konnten nicht importiert werden. Bitte versuchen Sie es erneut.",

      // CSV Validierungs-Nachrichten
      csvValidation: {
        invalidEmailFormat: "Ungültiges E-Mail-Format",
        invalidPhoneFormat: "Ungültiges Telefonformat",
        invalidDateFormat: "Ungültiges Datumsformat",
        invalidDate: "Ungültiges Datum",
        invalidGender: "Ungültiger Geschlechtswert. Verwenden Sie: m/f/d oder männlich/weiblich/divers",
        fieldRequired: "Dieses Feld ist erforderlich",
        invalidDuration: "Dauer muss eine positive Zahl sein",
        invalidPrice: "Preis muss eine positive Zahl sein",
        invalidBuffer: "Pufferzeit muss eine positive Zahl sein",
        invalidSearchType: "Suchtyp muss 'organic' oder 'local_finder' sein"
      },

      // CSV Vorlage
      csvTemplate: {
        filename: "kunden_import_vorlage",
        yes: "ja",
        no: "nein",
        prefersAfternoon: "Bevorzugt Nachmittagstermine",
        allergicNote: "Allergisch gegen bestimmte Produkte",
        serviceFilename: "dienstleistungen_import_vorlage",
        keywordFilename: "keyword_import_vorlage",
        serviceExamples: {
          massage: "Entspannungsmassage",
          massageDesc: "60-minütige Ganzkörper-Entspannungsmassage",
          wellness: "Wellness",
          facial: "Gesichtsbehandlung",
          facialDesc: "Tiefenreinigung mit feuchtigkeitsspendender Gesichtsbehandlung",
          skincare: "Hautpflege"
        }
      },

      // Resources
      manageRoomsEquipment:
        "Verwalten Sie Räume, Geräte und andere buchbare Ressourcen",
      addResource: "Ressource hinzufügen",
      editResource: "Ressource bearbeiten",
      createNewResource: "Neue Ressource erstellen",
      resourceName: "Ressourcenname",
      resourceNamePlaceholder: "z.B. Massage Raum A, Laser Gerät",

      briefDescription: "Kurze Beschreibung",
      resourceUpdated: "Ressource aktualisiert",
      resourceUpdatedSuccess: "Die Ressource wurde erfolgreich aktualisiert.",
      deleteResource: "Ressource löschen",
      resourceDeleted: "Ressource gelöscht",
      resourceDeletedSuccess: "Die Ressource wurde erfolgreich gelöscht.",
      deleteResourceConfirmation: "Sind Sie sicher, dass Sie diese Ressource löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",

      // Resource Details and Dienstleistungen Tabs
      resourceDetails: "Ressourcen-Details",
      assignedServices: "Zugewiesene Dienstleistungen",

      // Improved Capacity Description
      capacityExplanation:
        "Die Kapazität einer Ressource gibt an, wie viele Termine gleichzeitig stattfinden können, die diese Ressource benötigen.",

      // Legacy translations (keeping for compatibility)

      selectServicesForResource:
        "Dienstleistungen, die diese Ressource benötigen:",
      chooseWhichServicesCanUseThisResource:
        "Dienstleistungen, die diese Ressource benötigen",
      selectServicesForTemplate: "Dienstleistungen für diese Vorlage auswählen",
      capacityLabel: "Kapazität:",
      availableFor: "Benötigt für:",
      noServicesAssigned: "Keine Dienstleistungen zugewiesen",
      servicesUpdated: "Services aktualisiert",
      serviceAssignmentsUpdated:
        "Service-Zuweisungen wurden erfolgreich aktualisiert.",
      failedToUpdateServiceAssignment:
        "Fehler beim Aktualisieren der Service-Zuweisung.",

      // Common terms
      resource: "Ressource",

      // KPI Tiles
      websiteVisits: "Website-Besuche",
      googleBusinessViews: "Google Business Aufrufe",
      weeklyCustomers: "Neue Kunden (7 Tage)",
      weeklyLeads: "Neue Interessenten (7 Tage)",
      weeklyRevenue: "Umsatz (7 Tage)",
      totalLeads: "Interessenten",
      weeklyWebsiteVisits: "Website Besucher (7 Tage)",
      leadsDescription: "Interessenten haben in der Buchungsmaske ihre Daten hinterlassen, aber noch keinen Termin bei Ihnen gebucht. Wir empfehlen Interessenten zu kontaktieren.",
      vsLastWeek: "vs. Vorwoche",

      // KPI Tooltips
      websiteVisitsTooltip: "Anzahl der Personen, die Ihre Website besucht haben.",
      googleBusinessViewsTooltip: "Wie oft haben Personen Ihr Google Business-Profil angesehen.",
      totalCustomersTooltip: "Gesamtzahl der Kunden, die Termine bei Ihnen gebucht haben.",
      weeklyCustomersTooltip: "Neu gewonnene Kunden der letzten 7 Tage – Kontakte, die in den letzten 7 Tagen neu hinzukamen und (mindestens) einen Termin gebucht haben.",
      totalLeadsTooltip: "Personen, die Interesse gezeigt haben, aber noch nicht gebucht haben.",
      weeklyLeadsTooltip: "Neue Interessenten der letzten 7 Tage – Personen, die Interesse gezeigt, aber noch keinen Termin gebucht haben. Tipp: Sprich sie an, um sie zu Kunden zu machen. Klick auf die Kachel öffnet die Liste.",
      weeklyRevenueTooltip: "Umsatz der letzten 7 Tage im Vergleich zum vorherigen 7-Tage-Zeitraum.",
      weeklyWebsiteVisitsTooltip: "Anzahl der Website-Besucher in den letzten 7 Tagen im Vergleich zu den 7 Tagen davor.",
      weeklyLeadsMessaged: "Von Sylvia kontaktiert (7 Tage)",
      weeklyLeadsMessagedTooltip: "Anzahl der Interessenten, denen Sylvia in den letzten 7 Tagen die Willkommensnachricht gesendet hat.",
      weeklyConversationsEngaged: "Antworten erhalten (7 Tage)",
      weeklyConversationsEngagedTooltip: "Anzahl der Interessenten, die geantwortet haben, nachdem Sylvia ihnen die Willkommensnachricht gesendet hat.",

      // Acquisition Chart
      acquisitionChart: "Kontakte gewonnen",
      acquisitionChartDescription: "Neue Kontakte im Zeitverlauf, aufgeteilt nach Buchungsstatus",
      booked: "Kunden",
      notBooked: "Interessenten",
      noAcquisitionData: "Noch keine Daten",
      noAcquisitionDataDescription: "Daten erscheinen hier, sobald Sie Kontakte gewinnen",
      daily: "Täglich",
      weekly: "Wöchentlich",
      timeRange: "Zeitraum",
      last7Days: "7 Tage",
      last30Days: "30 Tage",
      last90Days: "90 Tage",
      last12Months: "12 Monate",
      allTime: "Alles",

      // Calendar
      searchAppointments: "Termine suchen...",
      filter: "Filter",
      filters: "Filter",
      day: "Tag",
      blockTime: "Zeit blockieren",
      blockTimeDescription: "Blockieren Sie einen Zeitraum, um zu verhindern, dass neue Termine geplant werden",
      editBlockedTime: "Blockierte Zeit bearbeiten",
      editBlockedTimeDescription: "Ändern Sie die Details dieser blockierten Zeitperiode",
      blockedPeriodCreatedSuccessfully: "Blockierte Zeit erfolgreich erstellt",
      blockedPeriodUpdatedSuccessfully: "Blockierte Zeit erfolgreich aktualisiert",
      blockedPeriodDeletedSuccessfully: "Blockierte Zeit erfolgreich gelöscht",
      blockedPeriodMoved: "Blockierte Zeit erfolgreich verschoben",
      failedToMoveBlockedPeriod: "Fehler beim Verschieben der blockierten Zeit",
      confirmDeleteBlockedPeriod:
        "Sind Sie sicher, dass Sie diese blockierte Zeit löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      deleteBlockedPeriodTitle: "Blockierte Zeit löschen",
      appointmentConflictWarning: "Terminkonflikt-Warnung",
      blockTimeConflictDescription: "Es gibt bestehende Termine, die mit dem Zeitraum überschneiden, den Sie blockieren möchten.",
      conflictWarningMessage: "{{count}} Termin(e) am {{date}} gefunden, die sich mit Ihrer blockierten Zeit überschneiden:",
      conflictActionRequired: "Bitte stellen Sie sicher, dass Sie betroffene Kunden benachrichtigen oder ein anderes Teammitglied für die Übernahme dieser Termine arrangieren.",
      proceedAnyway: "Trotzdem fortfahren",
      errorCheckingConflicts: "Fehler beim Überprüfen von Konflikten. Bitte versuchen Sie es erneut.",
      addAppointment: "Termin hinzufügen",
      editAppointment: "Termin bearbeiten",
      noTeamMembers: "Keine Teammitglieder verfügbar",

      // Time Blocking
      blockTitle: "Titel",
      blockTitleRequired: "Titel ist erforderlich",
      dateRequired: "Datum ist erforderlich",
      timeRequired: "Zeit ist erforderlich",
      endTimeAfterStartTime: "Endzeit muss nach Startzeit liegen",
      blockTitlePlaceholder: "z.B. Mittagspause, Team-Meeting",
      blockDescriptionPlaceholder:
        "Zusätzliche Details zu dieser blockierten Zeit...",
      periodType: "Zeitraumtyp",
      selectPeriodType: "Zeitraumtyp auswählen",
      break: "Pause",
      holiday: "Feiertag",
      unavailable: "Nicht verfügbar",
      maintenance: "Wartung",
      partOfMultiTeamMemberVisit: "Teil eines Besuchs mit mehreren Teammitgliedern",
      cannotDragMultiTeamMemberVisit: "Dieser Besuch hat mehrere Teammitglieder und kann nicht verschoben werden. Bitte bearbeiten Sie den Besuch, um ihn umzuplanen.",
      startTime: "Startzeit",
      endTime: "Endzeit",
      appliesTo: "Gilt für",
      selectAppliesTo: "Auswählen, für wen dies gilt",
      allTeamMembersAndResources: "Alle Teammitglieder und Ressourcen",
      allTeamMembers: "Alle Teammitglieder",
      specificTeamMembers: "Bestimmte Teammitglieder",
      specificResources: "Bestimmte Ressourcen",
      selectTeamMembers: "Teammitglieder auswählen",
      selectResources: "Ressourcen auswählen",
      selectAllMembers: "Alle auswählen",
      selectAtLeastOneTeamMember: "Bitte wählen Sie mindestens ein Teammitglied aus",

      // Calendar - Months

      // Calendar - Day abbreviations

      // Drag and Drop
      appointmentUpdated: "Termin aktualisiert",
      appointmentCreated: "Termin erfolgreich erstellt",
      appointmentConflict:
        "Terminkonflikt: Der gewählte Zeitraum ist bereits belegt",
      failedToUpdateAppointment: "Fehler beim Aktualisieren des Termins",
      appointmentDeleted: "Termin erfolgreich gelöscht",
      failedToDeleteAppointment: "Fehler beim Löschen des Termins",
      confirmDeleteAppointment:
        "Sind Sie sicher, dass Sie diesen Termin löschen möchten? Der Kunde wird benachrichtigt und diese Aktion kann nicht rückgängig gemacht werden.",
      deleteAppointmentTitle: "Termin löschen",
      confirmCancelVisit:
        "Sind Sie sicher, dass Sie diesen Termin stornieren möchten? Alle Dienstleistungen in diesem Termin werden storniert und der Kunde wird benachrichtigt.",
      cancelNotificationEnabled: "Der Kunde wird über die Stornierung benachrichtigt.",
      cancelNotificationDisabled: "Der Kunde wird nicht benachrichtigt.",
      cancelVisitTitle: "Termin stornieren",
      visitUpdated: "Termin erfolgreich aktualisiert",
      failedToUpdateVisit: "Fehler beim Aktualisieren des Termins",
      visitCancelled: "Termin erfolgreich storniert",
      failedToCancelVisit: "Fehler beim Stornieren des Termins",

      // Conflict Detection
      teamMemberConflict: "Teammitglied ist zu dieser Zeit bereits eingeplant",
      teamMemberScheduledConflict: "{{teamMemberName}} ist bereits zu dieser Zeit für einen anderen Termin eingeplant",
      resourceCapacityConflict: "Ressource ist voll ausgelastet",
      resourceCapacityConflictDetailed: "{{resourceName}} ist voll ausgelastet ({{capacity}} Termine)",

      // Appointment Reschedule Confirmation
      confirmReschedule: "Umplanung bestätigen",
      confirmRescheduleMessage:
        "Sind Sie sicher, dass Sie diesen Termin umplanen möchten? Der Kunde wird über die Zeitänderung benachrichtigt.",
      rescheduleNotificationEnabled: "Der Kunde wird benachrichtigt.",
      rescheduleNotificationDisabled: "Der Kunde wird nicht benachrichtigt.",
      createNotificationEnabled: "Der Kunde wird über den neuen Termin benachrichtigt.",
      createNotificationDisabled: "Der Kunde wird nicht benachrichtigt.",
      notificationEnabled: "Benachrichtigung aktiviert",
      notificationDisabled: "Keine Benachrichtigung",
      currentTime: "Aktuelle Zeit",
      newTime: "Neue Zeit",
      appointmentTime: "Terminzeit",
      schedulingConflicts: "Terminplanungskonflikte",
      schedulingWarnings: "Terminplanungswarnungen",
      teamMemberServiceWarning: "{{teamMemberName}} bietet die folgenden Leistungen nicht an: {{serviceNames}}",
      cannotReschedule: "Umplanung nicht möglich",
      confirmAppointmentCreation: "Terminerstellung bestätigen",
      cannotCreate: "Kann nicht erstellt werden",
      confirmCreate: "Bestätigen & Erstellen",
      confirmAppointmentUpdate: "Terminänderung bestätigen",
      cannotUpdate: "Kann nicht aktualisiert werden",
      confirmUpdate: "Bestätigen & Aktualisieren",
      updateNotificationEnabled: "Der Kunde wird über die Terminänderungen benachrichtigt.",
      updateNotificationDisabled: "Der Kunde wird nicht benachrichtigt.",
      serviceChanges: "Leistungsänderungen",
      teamMemberChanges: "Teammitglied-Änderungen",
      added: "Hinzugefügt",
      removed: "Entfernt",
      noChangesDetected: "Keine Änderungen erkannt",
      conflictValidationError:
        "Terminkonflikte können nicht validiert werden. Bitte versuchen Sie es erneut.",
      resourceNotFound: "Ressource nicht gefunden",

      // Appointment Modal
      selectCustomer: "Kunde auswählen",
      selectService: "Dienstleistung auswählen",
      selectTeamMember: "Teammitglied auswählen",
      selectTeamMemberImperativ: "Wählen Sie ein Teammitglied",
      pleaseSelectTeamMember: "Bitte wählen Sie ein Teammitglied aus",
      noTeamMembersAvailable: "Keine Teammitglieder verfügbar",
      noTeamMembersForService:
        "Keine Teammitglieder können diese Dienstleistung anbieten",
      noTeamMembersAvailableForService:
        "Keine Teammitglieder verfügbar für diese Dienstleistung",
      selectResource: "Ressource auswählen",

      appointmentNotesPlaceholder: "Zusätzliche Notizen für diesen Termin...",

      // Time units

      beforeAppointment: "vor Termin",

      // Email Template Creation Form
      createNewTemplate: "Neue Vorlage erstellen",
      editTemplate: "Vorlage bearbeiten",
      templateNameLabel: "Vorlagenname",
      templateNamePlaceholder: "z.B. Terminbestätigungs-E-Mail",
      messageTypeLabel: "Nachrichtentyp",
      emailOption: "E-Mail",
      triggerEventLabel: "Auslöser-Ereignis",
      emailSubjectLabel: "E-Mail-Betreff",
      emailSubjectPlaceholder: "Betreffzeile für die E-Mail",
      messageContentLabel: "Nachrichteninhalt",
      messageContentPlaceholder: "Schreiben Sie Ihre Nachricht hier...",
      availablePlaceholdersLabel: "Verfügbare Platzhalter",
      creatingTemplate: "Erstellen...",
      updatingTemplate: "Aktualisieren...",

      // Template Assignment Form

      // Template Assignment Tabs

      // Template Assignment Validation

      // Template Assignment Step

      saveAndAssign: "Speichern & Zuweisen",
      createAndAssign: "Erstellen & Zuweisen",

      // Email Template Validation
      templateNameRequired: "Vorlagenname ist erforderlich",
      emailSubjectRequired: "E-Mail-Betreff ist erforderlich",
      templateMustHaveContent: "Vorlage muss Text- oder HTML-Inhalt haben",

      templateCopySuffix: " (Kopie)",
      copyOf: "Kopie von",

      // Template Content Labels
      subjectLabel: "Betreff:",
      messageLabel: "Nachricht:",

      // Placeholder Translations
      placeholderCustomerName: "Name des Kunden",
      placeholderCustomerEmail: "E-Mail des Kunden",
      placeholderCustomerPhone: "Telefon des Kunden",
      placeholderAppointmentDate: "Termin-Datum",
      placeholderAppointmentTime: "Termin-Zeit",
      placeholderService: "Terminart",
      placeholderTeamMemberName: "Name des Teammitglieds",
      placeholderBusinessName: "Geschäftsname",
      placeholderBusinessAddress: "Geschäftsadresse",
      placeholderBusinessPhone: "Geschäfts-Telefon",
      placeholderBusinessEmail: "Geschäfts-E-Mail",

      // Notification Preferences

      settingsUpdated: "Einstellungen aktualisiert",

      notificationTypeUpdated: "Benachrichtigungseinstellung aktualisiert",

      failedToUpdateSettings:
        "Einstellungen konnten nicht aktualisiert werden. Bitte versuchen Sie es erneut.",
      loadingSettings: "Lade Einstellungen",
      noPreferencesFound: "Keine Einstellungen gefunden",
      preferencesWillBeCreated:
        "Benachrichtigungseinstellungen werden automatisch erstellt",
      savingSettings: "Speichere Einstellungen",
      manageNotificationSystem:
        "Verwalten Sie Ihre Benachrichtigungseinstellungen und E-Mail-Vorlagen",
      adminNotificationSettings:
        "Verwalten Sie, wann Sie über Geschäftsereignisse benachrichtigt werden",
      preferences: "Einstellungen",

      // Admin Notification Settings
      emailNotifications: "E-Mail-Benachrichtigungen",
      receiveEmailNotifications: "E-Mail-Benachrichtigungen erhalten",
      enableDisableEmailNotifications:
        "E-Mail-Benachrichtigungen für Geschäftsereignisse aktivieren oder deaktivieren",
      adminNotificationTypes: "Benachrichtigungsarten",
      neuerTermin: "Neuer Termin",
      neuerTerminDescription: "Benachrichtigung bei neuen Terminbuchungen",
      terminVerschoben: "Termin verschoben",
      terminVerschobenDescription: "Benachrichtigung bei Terminverschiebungen",
      terminAbgesagt: "Termin abgesagt",
      terminAbgesagtDescription: "Benachrichtigung bei Terminabsagen",
      notificationScope: "Benachrichtigungsbereich",
      whichAppointments:
        "Für welche Termine möchten Sie benachrichtigt werden?",
      allAppointmentsDescription:
        "Benachrichtigungen für alle Termine in Ihrem Unternehmen erhalten",
      assignedAppointmentsOnly: "Nur zugewiesene Termine",
      assignedAppointmentsOnlyDescription:
        "Nur für Ihnen zugewiesene Termine benachrichtigt werden",

      emailAddressOverride: "Alternative E-Mail-Adresse",
      emailAddressOverrideDescription:
        "Leer lassen, um Ihre Konto-E-Mail-Adresse zu verwenden",
      useAccountEmail: "Konto-E-Mail verwenden",
      notificationScopeUpdated: "Benachrichtigungsbereich aktualisiert",
      emailNotificationsEnabled: "E-Mail-Benachrichtigungen aktiviert",
      emailNotificationsDisabled: "E-Mail-Benachrichtigungen deaktiviert",

      // Public Booking Page
      loadingBookingOptions: "Buchungsoptionen werden geladen...",
      bookingNotAvailable: "Buchung nicht verfügbar",
      pleaseCheckUrl:
        "Bitte überprüfen Sie die URL oder kontaktieren Sie das Unternehmen direkt.",
      selectYourService: "Wählen Sie eine Dienstleistung",
      selectPreferredDateTime:
        "Wählen Sie Ihr bevorzugtes Datum und Ihre bevorzugte Uhrzeit",
      selectDateTime: "Termin auswählen",
      selectDateTimeImperativ: "Wählen Sie einen Termin",
      yourDetails: "Ihre Daten",
      haveYouVisitedUsBefore: "Waren Sie schon einmal bei uns?",
      itWillBeMyFirstTime: "Es wird mein erstes Mal sein",
      ihaveVisitedBefore: "Ich war schon einmal da",
      continue: "Weiter",
      failedToSaveCustomerData: "Fehler beim Speichern der Kundendaten. Bitte versuchen Sie es erneut.",
      chooseYourService: "Wählen Sie eine Dienstleistung",
      chooseTheServiceYouWant:
        "Wählen Sie die Dienstleistung, die Sie buchen möchten",
      chooseServiceYouWantToBook:
        "Wählen Sie die Dienstleistung, die Sie buchen möchten",
      chooseYourPreferredTeamMember: "Wählen Sie Ihr bevorzugtes Teammitglied",
      chooseYourPreferredDateTime:
        "Wählen Sie Ihr bevorzugtes Datum und Uhrzeit",
      enterYourContactDetails: "Geben Sie Ihre Kontaktdaten ein",
      almostDone: "Fast geschafft! Bitte geben Sie Ihre Kontaktdaten an",

      loadingAvailableTimes: "Verfügbare Zeiten werden geladen...",
      noAvailableTimesDate: "Keine verfügbaren Zeiten für dieses Datum.",
      pleaseSelectDifferentDate: "Bitte wählen Sie ein anderes Datum.",
      completeYourBooking: "Vervollständigen Sie Ihre Buchung",
      justFewDetailsAllSet: "Nur noch wenige Angaben und Sie sind startklar!",
      backToDateSelection: "Zurück zur Datumsauswahl",
      bookingSummary: "Buchungsübersicht",
      bookingConfirmed: "Buchung bestätigt",

      appointmentScheduledSuccessfully: "Ihr Termin wurde erfolgreich geplant!",
      appointmentDetails: "Termindetails",
      contactInformation: "Kontaktinformationen",
      whatsNext: "Wie geht es weiter?",
      confirmationEmailSent:
        "Eine Bestätigungs-E-Mail wurde an Ihre E-Mail-Adresse gesendet",
      addToCalendarReminder:
        "Fügen Sie diesen Termin zu Ihrem Kalender für Erinnerungen hinzu",
      contactForChanges:
        "Kontaktieren Sie uns, wenn Sie Änderungen an Ihrem Termin vornehmen müssen",

      with: "mit",
      firstNameRequired: "Vorname *",
      lastNameRequired: "Nachname *",
      emailRequired: "E-Mail *",
      phoneNumberOptional: "Telefonnummer",
      additionalNotesOptional: "Zusätzliche Notizen (Optional)",
      receiveAppointmentReminders:
        "Ich möchte Terminerinnerungen und gelegentliche Updates per E-Mail oder Whatsapp erhalten",
      confirmBooking: "Buchung bestätigen",
      creatingBooking: "Buchung wird erstellt...",
      yourInformation: "Ihre Informationen",
      firstName: "Vorname",
      lastName: "Nachname",
      enterYourFirstName: "Geben Sie Ihren Vornamen ein",
      enterYourLastName: "Geben Sie Ihren Nachnamen ein",
      enterYourEmail: "Geben Sie Ihre E-Mail ein",
      enterYourPhone: "Geben Sie Ihre Telefonnummer ein",
      firstNameRequiredError: "Vorname ist erforderlich",
      lastNameRequiredError: "Nachname ist erforderlich",
      emailRequiredError: "E-Mail ist erforderlich",
      phoneRequiredError: "Telefonnummer ist erforderlich",
      contactMethodRequired: "Bitte geben Sie entweder E-Mail oder Telefonnummer an",
      contactPermissionText: "Ich stimme zu, über Sonderangebote und Updates kontaktiert zu werden",
      invalidEmail: "Ungültige E-Mail-Adresse",
      invalidPhone: "Ungültige Telefonnummer",
      publicService: "Öffentliche Dienstleistung",
      anyAvailableTeamMember: "Beliebiges verfügbares Teammitglied",
      firstAvailableTeamMember: "Erstes verfügbares Teammitglied wird zugewiesen",
      confirmYourBooking: "Bestätigen Sie Ihre Buchung",
      bookingService: "Dienstleistung",
      bookingTeamMember: "Teammitglied",
      bookingDate: "Datum",
      bookingTime: "Zeit",
      bookingDuration: "Dauer",
      booking: "Buchung",
      anyTeamMemberRequested: "Beliebiges verfügbares Teammitglied angefordert",
      appointmentBookedTitle: "Termin erfolgreich gebucht",
      bookingError: "Fehler beim Buchen des Termins. Bitte versuchen Sie es erneut.",
      availableTimesTitle: "Verfügbare Zeiten",
      slotsAvailable: "{{count}} Termin",
      slotsAvailable_one: "{{count}} Termin",
      slotsAvailable_other: "{{count}} Termine",
      bookingConfirmation: "Bestätigung",
      verificationEmailSent: "Bestätigungs-E-Mail gesendet",
      pleaseCheckYourEmailToConfirmSubscription: "Eine Bestätigungs-E-Mail wurde an den Kunden gesendet, um seine Marketing-Präferenzen zu bestätigen",
      emailVerified: "E-Mail verifiziert",
      marketingConsentNotificationTitle: "Marketing-Einwilligungs-E-Mail",
      marketingConsentNotificationMessage: "{{customerName}} erhält eine E-Mail zur Bestätigung ihrer {{consentType}} Präferenzen.",
      and: "und",
      willReceiveVerificationEmail: "Erhält eine Bestätigungs-E-Mail",
      emailMarketingConsent: "E-Mail-Marketing-Kommunikation",
      whatsappMarketingConsent: "WhatsApp-Marketing-Kommunikation",
      consentVerificationExpires: "Der Bestätigungslink läuft in 48 Stunden ab",
      marketingConsentGDPRNote: "Dies gewährleistet die Einhaltung der DSGVO-Vorschriften durch Einholung der ausdrücklichen Zustimmung des Kunden.",
      sendVerificationEmail: "Bestätigungs-E-Mail senden",
      bookingConfirmedTitle: "Buchung bestätigt!",
      bookingSuccessMessage: "Vielen Dank {{name}}, Ihr Termin wurde erfolgreich gebucht.",
      yourAppointmentDetails: "Ihre Termindetails",
      confirmationEmailSentMessage: "Eine Bestätigungs-E-Mail mit allen Termindetails wurde an Ihre E-Mail-Adresse gesendet.",
      bookAnotherAppointment: "Weiteren Termin buchen",
      backToHome: "Zurück zur Startseite",
      backToWebsite: "Zurück zur Webseite",
      verificationPending: "Verifizierung ausstehend",
      resendVerification: "Verifizierung erneut senden",
      emailNotVerified: "nicht verifiziert",
      failedToSendVerificationEmail: "Bestätigungs-E-Mail konnte nicht gesendet werden",

      // Marketing Consent Verification Page
      marketingConsent: {
        verifying: "Ihre Einwilligung wird überprüft...",
        verificationSuccess: "Verifizierung erfolgreich!",
        subscriptionConfirmed: "Ihr Interesse an gelegentlichen Mitteilungen wurde bestätigt.",
        youWillReceive: "Sie erhalten",
        appointmentReminders: "Terminerinnerungen und -bestätigungen",
        specialOffers: "Sonderangebote und Aktionen",
        newsUpdates: "Neuigkeiten und Updates zu unseren Dienstleistungen",
        alreadyVerified: "Bereits verifiziert",
        subscriptionAlreadyConfirmed: "Ihr Interesse wurde bereits bestätigt.",
        verificationFailed: "Verifizierung fehlgeschlagen",
        invalidOrExpiredLink: "Der Bestätigungslink ist ungültig oder abgelaufen.",
        contactSupport: "Bitte kontaktieren Sie den Support, wenn Sie Hilfe benötigen.",
        canCloseWindow: "Sie können dieses Fenster jetzt schließen."
      },

      // Comprehensive Notification System

      // Notification Status Values
      pending: "Ausstehend",


      // Customer Appointment Management
      manageAppointment: "Termin verwalten",
      invalidLink: "Ungültiger Link",
      invalidOrExpiredAppointmentLink: "Ungültiger oder abgelaufener Link",
      failedToLoadAppointment: "Fehler beim Laden des Termins",
      appointmentCancelledSuccessfully: "Termin erfolgreich storniert",
      failedToCancelAppointment: "Fehler beim Stornieren des Termins",
      pleaseContactUsToReschedule: "Bitte kontaktieren Sie uns, um Ihren Termin zu verschieben",
      appointmentManagementInfo: "Sie können Ihren Termin über die Schaltflächen unten verschieben oder stornieren",
      cancelAppointment: "Termin stornieren",
      appointmentHasBeenCancelled: "Dieser Termin wurde storniert",
      upcoming: "Bevorstehend",
      failedToScheduleReminders: "Fehler beim Planen der Erinnerungen",
      failedToCleanupNotifications:
        "Fehler beim Bereinigen der Benachrichtigungen",
      failedToUpdateNotificationPreferences:
        "Fehler beim Aktualisieren der Benachrichtigungseinstellungen",
      failedToBulkUpdatePreferences:
        "Fehler beim Massen-Update der Einstellungen",

      // Customer Notification Preferences
      notificationPreferencesUpdated:
        "Benachrichtigungseinstellungen aktualisiert",
      customerNotificationPreferencesUpdatedSuccessfully:
        "Kunden-Benachrichtigungseinstellungen erfolgreich aktualisiert",
      bulkUpdateComplete: "Massen-Update abgeschlossen",

      // Queue Statistics

      pendingNotifications: "Ausstehend",

      // Email Service

      emailsSent: "E-Mails gesendet",

      // Template Management

      // Notification Engine Settings
      notificationEngineSettings: "Benachrichtigungsengine-Einstellungen",

      // Advanced Settings

      // Multi-language Support

      // Real-time Updates

      // Categories
      searchOrCreateCategory: "Kategorie suchen oder erstellen",
      createCategory: "Kategorie erstellen",
      createNewCategory: "Neue Kategorie erstellen",
      categoryName: "Kategoriename",
      categoryDescription: "Kategoriebeschreibung",
      categoryNameRequired: "Kategoriename ist erforderlich",
      categoryRequired: "Kategorie ist erforderlich",
      categoryCreated: "Kategorie erfolgreich erstellt",
      categoryCreationFailed: "Fehler beim Erstellen der Kategorie",
      manageCategories: "Kategorien verwalten",
      manageCategoriesDescription:
        "Kategorien umbenennen oder nicht mehr benötigte entfernen.",
      noCategoriesYet: "Noch keine Kategorien vorhanden.",
      oneService: "1 Dienstleistung",
      nServices: "{count} Dienstleistungen",
      rename: "Umbenennen",
      categoryNameTaken: "Eine Kategorie mit diesem Namen existiert bereits",
      categoryUpdated: "Kategorie umbenannt",
      categoryUpdateFailed: "Fehler beim Umbenennen der Kategorie",
      categoryDeleted: "Kategorie gelöscht",
      categoryDeletionFailed: "Fehler beim Löschen der Kategorie",
      categoryInUseCannotDelete:
        "Noch in Verwendung - ordne die Dienstleistungen zuerst einer anderen Kategorie zu",
      categoryDeleteHint:
        "Nur Kategorien ohne Dienstleistungen können gelöscht werden.",
      deleteCategory: "Kategorie löschen",
      deleteCategoryConfirm:
        "Kategorie \"{name}\" wirklich löschen? Das lässt sich nicht rückgängig machen.",

      // Additional missing translation keys for hardcoded strings (German)
      bookAnAppointment: "Termin buchen",
      scheduleYourNextServiceAppointment:
        "Planen Sie Ihren nächsten Servicetermin",
      bookYourAppointment: "Ihren Termin buchen",
      dateTime: "Datum & Uhrzeit",
      details: "Details",
      continueToDateTime: "Weiter zu Datum & Uhrzeit",

      staffMemberRequired: "Teammitglied *",
      selectStaffMember: "Wählen Sie ein Teammitglied",
      anyAvailableStaff: "Verfügbares Personal",
      selectDate: "Datum auswählen",
      selectTime: "Uhrzeit auswählen",
      continueToDetails: "Weiter zu Details",

      specialRequestsOrNotes: "Besondere Wünsche oder Notizen",
      bookingSummaryTitle: "Buchungszusammenfassung",
      serviceCost: "Dienstleistung",
      durationLabel: "Dauer",
      priceLabel: "Preis",
      dateLabel: "Datum",
      timeLabel: "Uhrzeit",
      staffLabel: "Personal",
      confirmBookingButton: "Buchung bestätigen",
      appointmentBooked: "Termin gebucht!",

      // Missing service-related keys (German)
      bookingLinkCopied: "Buchungslink in Zwischenablage kopiert",
      servicesDeletedSuccessfully:
        "{{count}} Dienstleistungen erfolgreich gelöscht",
      servicesDuplicatedSuccessfully:
        "{{count}} Dienstleistungen erfolgreich dupliziert",
      bulkDeleteServicesConfirmation:
        "Sind Sie sicher, dass Sie {{count}} Dienstleistungen löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",
      bulkDuplicateServicesConfirmation:
        "Sind Sie sicher, dass Sie {{count}} Dienstleistungen duplizieren möchten?",
      schedulingLinkCopied: "Terminplanungslink in Zwischenablage kopiert",
      bufferTime: "Pufferzeit",
      deleteServiceConfirmation:
        "Sind Sie sicher, dass Sie diese Dienstleistung löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",

      // Appointment and booking related (German)
      appointmentConfirmedFor: "Termin bestätigt für {{name}}",
      fieldIsRequired: "Dieses Feld ist erforderlich",
      exportSuccessDescription: "Ihre Daten wurden erfolgreich exportiert",
      exportSummary: "Export-Zusammenfassung",
      importSuccessDescription: "Ihre Daten wurden erfolgreich importiert",
      andXMore: "und {{count}} weitere",
      importXCustomers: "{{count}} Kunden importieren",
      availableTimes: "Verfügbare Zeiten",
      availabilityForTeamMember: "Verfügbarkeit für {{name}}",

      // Notification related (German)
      updatedPreferencesForCustomers:
        "Einstellungen für {{count}} Kunden aktualisiert",
      invalidPlaceholders: "Ungültige Platzhalter gefunden",
      retriedNotifications:
        "{{count}} fehlgeschlagene Benachrichtigungen wiederholt",
      processedEmails: "{{count}} E-Mails verarbeitet",
      scheduledReminders: "{{count}} Erinnerungen geplant",
      cleanedUpNotifications: "{{count}} alte Benachrichtigungen bereinigt",
      serviceResourceCapacity: "Terminart-Ressourcenkapazität",
      serviceResourceCapacityDetailed: "{{resourceName}} wird für diese Dienstleistung benötigt, ist aber voll ausgelastet ({{capacity}} Termine)",

      // Auth
      createAccount: "Konto erstellen",

      // Error Messages
      failedToLoadAppointments: "Termine konnten nicht geladen werden",
      fileSizeMustBeLessThan2mb: "Dateigröße muss weniger als 2MB betragen",
      errorUnknownError: "Unbekannter Fehler",
      errorPleaseFixTheErrorsBeforeSubmitting: "Bitte beheben Sie die Fehler vor dem Absenden.",
      errorFailedToCreateAppointment: "Termin konnte nicht erstellt werden",
      errorFailedToUpdateAppointment: "Termin konnte nicht aktualisiert werden",
      errorFailedToRescheduleAppointment: "Termin konnte nicht umgeplant werden",
      missingTenantIdOrCustomerId: "Fehlende Tenant-ID oder Kunden-ID",
      errorFailedToFetchResources: "Ressourcen konnten nicht abgerufen werden",
      errorAnErrorOccurred: "Ein Fehler ist aufgetreten",
      errorFailedToLoadCalendar: "Kalender konnte nicht geladen werden",

      // Time formats
      timeFormatHourMinute: "HH:mm",
      timeFormatFull: "HH:mm",
      dateFormat: "dd.MM.yyyy",
      dateFormatShort: "EEE, d. MMM",
      dateFormatLong: "EEEE, d. MMMM yyyy",
      dateFormatMonthYear: "MMMM yyyy",
      dateFormatWeekdayShort: "EEE",
      dateFormatWeekdayLong: "EEEE",
      dateFormatMonthYearShort: "MMM yyyy",
      dateFormatDayNumeric: "d",

      noTenantSelected: "Kein Konto ausgewählt",
      // Notifications
      failedToClearNotificationQueue:
        "Benachrichtigungswarteschlange konnte nicht geleert werden",
      clearAllNotificationsConfirm:
        "Sind Sie sicher, dass Sie ALLE Benachrichtigungen aus der Warteschlange löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.",

      // New translation keys for hardcoded strings
      chooseAService: "Service auswählen",
      nextDateTime: "Weiter: Datum & Uhrzeit",
      nextYourDetails: "Weiter: Ihre Daten",
      anySpecialRequestsOrInformation:
        "Besondere Wünsche oder Informationen...",
      calendarNotFoundMessage:
        "Wir konnten den gesuchten Kalender nicht finden. Bitte überprüfen Sie die URL oder kontaktieren Sie das Unternehmen direkt.",
      bookYourAppointmentWithEase:
        "Buchen Sie Ihren Termin ganz einfach. Wählen Sie Ihr bevorzugtes Datum und Ihre Uhrzeit.",
      bookAppointmentNow: "Jetzt Termin buchen",
      somethingWentWrongPleaseTryAgain:
        "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
      addYourFirstResource: "Ihre erste Ressource hinzufügen",
      noResourcesYet: "Noch keine Ressourcen",
      addRoomsEquipmentOrOtherBookableResources: "Fügen Sie Räume, Ausrüstung oder andere buchbare Ressourcen hinzu",
      returnToHome: "Zur Startseite zurückkehren",
      noTenantSelectedPleaseSelectATenantFirst:
        "Kein Mandant ausgewählt. Bitte wählen Sie zuerst einen Mandanten aus.",
      formSubmittedSuccessfully: "Ihr Formular wurde erfolgreich übermittelt.",
      submissionError: "Übermittlungsfehler",
      failedToSubmitFormPleaseTryAgain:
        "Formular konnte nicht übermittelt werden. Bitte versuchen Sie es erneut.",
      submitForm: "Formular absenden",
      submitting: "Wird übermittelt...",
      goToPreviousPage: "Zur vorherigen Seite",
      goToNextPage: "Zur nächsten Seite",
      errorAnalysis: "Fehleranalyse",
      smtpConnectionFailed: "SMTP-Verbindung fehlgeschlagen",
      authenticationFailed: "Authentifizierung fehlgeschlagen",
      retryFailed: "Wiederholung fehlgeschlagen",
      thisWeek: "Diese Woche",
      tomorrow: "Morgen",
      dateOfTheAppointment: "Datum des Termins",
      timeOfTheAppointment: "Zeit des Termins",
      appointmentDurationInMinutes: "Termin-Dauer in Minuten",
      emailSubjectIsRequired: "E-Mail-Betreff ist erforderlich",
      emailSendingFailed: "E-Mail-Versand fehlgeschlagen",
      emailDeliveryFailed: "E-Mail-Zustellung fehlgeschlagen",
      failedToFetchResources: "Ressourcen konnten nicht abgerufen werden",
      failedToLoadCalendar: "Kalender konnte nicht geladen werden",
      unknownError: "Unbekannter Fehler",
      anErrorOccurred: "Ein Fehler ist aufgetreten",
      pleaseFixTheErrorsBeforeSubmitting:
        "Bitte beheben Sie die Fehler vor dem Absenden.",
      resourceIsAlreadyBookedForThisTimeSlot:
        "Ressource ist bereits für diesen Zeitraum gebucht",
      errorCheckingResourceAvailability:
        "Fehler beim Prüfen der Ressourcenverfügbarkeit",

      emailsWouldAppearInProduction:
        "E-Mails, die in der Produktion gesendet würden, erscheinen hier",

      profile: "Profil",
      viewAll: "Alle anzeigen",
      analyzed: "analysiert",
      discovered: "Entdeckt",
      website: "Website",
      primary: "Primär",
      optional: "optional",
      url: "URL",
      trend: "Trend",
      rankingType: "Ranking-Typ",
      export: "Exportieren",
      exporting: "Exportieren...",

      // Common form fields
      status: "Status",
      remove: "Entfernen",
      addService: "weitere Dienstleistung hinzufügen",
      assignDifferentTeamMembers: "Unterschiedliche Teammitglieder pro Dienstleistung zuweisen",
      teamMemberNotRelatedToService: "Dieses Teammitglied ist nicht allen ausgewählten Dienstleistungen zugeordnet",
      serviceName: "Servicename",
      category: "Kategorie",
      selectCategory: "Kategorie wählen",
      title: "Titel",
      active: "Aktiv",
      inactive: "Inaktiv",
      previousAnalysisRuns: "Vorherige Analyseläufe",
      imported: "Importiert",
      results: "Ergebnisse",
      yourRankingsForTopKeywords: "Ihre Rankings für Top-Keywords",
      location: "Standort",
      archive: "Archivieren",
    },
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    debug: false,

    interpolation: {
      escapeValue: false, // React already does escaping
    },

    detection: {
      order: ["navigator", "localStorage", "htmlTag"],
      caches: ["localStorage"],
      lookupLocalStorage: "i18nextLng",
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
    },
  });

export default i18n;
